import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { X, Star, Clock, CalendarDays, TrendingDown, TrendingUp, BarChart3, Newspaper, Calculator, PieChart, Wallet, AlertTriangle, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, GitCompare, FileSpreadsheet, Share2, Target, Briefcase, BookOpen, ArrowLeft, Receipt, FileText, Upload, Triangle as TriangleIcon, Camera, Flag, Loader2, Maximize2 } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { computeRealYield } from '@/lib/realYield';
import DivGrowthTab from '@/components/DivGrowthTab';
import NonDivGrowthTab from '@/components/NonDivGrowthTab';
import StockMetricsTab from '@/components/StockMetricsTab';
import FinancialHealthTab from '@/components/FinancialHealthTab';
import IntradayTab from '@/components/IntradayTab';
import usePriceStream from '@/hooks/usePriceStream';
import { RecycledChevron } from '@/hooks/useRecycledLockedTickers';
import LivePriceIndicator from '@/components/LivePriceIndicator';
import DividendChangeBadge from '@/components/DividendChangeBadge';
import YieldHistoryTab from '@/components/YieldHistoryTab';
import NewsTab from '@/components/NewsTab';
import StockJournalTab from '@/components/StockJournalTab';
import DripCalcTab from '@/components/DripCalcTab';
import MyPortfolioTab from '@/components/MyPortfolioTab';
import MyPositionTab from '@/components/MyPositionTab';
import TenK8KRisksTab from '@/components/TenK8KRisksTab';
import InsiderActivityTab from '@/components/InsiderActivityTab';
import TriangleScorecard from '@/components/TriangleScorecard';
import { ExecutiveSummaryTab } from '@/components/ExecutiveSummary';
import TriangleSentimentRibbon from '@/components/TriangleSentimentRibbon';
import CapitalAnalyticsTab from '@/components/CapitalAnalyticsTab';
import StockModalCommunitySurfacer from '@/components/StockModalCommunitySurfacer';
import MassiveInsightsTab from '@/components/MassiveInsightsTab';
import MoneyMarketFundOverviewCard from '@/components/MoneyMarketFundOverviewCard';
import RecentEventsCard from '@/components/RecentEventsCard';
import DividendScheduleCards from '@/components/DividendScheduleCards';
import TechnicalsAndShortDataCards from '@/components/TechnicalsAndShortDataCards';
import YieldTrapBadge from '@/components/YieldTrapBadge';
import { COMMUNITY_ENABLED } from '@/config/featureFlags';
import HeadlineInsight from '@/components/HeadlineInsight';
import ModalCloseButton from '@/components/ModalCloseButton';
import TabsScrollSlider from '@/components/TabsScrollSlider';
import ModalResizeGrips from '@/components/ModalResizeGrips';
import useModalResize from '@/hooks/useModalResize';
import { computeRisk, riskColorClasses, riskLabel, riskTooltip } from '@/lib/riskScore';
import QualityBadge from '@/components/QualityBadge';
import TitleTapLayer from '@/components/TitleTapLayer';
import LeveragedCefIcon from '@/components/LeveragedCefIcon';
import CapitalScoreBadge from '@/components/CapitalScoreBadge';
import { BadgeHelpIcon } from '@/components/BadgeKey';
import PreferredCallChip from '@/components/PreferredCallChip';
import { WarrantBadge, WarrantPanel } from '@/components/WarrantBadge';
import WarrantVsUnderlyingChart from '@/components/WarrantVsUnderlyingChart';
import UnitPanel from '@/components/UnitPanel';
import RightPanel, { RightBadge, isSpacRight, rightShareTicker, rightName } from '@/components/RightPanel';
import { useWarrant } from '@/hooks/useWarrant';
import CommunityTagsSummaryChip from '@/components/CommunityTagsSummaryChip';
import CompanyIcon from '@/components/CompanyIcon';
import { ccToFlag, ccName } from '@/lib/flagEmoji';
import CompanyLogo from '@/components/CompanyLogo';
import AbbrTip from '@/components/AbbrTip';
import FixedRateAssetCard, { isFixedRateAsset } from '@/components/FixedRateAssetCard';
import CompanyDetailEnrichment from '@/components/CompanyDetailEnrichment';
import CloExplainerCard from '@/components/CloExplainerCard';
import { ExpandableAboutText } from '@/components/ExpandableAboutText';
import StreakRiskChart from '@/components/StreakRiskChart';
import AdminCheckLiquidationButton from '@/components/AdminCheckLiquidationButton';
import HomeCurrencyDividendCard from '@/components/HomeCurrencyDividendCard';
import SpecialPayoutCard from '@/components/SpecialPayoutCard';
import SpecialPayerTag from '@/components/SpecialPayerTag';
import MegaTierEliteBadge from '@/components/MegaTierEliteBadge';
import { trackEvent } from '@/components/Analytics';
import CefZScoreGauge from '@/components/CefZScoreGauge';
import { useTreasuryYields } from '@/hooks/useTreasuryYields';
import { useInflation } from '@/hooks/useInflation';

// Dividend-tier metadata used by the header badge + About-section explainer.
const TIER_META = {
  king: {
    icon: '\u{1F451}', // 👑
    label: 'Dividend King',
    listLabel: 'Kings List',
    path: '/kings-challenge',
    badgeCls: 'bg-yellow-100 text-yellow-900 border-yellow-300 dark:bg-yellow-950/60 dark:text-yellow-200 dark:border-yellow-800/60',
    cardCls: 'border-yellow-300/70 bg-yellow-50/60 dark:bg-yellow-950/20 dark:border-yellow-800/50',
    tag: 'text-yellow-800 dark:text-yellow-300',
    link: 'text-yellow-800 hover:text-yellow-900 dark:text-yellow-300 dark:hover:text-yellow-200',
    borderTop: 'border-yellow-300/50 dark:border-yellow-800/40',
    blurb: 'Elite companies that have raised their dividend every year for 50+ consecutive years. Proven through recessions, wars, and market crashes — the gold standard for long-term dividend investing.',
  },
  aristocrat: {
    icon: '\u{1F3C6}', // 🏆
    label: 'Dividend Aristocrat',
    listLabel: 'Aristocrats List',
    path: '/aristocrats-challenge',
    badgeCls: 'bg-blue-100 text-blue-900 border-blue-300 dark:bg-blue-950/60 dark:text-blue-200 dark:border-blue-800/60',
    cardCls: 'border-blue-300/70 bg-blue-50/60 dark:bg-blue-950/20 dark:border-blue-800/50',
    tag: 'text-blue-800 dark:text-blue-300',
    link: 'text-blue-800 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200',
    borderTop: 'border-blue-300/50 dark:border-blue-800/40',
    blurb: 'S&P 500 companies with 25+ consecutive years of annual dividend increases. A shorter track record than Kings but still a strong signal of financial discipline and shareholder commitment.',
  },
  achiever: {
    icon: '\u{1F948}', // 🥉
    label: 'Dividend Achiever',
    listLabel: 'Achievers List',
    path: '/achievers-challenge',
    badgeCls: 'bg-green-100 text-green-900 border-green-300 dark:bg-green-950/60 dark:text-green-200 dark:border-green-800/60',
    cardCls: 'border-green-300/70 bg-green-50/60 dark:bg-green-950/20 dark:border-green-800/50',
    tag: 'text-green-800 dark:text-green-300',
    link: 'text-green-800 hover:text-green-900 dark:text-green-300 dark:hover:text-green-200',
    borderTop: 'border-green-300/50 dark:border-green-800/40',
    blurb: '10+ consecutive years of annual dividend increases. The entry-level tier that signals a management team committed to rewarding shareholders over time.',
  },
};

function TierHeaderBadge({ tier }) {
  const meta = TIER_META[tier];
  if (!meta) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border cursor-help ${meta.badgeCls}`}
      title={`${meta.label} — ${meta.blurb}`}
      data-testid={`modal-tier-badge-${tier}`}
    >
      <span className="text-[11px] leading-none">{meta.icon}</span>
      {meta.label.replace('Dividend ', '')}
    </span>
  );
}

function TierExplainerCard({ tier, streakYears, onNavigate, onNavigateWithOrigin }) {
  const meta = TIER_META[tier];
  if (!meta) return null;
  return (
    <div
      className={`mt-3 rounded-lg border p-3 ${meta.cardCls}`}
      data-testid={`modal-tier-explainer-${tier}`}
    >
      <div className="flex items-start gap-3">
        <div className="text-3xl leading-none shrink-0" aria-hidden="true">{meta.icon}</div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className={`text-sm font-bold ${meta.tag}`}>{meta.label}</span>
            {streakYears ? (
              <span className="text-[11px] text-muted-foreground font-mono">{streakYears} yrs streak</span>
            ) : null}
          </div>
          <p className="text-[11px] text-muted-foreground leading-relaxed mt-1">{meta.blurb}</p>
        </div>
      </div>
      <div className={`mt-2.5 pt-2 border-t ${meta.borderTop} text-right`}>
        {/* Round 79b — navigate via onNavigateWithOrigin so the
            destination page records the modal's ticker + fromPath
            in history.state, enabling the back-restore flow. Falls
            back to a plain Link when the prop isn't passed (legacy
            consumers).*/}
        {onNavigateWithOrigin ? (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              if (typeof onNavigate === 'function') onNavigate();
              onNavigateWithOrigin(meta.path);
            }}
            className={`text-[11px] font-semibold underline-offset-2 hover:underline ${meta.link}`}
            data-testid={`modal-tier-list-link-${tier}`}
          >
            View {meta.listLabel} &rarr;
          </button>
        ) : (
          <Link
            to={meta.path}
            onClick={onNavigate}
            className={`text-[11px] font-semibold underline-offset-2 hover:underline ${meta.link}`}
            data-testid={`modal-tier-list-link-${tier}`}
          >
            View {meta.listLabel} &rarr;
          </Link>
        )}
      </div>
    </div>
  );
}

function LeveragedExplainerCard() {
  return (
    <div
      className="mt-3 rounded-lg border p-3 border-red-300/70 bg-red-50/60 dark:bg-red-950/20 dark:border-red-800/50"
      data-testid="modal-leveraged-explainer"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 mt-0.5">
          <AlertTriangle className="w-7 h-7 text-red-700 dark:text-red-400" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-bold text-red-800 dark:text-red-300">Leveraged Stock / ETF</span>
            <span className="text-[11px] text-muted-foreground">Not for buy-and-hold</span>
          </div>
          <p className="text-[11px] text-muted-foreground leading-relaxed mt-1">
            A leveraged stock or ETF is a financial instrument that uses debt and derivatives to multiply the daily returns of an underlying index or company.
          </p>
        </div>
      </div>

      <div className="mt-2.5 pt-2 border-t border-red-300/50 dark:border-red-800/40 space-y-2">
        <div>
          <h5 className="text-[11px] font-bold text-red-800 dark:text-red-300 flex items-center gap-1">
            <span>⚠️</span> The Critical Risks
          </h5>
          <p className="text-[11px] text-muted-foreground leading-relaxed mt-1">
            Leveraged stocks are <strong>not meant for long-term "buy and hold" investing</strong>. They are designed for day trading.
          </p>
        </div>
        <div>
          <h5 className="text-[11px] font-bold text-red-800 dark:text-red-300">
            Volatility Decay (The "Math Trap")
          </h5>
          <p className="text-[11px] text-muted-foreground leading-relaxed mt-1">
            Because these reset daily, math works against you in a choppy market. If a stock goes down 10% one day and up 10% the next, you don't break even — you actually lose more money in a leveraged fund than in a normal one.
          </p>
        </div>
      </div>
    </div>
  );
}

function FuturesExplainerCard() {
  return (
    <div
      className="mt-3 rounded-lg border p-3 border-amber-300/70 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-800/50"
      data-testid="modal-futures-explainer"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 mt-0.5">
          <AlertTriangle className="w-7 h-7 text-amber-700 dark:text-amber-400" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-bold text-amber-800 dark:text-amber-300">Futures-Based Fund</span>
            <span className="text-[11px] text-muted-foreground">Commodity / derivatives exposure</span>
          </div>
          <p className="text-[11px] text-muted-foreground leading-relaxed mt-1">
            These funds don't own the underlying asset directly — they use futures contracts, Treasury Bills, and a rolling strategy to generate the cash they distribute to you.
          </p>
        </div>
      </div>

      <div className="mt-2.5 pt-2 border-t border-amber-300/50 dark:border-amber-800/40 space-y-2.5">
        <div>
          <h5 className="text-[11px] font-bold text-amber-800 dark:text-amber-300 flex items-center gap-1">
            <span>🏗️</span> How They Make Money (3-step cycle)
          </h5>
          <ul className="text-[11px] text-muted-foreground leading-relaxed mt-1 space-y-1 list-none">
            <li><strong className="text-foreground">1. Collateral:</strong> 70–95% of the fund sits in U.S. Treasury Bills or cash, earning interest — this forms the base of your dividend.</li>
            <li><strong className="text-foreground">2. Futures:</strong> The remaining small % is used as margin to control large amounts of commodities or indexes via futures contracts.</li>
            <li>
              <strong className="text-foreground">3. The "Roll":</strong> Contracts expire monthly and must be rolled forward.
              <span className="text-emerald-700 dark:text-emerald-400"> Profit</span> when next month's contract is cheaper (<em>backwardation</em>);
              <span className="text-red-700 dark:text-red-400"> loss</span> when it's more expensive (<em>contango</em>).
            </li>
          </ul>
        </div>

        <div>
          <h5 className="text-[11px] font-bold text-amber-800 dark:text-amber-300 flex items-center gap-1">
            <span>💰</span> How They Pay Dividends
          </h5>
          <ul className="text-[11px] text-muted-foreground leading-relaxed mt-1 space-y-0.5 list-disc list-inside">
            <li><strong className="text-foreground">Yield on Cash</strong> — interest earned from Treasury Bills.</li>
            <li><strong className="text-foreground">Roll Yield</strong> — profits from the monthly rolling process.</li>
            <li><strong className="text-foreground">Capital Gains</strong> — if the commodity price rises, gains are distributed to holders by law.</li>
          </ul>
        </div>

        <div>
          <h5 className="text-[11px] font-bold text-amber-800 dark:text-amber-300 flex items-center gap-1">
            <span>⚠️</span> Hidden Risks
          </h5>
          <ul className="text-[11px] text-muted-foreground leading-relaxed mt-1 space-y-0.5 list-disc list-inside">
            <li><strong className="text-foreground">K-1 Tax Forms</strong> — many older commodity funds (like DBC) issue a Schedule K-1, a tax nightmare. Look for <em>"K-1 Free"</em> or <em>"No K-1"</em> funds (e.g., BCI, PDBC).</li>
            <li><strong className="text-foreground">Return of Capital (ROC)</strong> — some funds "pay" you by returning your own money and calling it a dividend. Lowers cost basis, not real growth.</li>
            <li><strong className="text-foreground">No Compounding</strong> — because gains are paid out as dividends, the ETF price often stays flat or drops over decades. You only profit by collecting the checks.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function AdrExplainerCard({ ticker }) {
  // PN (Preferred) vs ON (Common) only meaningful for the small set of
  // Brazilian dual-class ADRs (BBD/BBDO, ITUB/ITUB4, etc.). Surface the
  // rights summary only when the ticker name actually contains the
  // (ON) or (PN) tag so we don't confuse holders of plain ADRs.
  const upper = (ticker || "").toUpperCase();
  const showShareClass = ["BBD", "BBDO"].includes(upper);
  return (
    <div
      className="mt-3 rounded-lg border p-3 border-red-300/70 bg-pink-50/70 dark:bg-pink-200/10 dark:border-red-400/40"
      data-testid="modal-adr-explainer"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 mt-0.5">
          <span
            className="inline-flex items-center justify-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-pink-100 text-red-800 dark:bg-pink-200/90 dark:text-red-900 border border-red-300/60 dark:border-red-400/60"
          >
            ADR
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-bold text-red-800 dark:text-red-300">American Depositary Receipt / Share</span>
            <span className="text-[11px] text-muted-foreground">U.S.-listed foreign company</span>
          </div>
          <p className="text-[11px] text-muted-foreground leading-relaxed mt-1">
            An ADR (or its underlying ADS) is a negotiable certificate issued by a U.S. bank representing a specified number of shares in a foreign company's stock. It trades on U.S. exchanges in U.S. dollars, letting U.S. investors buy foreign stocks easily without dealing with currency conversion or international brokers.
          </p>
        </div>
      </div>

      <div className="mt-2.5 pt-2 border-t border-red-300/50 dark:border-red-400/30 space-y-2.5">
        <div>
          <h5 className="text-[11px] font-bold text-red-800 dark:text-red-300">Key Details</h5>
          <ul className="text-[11px] text-muted-foreground leading-relaxed mt-1 space-y-0.5 list-disc list-inside">
            <li><strong className="text-foreground">Definition:</strong> the ADS is the actual underlying share, while the ADR is the certificate representing ownership — the terms are often used interchangeably.</li>
            <li><strong className="text-foreground">Trading & Dividends:</strong> traded on NYSE/NASDAQ or OTC just like domestic stocks. Dividends are paid in USD.</li>
            <li><strong className="text-foreground">Structure:</strong> often a set ratio (e.g., 1 ADR = 5 foreign shares).</li>
          </ul>
        </div>

        <div>
          <h5 className="text-[11px] font-bold text-red-800 dark:text-red-300 flex items-center gap-1">
            <span>💡</span> Why Investors Use Them
          </h5>
          <ul className="text-[11px] text-muted-foreground leading-relaxed mt-1 space-y-0.5 list-disc list-inside">
            <li><strong className="text-foreground">USD denominated:</strong> trade and receive dividends in U.S. dollars.</li>
            <li><strong className="text-foreground">U.S. hours:</strong> buy and sell during normal NYSE/NASDAQ sessions.</li>
            <li><strong className="text-foreground">No foreign account:</strong> no need for a specialized international brokerage account.</li>
          </ul>
        </div>

        <div>
          <h5 className="text-[11px] font-bold text-red-800 dark:text-red-300">ADR Levels</h5>
          <ul className="text-[11px] text-muted-foreground leading-relaxed mt-1 space-y-0.5 list-disc list-inside">
            <li><strong className="text-foreground">Level I:</strong> traded over-the-counter (OTC), low regulatory requirements.</li>
            <li><strong className="text-foreground">Level II &amp; III:</strong> listed on exchanges (NYSE/NASDAQ), higher SEC compliance.</li>
          </ul>
        </div>

        <div>
          <h5 className="text-[11px] font-bold text-red-800 dark:text-red-300 flex items-center gap-1">
            <span>⚠️</span> Important Risks
          </h5>
          <ul className="text-[11px] text-muted-foreground leading-relaxed mt-1 space-y-0.5 list-disc list-inside">
            <li><strong className="text-foreground">Currency risk:</strong> fluctuations in the foreign company's home currency affect the share price in USD.</li>
            <li><strong className="text-foreground">Foreign taxes:</strong> dividends may be subject to withholding taxes from the home country.</li>
            <li><strong className="text-foreground">ADR fees:</strong> banks often charge small "pass-through" fees for custodial services.</li>
          </ul>
        </div>

        {showShareClass && (
          <div>
            <h5 className="text-[11px] font-bold text-red-800 dark:text-red-300">Common (ON) vs Preferred (PN) Shares</h5>
            <p className="text-[11px] text-muted-foreground leading-relaxed mt-1">
              Common <strong className="text-foreground">(ON)</strong> shares carry voting rights at shareholders' meetings and 100% tag-along rights. Preferred <strong className="text-foreground">(PN)</strong> shares have no voting rights but earn a dividend 10% higher than common shares and have 80% tag-along rights.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function BdcExplainerCard() {
  return (
    <div
      className="mt-3 rounded-lg border p-3 border-indigo-300/70 bg-indigo-50/70 dark:bg-indigo-950/20 dark:border-indigo-700/60"
      data-testid="modal-bdc-explainer"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 mt-0.5">
          <span
            className="inline-flex items-center justify-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-indigo-100 text-indigo-800 dark:bg-indigo-900/60 dark:text-indigo-200 border border-indigo-300/60 dark:border-indigo-700/60"
          >
            BDC
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-bold text-indigo-800 dark:text-indigo-300">Business Development Company</span>
            <span className="text-[11px] text-muted-foreground">Public closed-end credit fund</span>
          </div>
          <p className="text-[11px] text-muted-foreground leading-relaxed mt-1">
            A Business Development Company (BDC) is a type of closed-end investment fund, created by the U.S. Congress in 1980, that provides capital to small- to mid-sized businesses and financially distressed companies.
          </p>
        </div>
      </div>

      <div className="mt-2.5 pt-2 border-t border-indigo-300/50 dark:border-indigo-700/40 space-y-2.5">
        <div>
          <h5 className="text-[11px] font-bold text-indigo-800 dark:text-indigo-300">Key Characteristics</h5>
          <ul className="text-[11px] text-muted-foreground leading-relaxed mt-1 space-y-0.5 list-disc list-inside">
            <li><strong className="text-foreground">Purpose:</strong> assist smaller companies in growing or regaining financial health by providing debt or equity financing.</li>
            <li><strong className="text-foreground">Structure:</strong> BDCs trade on major stock exchanges, letting public investors access private-credit-like returns.</li>
            <li><strong className="text-foreground">Regulations:</strong> required to invest at least 70% of assets in private or small public U.S. firms.</li>
            <li><strong className="text-foreground">Tax Efficiency:</strong> by electing to be treated as a regulated investment company (RIC), BDCs avoid corporate-level taxes if they distribute at least 90% of taxable income to shareholders — often resulting in high dividend yields.</li>
          </ul>
        </div>

        <div>
          <h5 className="text-[11px] font-bold text-indigo-800 dark:text-indigo-300 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Important Risks
          </h5>
          <p className="text-[11px] text-muted-foreground leading-relaxed mt-1">
            BDCs are generally considered <strong className="text-foreground">higher-risk</strong> investments due to their exposure to private, often illiquid company debt and their typical use of leverage.
          </p>
        </div>
      </div>
    </div>
  );
}

function CefExplainerCard({ ticker }) {
  const [navData, setNavData] = useState(null);
  const [stratOpen, setStratOpen] = useState(false);

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    axios.get(`${PEER_API}/stocks/${ticker}/cef-nav`)
      .then(r => { if (!cancelled) setNavData(r.data || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [ticker]);

  // Format the as-of label as "Mon YYYY" since users only need to know
  // roughly how stale the balance-sheet data is.
  const formatAsOf = (s) => {
    if (!s) return '';
    try {
      const d = new Date(s + 'T00:00:00Z');
      return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
    } catch { return s; }
  };

  // Curated rows from /api/stocks/{ticker}/cef-nav include a `source`
  // of "user-curated" with strategy / sponsor / 52-week-avg / YTD NAV
  // return fields. Yahoo-TBV fallback rows have `source` of
  // "yahoo-balance-sheet" and a much smaller payload.
  const isCurated = navData?.source === 'user-curated';
  // Prefer the live recompute (price ÷ curated NAV) for curated rows
  // since price refreshes constantly while NAV is updated periodically.
  const liveDisc = isCurated ? navData?.discount_pct_live : navData?.discount_pct;
  const showDisc = liveDisc !== undefined && liveDisc !== null ? liveDisc : navData?.discount_pct;

  return (
    <div
      className="mt-3 rounded-lg border p-3 border-teal-300/70 bg-teal-50/70 dark:bg-teal-950/20 dark:border-teal-700/60"
      data-testid="modal-cef-explainer"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 mt-0.5">
          <span
            className="inline-flex items-center justify-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-teal-100 text-teal-800 dark:bg-teal-900/60 dark:text-teal-200 border border-teal-300/60 dark:border-teal-700/60"
          >
            CEF
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-bold text-teal-800 dark:text-teal-300">Closed-End Fund</span>
            <span className="text-[11px] text-muted-foreground">Exchange-traded with fixed share count</span>
          </div>
          <p className="text-[11px] text-muted-foreground leading-relaxed mt-1">
            A Closed-End Fund (CEF) is a publicly-traded investment fund that raises a fixed pool of capital through an IPO and then trades on a stock exchange like a regular stock. Unlike open-end mutual funds and ETFs, the share count is fixed — meaning the fund's market price can trade at a <strong className="text-foreground">premium or discount to NAV</strong> based on supply and demand.
          </p>
        </div>
      </div>

      {/* Strategy & Sponsor — only shown for curated rows. Strategy is
          clickable when a definition exists, expanding into a paragraph
          panel (these definitions are substantive — too long for a
          hover-only tooltip). */}
      {isCurated && (navData.strategy || navData.sponsor) && (
        <div className="mt-2.5 pt-2 border-t border-teal-300/50 dark:border-teal-700/40 text-[11px] space-y-0.5">
          {navData.strategy && (
            <div data-testid="cef-strategy-row">
              <span className="font-bold text-teal-800 dark:text-teal-300">Strategy:</span>{' '}
              {navData.strategy_definition ? (
                <button
                  type="button"
                  onClick={() => setStratOpen(o => !o)}
                  className="text-foreground hover:text-teal-700 dark:hover:text-teal-300 underline decoration-dotted underline-offset-2 transition-colors"
                  data-testid="cef-strategy-toggle"
                  title={stratOpen ? 'Collapse strategy details' : 'Click to read the full strategy classification'}
                >
                  {navData.strategy} {stratOpen ? <ChevronUp className="inline w-3 h-3" /> : <ChevronDown className="inline w-3 h-3" />}
                </button>
              ) : (
                <span className="text-foreground" title="Definition coming soon — strategy classification from Morningstar.">
                  {navData.strategy}
                </span>
              )}
            </div>
          )}
          {navData.sponsor && (
            <div data-testid="cef-sponsor-row">
              <span className="font-bold text-teal-800 dark:text-teal-300">Sponsor:</span>{' '}
              <span className="text-foreground">{navData.sponsor}</span>
            </div>
          )}
          {/* Expanded strategy definition. Whitespace-pre-line so the
              backend's "\n• bullet" formatting renders as actual lines. */}
          {stratOpen && navData.strategy_definition && (
            <div
              className="mt-2 p-2.5 rounded-md bg-teal-100/70 dark:bg-teal-900/30 border border-teal-200/60 dark:border-teal-800/40 text-[11px] text-foreground leading-relaxed whitespace-pre-line"
              data-testid="cef-strategy-definition"
            >
              {navData.strategy_definition}
            </div>
          )}
        </div>
      )}

      {/* NAV / Discount-Premium tracker. Two display paths:
          - Curated: today-fresh NAV from CEF Connect; live discount
            recomputed against current price. Includes 52-wk avg
            comparator + YTD NAV total return.
          - Yahoo-TBV fallback: quarterly Tangible Book Value /
            Shares Outstanding. Less precise, clearly labeled "est." */}
      {navData?.nav ? (
        <div className="mt-2.5 pt-2 border-t border-teal-300/50 dark:border-teal-700/40">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div className="flex items-baseline gap-3 flex-wrap">
              <div>
                <div className="text-[9px] font-bold uppercase tracking-wide text-muted-foreground">{isCurated ? 'NAV' : 'Est. NAV'}</div>
                <div className="font-mono text-sm font-semibold text-foreground" data-testid="cef-nav-value">
                  ${Number(navData.nav).toFixed(2)}
                </div>
              </div>
              <div>
                <div className="text-[9px] font-bold uppercase tracking-wide text-muted-foreground">Price</div>
                <div className="font-mono text-sm font-semibold text-foreground">
                  ${Number(navData.price).toFixed(2)}
                </div>
              </div>
              <div>
                <div className="text-[9px] font-bold uppercase tracking-wide text-muted-foreground">
                  {showDisc < 0 ? 'Discount' : 'Premium'}
                </div>
                <div
                  className={`font-mono text-sm font-bold ${showDisc < 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}
                  data-testid="cef-discount-value"
                >
                  {showDisc < 0 ? '−' : '+'}{Math.abs(Number(showDisc)).toFixed(1)}%
                </div>
              </div>
              {isCurated && navData.discount_52wk_avg !== undefined && navData.discount_52wk_avg !== null && (
                <div>
                  <div className="text-[9px] font-bold uppercase tracking-wide text-muted-foreground">52w Avg</div>
                  <div
                    className={`font-mono text-sm font-semibold ${navData.discount_52wk_avg < 0 ? 'text-emerald-600/80 dark:text-emerald-400/80' : 'text-amber-600/80 dark:text-amber-400/80'}`}
                    data-testid="cef-52wk-avg"
                    title="Trailing 52-week average discount/premium to NAV — useful as a mean-reversion benchmark."
                  >
                    {navData.discount_52wk_avg < 0 ? '−' : '+'}{Math.abs(Number(navData.discount_52wk_avg)).toFixed(1)}%
                  </div>
                </div>
              )}
              {isCurated && navData.ytd_nav_return !== undefined && navData.ytd_nav_return !== null && (
                <div>
                  <div className="text-[9px] font-bold uppercase tracking-wide text-muted-foreground">YTD NAV</div>
                  <div
                    className={`font-mono text-sm font-semibold ${navData.ytd_nav_return >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}
                    data-testid="cef-ytd-nav"
                    title="Year-to-date total return ON NAV (NAV-based, not market-price). Reflects the underlying portfolio's actual performance regardless of trading discount."
                  >
                    {navData.ytd_nav_return >= 0 ? '+' : ''}{Number(navData.ytd_nav_return).toFixed(2)}%
                  </div>
                </div>
              )}
            </div>
            <span
              className="text-[10px] text-muted-foreground italic"
              title={isCurated
                ? 'User-curated data sourced from CEF Connect / sponsor pages, refreshed periodically. Discount recomputed live against current market price.'
                : 'Estimated from balance-sheet data (Tangible Book Value ÷ Shares Outstanding). Refreshed quarterly with each fund\'s NAV report — not real-time.'}
            >
              {isCurated ? `as of ${formatAsOf(navData.as_of)}` : `as of ${formatAsOf(navData.as_of)} · est.`}
            </span>
          </div>
          <p className="text-[10px] text-muted-foreground italic mt-1.5">
            {showDisc < -2
              ? `Trading at a ${Math.abs(showDisc).toFixed(1)}% discount means you're buying $1 of underlying assets for about $${(1 + showDisc / 100).toFixed(2)} — a tailwind if the discount narrows.`
              : showDisc > 2
                ? `Trading at a ${showDisc.toFixed(1)}% premium means you're paying about $${(1 + showDisc / 100).toFixed(2)} for $1 of underlying assets — premiums can compress quickly.`
                : 'Trading near NAV (within ±2%) — neither a discount nor a meaningful premium right now.'}
            {isCurated && navData.discount_52wk_avg !== undefined && navData.discount_52wk_avg !== null && Math.abs(showDisc - navData.discount_52wk_avg) > 1 && (
              <span>
                {' '}Currently {Math.abs(showDisc - navData.discount_52wk_avg).toFixed(1)} pts {showDisc < navData.discount_52wk_avg ? 'wider' : 'narrower'} than its 52-week average — a {showDisc < navData.discount_52wk_avg ? 'better-than-typical entry point' : 'tighter-than-usual spread'}.
              </span>
            )}
          </p>

          {/* Phase 5 (Jun 2026) — 1-Year Z-Score gauge. Only renders
              for curated rows (where the 52w avg discount is known).
              Tells the user whether today's premium/discount is a
              statistical outlier vs the fund's own historical band,
              not just a single-point comparison. */}
          {isCurated && (
            <CefZScoreGauge
              ticker={ticker}
              currentDiscount={showDisc}
              avg52wkDiscount={navData.discount_52wk_avg}
              strategy={navData.strategy}
            />
          )}
        </div>
      ) : null}

      <div className="mt-2.5 pt-2 border-t border-teal-300/50 dark:border-teal-700/40 space-y-2.5">
        <div>
          <h5 className="text-[11px] font-bold text-teal-800 dark:text-teal-300">Key Characteristics</h5>
          <ul className="text-[11px] text-muted-foreground leading-relaxed mt-1 space-y-0.5 list-disc list-inside">
            <li><strong className="text-foreground">Fixed Capital:</strong> share count is set at IPO; new units are not created on demand, so price is decoupled from NAV.</li>
            <li><strong className="text-foreground">Premium / Discount to NAV:</strong> CEFs frequently trade below NAV (a "discount"), letting investors buy a dollar's worth of assets for less. Persistent discounts are also a risk.</li>
            <li><strong className="text-foreground">Leverage:</strong> many CEFs borrow up to ~30-40% of assets to amplify income and total return — boosting yields in good times, magnifying losses in down markets.</li>
            <li><strong className="text-foreground">Distribution-Heavy:</strong> often distribute monthly with yields of 6-12%; some distributions may include return of capital (ROC), which lowers cost basis rather than representing new income.</li>
            <li><strong className="text-foreground">Active Management:</strong> CEFs are typically actively managed and can hold less-liquid assets (private credit, emerging markets, munis) that ETFs can't easily wrap.</li>
          </ul>
        </div>

        <div>
          <h5 className="text-[11px] font-bold text-teal-800 dark:text-teal-300 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Important Risks
          </h5>
          <p className="text-[11px] text-muted-foreground leading-relaxed mt-1">
            Leverage amplifies both gains and losses. Discount-to-NAV can persist or widen, locking in a paper-loss vs the underlying assets. Distributions are not guaranteed and may include return of capital — always check the fund's <strong className="text-foreground">Section 19a notice</strong> to understand the source of payouts before relying on the yield.
          </p>
        </div>
      </div>
    </div>
  );
}

function ReitExplainerCard() {
  return (
    <div
      className="mt-3 rounded-lg border p-3 border-emerald-300/70 bg-emerald-50/70 dark:bg-emerald-950/20 dark:border-emerald-700/60"
      data-testid="modal-reit-explainer"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 mt-0.5">
          <span
            className="inline-flex items-center justify-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200 border border-emerald-300/60 dark:border-emerald-700/60"
          >
            REIT
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-bold text-emerald-800 dark:text-emerald-300">Real Estate Investment Trust</span>
            <span className="text-[11px] text-muted-foreground">Pronounced "reet"</span>
          </div>
          <p className="text-[11px] text-muted-foreground leading-relaxed mt-1">
            A REIT (Real Estate Investment Trust) is a company that owns, operates, or finances income-producing real estate across various sectors — letting individuals invest in commercial real estate through stocks, ETFs, or funds without buying or managing buildings directly.
          </p>
        </div>
      </div>

      <div className="mt-2.5 pt-2 border-t border-emerald-300/50 dark:border-emerald-700/40 space-y-2.5">
        <div>
          <h5 className="text-[11px] font-bold text-emerald-800 dark:text-emerald-300">How REITs Work</h5>
          <ul className="text-[11px] text-muted-foreground leading-relaxed mt-1 space-y-0.5 list-disc list-inside">
            <li><strong className="text-foreground">90% rule:</strong> by law, a REIT must distribute at least 90% of taxable income to shareholders — which is why yields of 4-8% are typical and some mortgage REITs reach 10-15%.</li>
            <li><strong className="text-foreground">Pass-through taxation:</strong> the REIT itself avoids corporate-level tax by qualifying under IRS rules, but shareholders pay ordinary-income tax on most of the distribution (not the lower qualified-dividend rate).</li>
            <li><strong className="text-foreground">Exchange-traded:</strong> publicly-listed REITs trade on NYSE / NASDAQ just like regular stocks, with live prices and normal brokerage access.</li>
          </ul>
        </div>

        <div>
          <h5 className="text-[11px] font-bold text-emerald-800 dark:text-emerald-300">Main Flavors</h5>
          <ul className="text-[11px] text-muted-foreground leading-relaxed mt-1 space-y-0.5 list-disc list-inside">
            <li><strong className="text-foreground">Equity REITs:</strong> own physical real estate (apartments, offices, malls, warehouses, data centers, hotels, cell towers, healthcare facilities). Revenue = rent. Examples: O (net lease retail), PLD (industrial), AMT (cell towers), WELL (healthcare).</li>
            <li><strong className="text-foreground">Mortgage REITs (mREITs):</strong> don't own buildings — they own mortgages and mortgage-backed securities. Earn the spread between short-term borrowing and long-term loan rates. Higher yields (10-15%) but much more interest-rate sensitive. Examples: AGNC, NLY, STWD, BXMT.</li>
            <li><strong className="text-foreground">Hybrid REITs:</strong> mix both. Less common since most focus on one model.</li>
          </ul>
        </div>

        <div>
          <h5 className="text-[11px] font-bold text-emerald-800 dark:text-emerald-300 flex items-center gap-1">
            <span>💡</span> Why Investors Use Them
          </h5>
          <ul className="text-[11px] text-muted-foreground leading-relaxed mt-1 space-y-0.5 list-disc list-inside">
            <li><strong className="text-foreground">High, stable income:</strong> the 90% payout rule makes REITs one of the most reliable dividend-yielding asset classes.</li>
            <li><strong className="text-foreground">Inflation hedge:</strong> rent and property values tend to rise with inflation over time, giving REITs some built-in CPI protection.</li>
            <li><strong className="text-foreground">Real-estate exposure without the hassle:</strong> no tenants, no repairs, no mortgage paperwork — just buy shares.</li>
            <li><strong className="text-foreground">Diversification:</strong> REIT returns don't perfectly track the S&P 500, so they can smooth a stock-heavy portfolio.</li>
          </ul>
        </div>

        <div>
          <h5 className="text-[11px] font-bold text-emerald-800 dark:text-emerald-300 flex items-center gap-1">
            <span>⚠️</span> Important Risks
          </h5>
          <ul className="text-[11px] text-muted-foreground leading-relaxed mt-1 space-y-0.5 list-disc list-inside">
            <li><strong className="text-foreground">Interest-rate sensitivity:</strong> REIT prices often drop when long-term rates rise, because bonds compete for income investors' dollars.</li>
            <li><strong className="text-foreground">Sector concentration:</strong> some REITs are 100% hotel, 100% office, 100% data-center, etc. A bad year for that sector hits hard.</li>
            <li><strong className="text-foreground">AFFO vs EPS:</strong> ignore GAAP earnings. For REITs the right payout-ratio denominator is <em>AFFO</em> (Adjusted Funds From Operations), which is why our Quality Score uses a relaxed 90% payout cap for REITs instead of the usual 75%.</li>
            <li><strong className="text-foreground">mREIT volatility:</strong> mortgage REITs can cut their dividend fast if mortgage spreads collapse — treat 10%+ yields with eyes open.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

/**
 * IssuerAttribution — small "Issuer: {Name}" line that appears under
 * the About / Fund Overview business_summary. Detects the issuer
 * family from the fund's name (case-insensitive substring match) so
 * newly-launched funds light up automatically without per-ticker
 * hardcoding.
 *
 * NestYield is matched per-ticker because its underlying funds don't
 * include "NestYield" in the Yahoo longName.
 */
// IMPORTANT — these tables are 100% client-side constants. They are
// shipped in the React JS bundle at build time and are NEVER touched
// by Yahoo Finance or Massive data refreshes (those endpoints only
// write to MongoDB stock_cache). The only way a fund could "lose" its
// issuer link is if a fetch overwrote the fund's `name` so it no
// longer contained the issuer keyword. The backend's `name_locked`
// flag prevents that for any fund whose name we have manually pinned;
// for un-locked names, every issuer below appears as a stable prefix
// in Yahoo's longName, so the regex match survives.
//
// Order matters: more-specific patterns must come before more-general
// ones (e.g., iShares before BlackRock; Western Asset / Putnam before
// Franklin Templeton; SPDR before any standalone "Trust" patterns).
const ISSUER_FAMILIES = [
  // ===== Specialty / option-income / leveraged issuers =====
  { match: /yieldmax/i,                    label: 'YieldMax',                  url: 'https://www.yieldmaxetfs.com/' },
  { match: /direxion/i,                    label: 'Direxion',                  url: 'https://www.direxion.com/' },
  { match: /roundhill/i,                   label: 'Roundhill',                 url: 'https://www.roundhillinvestments.com/' },
  { match: /bitwise/i,                     label: 'Bitwise',                   url: 'https://bitwiseinvestments.com/' },
  { match: /proshares/i,                   label: 'ProShares',                 url: 'https://www.proshares.com/' },
  { match: /amplify/i,                     label: 'Amplify',                   url: 'https://amplifyetfs.com/' },
  { match: /\banfield\b/i,                 label: 'Regents Park',              url: 'https://regentsparkfunds.com/our-funds/' },
  { match: /angel\s*oak/i,                 label: 'Angel Oak',                 url: 'https://angeloakcapital.com/' },
  { match: /\bares\b/i,                    label: 'Ares',                      url: 'https://www.aresmgmt.com/' },
  { match: /avantis/i,                     label: 'Avantis',                   url: 'https://www.avantisinvestors.com/home/' },
  // AB = AllianceBernstein. Matches the AB parent holding company AND
  // every AB-branded ETF (12 funds: CAM, CORB, CPLS, EYEG, HYFI, SYFI,
  // SDFI, TAFM, TAFL, TAFI, YEAR, HIDV). Start-anchored `^AB\s` avoids
  // collisions with AbbVie / Abbott / Arbor Realty (no space after "AB").
  { match: /alliancebernstein|^AB\s/i,     label: 'AllianceBernstein',         url: 'https://www.alliancebernstein.com/us/en-us/investments/products/etf.html' },

  // ===== Mega-issuers (specific brand patterns first) =====
  // iShares is BlackRock's ETF brand — must run BEFORE the BlackRock
  // pattern so iShares funds land on ishares.com.
  { match: /\bishares\b/i,                 label: 'iShares',                   url: 'https://www.ishares.com/us' },
  { match: /\binvesco\b/i,                 label: 'Invesco',                   url: 'https://www.invesco.com/us/financial-products/etfs' },
  // SPDR funds are issued by State Street Global Advisors. Match either name.
  { match: /(state\s+street|\bspdr\b)/i,   label: 'SPDR / State Street',       url: 'https://www.ssga.com/us/en/intermediary/etfs' },
  { match: /first\s+trust/i,               label: 'First Trust',               url: 'https://www.ftportfolios.com/' },
  { match: /wisdomtree/i,                  label: 'WisdomTree',                url: 'https://www.wisdomtree.com/' },
  { match: /vanguard/i,                    label: 'Vanguard',                  url: 'https://investor.vanguard.com/investment-products/etfs' },
  { match: /global\s+x/i,                  label: 'Global X',                  url: 'https://www.globalxetfs.com/' },
  { match: /nuveen/i,                      label: 'Nuveen',                    url: 'https://www.nuveen.com/' },
  // Western Asset and Putnam are Franklin Templeton subsidiaries but
  // have their own dedicated brand sites — match them BEFORE Franklin.
  { match: /western\s+asset/i,             label: 'Western Asset',             url: 'https://www.westernasset.com/' },
  { match: /\bputnam\b/i,                  label: 'Putnam',                    url: 'https://www.putnam.com/individual/etf' },
  { match: /\bfranklin\b/i,                label: 'Franklin Templeton',        url: 'https://www.franklintempleton.com/' },
  // Fidelity Investments — exclude FNF/FIS via lookahead ("Fidelity National…").
  { match: /\bfidelity\b(?!\s+national)/i, label: 'Fidelity',                  url: 'https://www.fidelity.com/etfs/overview' },
  // JPMorgan Asset Management. JPM (the bank) is in SUPPRESS_AUTO_ISSUER.
  { match: /jpmorgan|jp\s*morgan/i,        label: 'JPMorgan',                  url: 'https://am.jpmorgan.com/us/en/asset-management/per/products/etfs' },
  // BlackRock CEFs (BCAT, BCX, BBN, BIE, …). iShares already matched above.
  { match: /\bblackrock\b/i,               label: 'BlackRock',                 url: 'https://www.blackrock.com/us/individual' },
  { match: /\bvaneck\b/i,                  label: 'VanEck',                    url: 'https://www.vaneck.com/us/en/' },
  { match: /\bpimco\b/i,                   label: 'PIMCO',                     url: 'https://www.pimco.com/en-us/investments/etf' },
  { match: /eaton\s+vance/i,               label: 'Eaton Vance',               url: 'https://www.eatonvance.com/' },
  { match: /dimensional/i,                 label: 'Dimensional',               url: 'https://www.dimensional.com/us-en' },
  { match: /goldman\s+sachs/i,             label: 'Goldman Sachs',             url: 'https://am.gs.com/en-us/individual/products/etfs' },
  { match: /bondbloxx/i,                   label: 'BondBloxx',                 url: 'https://bondbloxxetf.com/' },
  { match: /xtrackers/i,                   label: 'Xtrackers (DWS)',           url: 'https://etf.dws.com/en-us/' },
  { match: /\bpacer\b/i,                   label: 'Pacer',                     url: 'https://www.paceretfs.com/' },
  { match: /\bschwab\b/i,                  label: 'Schwab',                    url: 'https://www.schwabassetmanagement.com/' },
  { match: /john\s+hancock/i,              label: 'John Hancock',              url: 'https://www.jhinvestments.com/' },
  { match: /simplify/i,                    label: 'Simplify',                  url: 'https://www.simplify.us/etfs' },
  { match: /flexshares/i,                  label: 'FlexShares',                url: 'https://www.flexshares.com/' },
  { match: /capital\s+group/i,             label: 'Capital Group',             url: 'https://www.capitalgroup.com/individual/investments/etf.html' },
  { match: /virtus/i,                      label: 'Virtus',                    url: 'https://www.virtus.com/' },
  { match: /\babrdn\b/i,                   label: 'abrdn',                     url: 'https://www.abrdn.com/en-us' },
  { match: /kraneshares/i,                 label: 'KraneShares',               url: 'https://kraneshares.com/' },
  { match: /bny\s+mellon|\bbny\b/i,        label: 'BNY Mellon',                url: 'https://im.bnymellon.com/us/en/individual.html' },
  { match: /cohen\s*[&]?\s*steers/i,       label: 'Cohen & Steers',            url: 'https://www.cohenandsteers.com/' },
  { match: /\bpgim\b/i,                    label: 'PGIM',                      url: 'https://www.pgim.com/investments/' },
  // Columbia Threadneedle — COLB (Columbia Banking System) is in SUPPRESS_AUTO_ISSUER.
  { match: /\bcolumbia\b/i,                label: 'Columbia Threadneedle',     url: 'https://www.columbiathreadneedleus.com/' },
  { match: /trueshares/i,                  label: 'TrueShares',                url: 'https://www.true-shares.com/' },
  { match: /victoryshares/i,               label: 'VictoryShares',             url: 'https://www.vcm.com/' },
  { match: /neuberger/i,                   label: 'Neuberger Berman',          url: 'https://www.nb.com/' },
  { match: /cambria/i,                     label: 'Cambria',                   url: 'https://www.cambriafunds.com/' },
  { match: /\balps\b/i,                    label: 'ALPS',                      url: 'https://www.alpsfunds.com/' },
  { match: /\bmfs\b/i,                     label: 'MFS',                       url: 'https://www.mfs.com/en-us/individual-investor.html' },
  // NYLI / MainStay / IndexIQ are all New York Life Investments brands.
  { match: /\b(nyli|mainstay|indexiq)\b/i, label: 'New York Life Investments', url: 'https://www.newyorklifeinvestments.com/etf' },
  { match: /rivernorth/i,                  label: 'RiverNorth',                url: 'https://rivernorth.com/' },
  // Matthews Asia — MATW (Matthews International Corp) is in SUPPRESS_AUTO_ISSUER.
  { match: /\bmatthews\b/i,                label: 'Matthews Asia',             url: 'https://global.matthewsasia.com/' },
  { match: /calamos/i,                     label: 'Calamos',                   url: 'https://www.calamos.com/' },
  { match: /sprott/i,                      label: 'Sprott',                    url: 'https://sprott.com/' },
  { match: /doubleline/i,                  label: 'DoubleLine',                url: 'https://doublelineetfs.com/' },
  { match: /\blifex\b/i,                   label: 'LifeX (Stone Ridge)',       url: 'https://lifexfunds.com/' },
  { match: /defiance/i,                    label: 'Defiance',                  url: 'https://www.defianceetfs.com/' },
  { match: /\bneos\b/i,                    label: 'NEOS',                      url: 'https://neosfunds.com/' },
  { match: /janus\s+henderson|\bjanus\b/i, label: 'Janus Henderson',           url: 'https://www.janushenderson.com/en-us/investor/' },
  { match: /brookfield/i,                  label: 'Brookfield',                url: 'https://www.brookfield.com/' },
  { match: /\bhartford\b/i,                label: 'Hartford Funds',            url: 'https://www.hartfordfunds.com/' },
  { match: /\btcw\b/i,                     label: 'TCW',                       url: 'https://www.tcw.com/' },
  { match: /t\.\s*rowe\s+price/i,          label: 'T. Rowe Price',             url: 'https://www.troweprice.com/' },
  { match: /american\s+century/i,          label: 'American Century',          url: 'https://www.americancentury.com/' },
  { match: /innovator/i,                   label: 'Innovator',                 url: 'https://www.innovatoretfs.com/' },
  { match: /federated\s+hermes/i,          label: 'Federated Hermes',          url: 'https://www.federatedhermes.com/us/' },
  // Morgan Stanley Investment Management. MS (the bank) is in SUPPRESS_AUTO_ISSUER.
  { match: /morgan\s+stanley/i,            label: 'Morgan Stanley IM',         url: 'https://www.morganstanley.com/im/en-us/individual-investor.html' },
  { match: /gabelli/i,                     label: 'Gabelli',                   url: 'https://gabelli.com/' },
  { match: /\bf\/m\b/i,                    label: 'F/m Investments',           url: 'https://fminvest.com/' },
  { match: /overlay\s+shares/i,            label: 'Overlay Shares',            url: 'https://overlayshares.com/' },
  { match: /\bkurv\b/i,                    label: 'Kurv',                      url: 'https://kurvinvest.com/' },
  // GraniteShares — includes their YieldBOOST option-income family
  // (TQQY, TSYY, YSPY) plus COMB (Commodity Strategy No K-1) and HIPS
  // (US High Income). All five funds carry the "GraniteShares" prefix.
  { match: /granite\s*shares/i,            label: 'GraniteShares',             url: 'https://graniteshares.com/' },
  // Harbor Capital Advisors. Anchored to start-of-name so we skip
  // "Bar Harbor Bankshares" (BHB), "Two Harbors Investment Corp" (TWO),
  // and the "Virtus Stone Harbor" funds (EDF, VEMY). Backend uses the
  // HARBOR ISSUER_ALIASES entry to return the exact 8-ticker family
  // when the peer-compare widget asks for "Harbor".
  { match: /^harbor\b/i,                   label: 'Harbor',                    url: 'https://www.harborcapital.com/' },
  // Teucrium — agricultural / commodity / leveraged crypto ETFs.
  // Family of 4: TILL, CXRN, WXET, XXRP. Backend uses the TEUCRIUM
  // ISSUER_ALIASES entry to short-circuit the peer-compare lookup.
  { match: /\bteucrium\b/i,                label: 'Teucrium',                  url: 'https://teucrium.com/' },
  // Cornerstone Advisors — 2 closed-end funds (CRF, CLM). Both names
  // start with "Cornerstone " and there are no false-positive
  // collisions in the cache. Each ticker keeps its own dedicated
  // fund-page URL (cornerstonetotalreturnfund.com / -strategicvaluefund.com)
  // already locked on the stock_cache rows, so the issuer ribbon links
  // to the parent advisor site.
  { match: /^cornerstone\b/i,              label: 'Cornerstone',               url: 'https://www.cornerstoneadvisorsinc.com/' },
  // Distillate Capital — 3 actively-managed equity ETFs (DSTL, DSMC,
  // DSTX). All three names start with "Distillate " — no collisions
  // in the cache. Backend DISTILLATE alias short-circuits the peer
  // lookup.
  { match: /^distillate\b/i,               label: 'Distillate',                url: 'https://distillatecapital.com/' },
  // Donegal Group — dual-class shares (DGICA / DGICB), NOT a fund
  // family. Anchored to start-of-name. Issuer ribbon links to the
  // investor-relations site; the mini-table is relabeled
  // "Other share classes" via SHARE_CLASS_KEYWORDS below.
  { match: /^donegal\s+group/i,            label: 'Donegal Group',             url: 'https://investors.donegalgroup.com/' },
];

// Issuer keywords whose "peer" group is actually multiple share
// classes of the SAME company (dual-class structures), not separate
// fund products. The mini-table renders with the header
// "Other share classes" instead of "Top peer funds by yield" so the
// wording reads correctly. Values match what `issuerSearchKeyword()`
// resolves to (i.e., the actual keyword passed to the backend search).
const SHARE_CLASS_KEYWORDS = new Set([
  'Donegal',  // DGICA / DGICB
]);

// Tickers whose `name` matches an ISSUER_FAMILIES regex but is NOT
// actually a fund product (operating companies, banks, lookalike
// brand names). These suppress the auto-injected "Issuer:" line.
const SUPPRESS_AUTO_ISSUER = new Set([
  'GS',    // The Goldman Sachs Group, Inc. (operating co)
  'MS',    // Morgan Stanley (operating co)
  'JPM',   // JPMorgan Chase & Co. (bank)
  'BLK',   // BlackRock, Inc. itself
  'BK',    // The Bank of New York Mellon Corp (operating co)
  'TROW',  // T. Rowe Price Group, Inc. itself
  'FNF',   // Fidelity National Financial (NOT Fidelity Investments)
  'FIS',   // Fidelity National Information Services (NOT Fidelity)
  'MATW',  // Matthews International Corp (NOT Matthews Asia funds)
  'COLB',  // Columbia Banking System (NOT Columbia Threadneedle)
]);

// Per-ticker overrides — used when a specific ticker has a stable
// product-page URL different from the family homepage. Wins over
// ISSUER_FAMILIES.
//
// Vanguard pattern is reliable (`/investment-products/etfs/profile/<ticker-lowercase>`)
// and Schwab is `/products/<ticker-lowercase>`. JPMorgan and SSGA URLs
// use opaque CUSIP/slug identifiers — those have to be hand-curated.
const PER_TICKER_ISSUER = {
  // BlackRock CEFs
  BCAT: { label: 'BlackRock', url: 'https://www.blackrock.com/us/individual/products/315530/blackrock-capital-allocation-term-trust' },

  // JPMorgan flagship dividend / option-income ETFs
  JEPI: { label: 'JPMorgan',  url: 'https://am.jpmorgan.com/us/en/asset-management/adv/products/jpmorgan-equity-premium-income-etf-etf-shares-46641q332' },
  JEPQ: { label: 'JPMorgan',  url: 'https://am.jpmorgan.com/us/en/asset-management/adv/products/jpmorgan-nasdaq-equity-premium-income-etf-etf-shares-46654q203' },

  // Schwab — `/products/<ticker>` works for every Schwab-issued ETF.
  SCHD: { label: 'Schwab',    url: 'https://www.schwabassetmanagement.com/products/schd' },
  SCHB: { label: 'Schwab',    url: 'https://www.schwabassetmanagement.com/products/schb' },
  SCHX: { label: 'Schwab',    url: 'https://www.schwabassetmanagement.com/products/schx' },
  SCHA: { label: 'Schwab',    url: 'https://www.schwabassetmanagement.com/products/scha' },
  SCHV: { label: 'Schwab',    url: 'https://www.schwabassetmanagement.com/products/schv' },
  SCHE: { label: 'Schwab',    url: 'https://www.schwabassetmanagement.com/products/sche' },
  SCHF: { label: 'Schwab',    url: 'https://www.schwabassetmanagement.com/products/schf' },
  SCHQ: { label: 'Schwab',    url: 'https://www.schwabassetmanagement.com/products/schq' },
  SCHI: { label: 'Schwab',    url: 'https://www.schwabassetmanagement.com/products/schi' },
  SCYB: { label: 'Schwab',    url: 'https://www.schwabassetmanagement.com/products/scyb' },

  // Vanguard — `/investment-products/etfs/profile/<ticker>` is universal.
  VOO:  { label: 'Vanguard',  url: 'https://investor.vanguard.com/investment-products/etfs/profile/voo' },
  VTI:  { label: 'Vanguard',  url: 'https://investor.vanguard.com/investment-products/etfs/profile/vti' },
  VYM:  { label: 'Vanguard',  url: 'https://investor.vanguard.com/investment-products/etfs/profile/vym' },
  VYMI: { label: 'Vanguard',  url: 'https://investor.vanguard.com/investment-products/etfs/profile/vymi' },
  VIG:  { label: 'Vanguard',  url: 'https://investor.vanguard.com/investment-products/etfs/profile/vig' },
  VIGI: { label: 'Vanguard',  url: 'https://investor.vanguard.com/investment-products/etfs/profile/vigi' },
  VTV:  { label: 'Vanguard',  url: 'https://investor.vanguard.com/investment-products/etfs/profile/vtv' },
  VUG:  { label: 'Vanguard',  url: 'https://investor.vanguard.com/investment-products/etfs/profile/vug' },
  VOOG: { label: 'Vanguard',  url: 'https://investor.vanguard.com/investment-products/etfs/profile/voog' },
  VOOV: { label: 'Vanguard',  url: 'https://investor.vanguard.com/investment-products/etfs/profile/voov' },
  BND:  { label: 'Vanguard',  url: 'https://investor.vanguard.com/investment-products/etfs/profile/bnd' },
  BNDX: { label: 'Vanguard',  url: 'https://investor.vanguard.com/investment-products/etfs/profile/bndx' },
  BNDW: { label: 'Vanguard',  url: 'https://investor.vanguard.com/investment-products/etfs/profile/bndw' },
  BSV:  { label: 'Vanguard',  url: 'https://investor.vanguard.com/investment-products/etfs/profile/bsv' },
  BLV:  { label: 'Vanguard',  url: 'https://investor.vanguard.com/investment-products/etfs/profile/blv' },
  VEA:  { label: 'Vanguard',  url: 'https://investor.vanguard.com/investment-products/etfs/profile/vea' },
  VWO:  { label: 'Vanguard',  url: 'https://investor.vanguard.com/investment-products/etfs/profile/vwo' },
  VXUS: { label: 'Vanguard',  url: 'https://investor.vanguard.com/investment-products/etfs/profile/vxus' },
  VNQ:  { label: 'Vanguard',  url: 'https://investor.vanguard.com/investment-products/etfs/profile/vnq' },
  VTEB: { label: 'Vanguard',  url: 'https://investor.vanguard.com/investment-products/etfs/profile/vteb' },

  // SPDR / State Street flagships (slug pattern is hand-curated).
  SPY:  { label: 'SPDR / State Street', url: 'https://www.ssga.com/us/en/intermediary/etfs/state-street-spdr-sp-500-etf-trust-spy' },
};

const NESTYIELD_TICKERS = ['EGGY', 'EGGQ', 'EGGS'];

// Map issuer label → screener search keyword. Defaults to label, but
// some labels don't substring-match the DB names (e.g., "SPDR / State
// Street" — DB uses "State Street ..." prefix; "New York Life
// Investments" — DB uses "NYLI MacKay ..."). Per-ticker entries default
// to the same lookup.
const ISSUER_SEARCH_KEYWORD = {
  'SPDR / State Street': 'State Street',
  'New York Life Investments': 'NYLI',
  'LifeX (Stone Ridge)': 'LifeX',
  'Xtrackers (DWS)': 'Xtrackers',
  'F/m Investments': 'F/m',
  'Hartford Funds': 'Hartford',
  'Morgan Stanley IM': 'Morgan Stanley',
  'Janus Henderson': 'Janus',
  'Federated Hermes': 'Federated',
  'Cohen & Steers': 'Cohen',
  // Donegal Group dual-class shares — backend alias key is DONEGAL,
  // and `SHARE_CLASS_KEYWORDS` below uses 'Donegal Group' so the
  // header reads "Other share classes". The frontend sends 'Donegal'
  // (uppercased to DONEGAL backend-side) to hit the alias and return
  // both DGICA and DGICB cleanly.
  'Donegal Group': 'Donegal',
  'Capital Group': 'Capital Group',
  'BNY Mellon': 'BNY',
  // "AllianceBernstein" is resolved backend-side to the full family
  // (parent AB + 12 ETFs) via the ISSUER_ALIASES map in search_stocks.
  // This sidesteps the name-substring mismatch (parent is
  // "AllianceBernstein…" but ETFs are named "AB …").
};

function issuerSearchKeyword(label) {
  return ISSUER_SEARCH_KEYWORD[label] || label;
}

/**
 * Round 86 (Feb 2026) — Issuer → Distributions page URL map.
 *
 * Goal: when an issuer publishes per-distribution Section 19(a) notices
 * (or any breakdown of Return-of-Capital vs Net Income vs Capital
 * Gain), we surface a "Distributions & 19(a) notices →" link on the
 * Stock Modal so the user can open the source page in a new tab and
 * read the official breakdown themselves. We deliberately link to the
 * LANDING page (not a direct PDF download) so users see context and
 * choose what to open.
 *
 * Keys match the issuer label that backend returns in `issuer_name`
 * (case-sensitive — same format used in `ISSUER_SEARCH_KEYWORD`).
 *
 * If an issuer isn't in this map the link self-hides. Add new issuers
 * here as they come up; the URLs should always point to the page that
 * lists / links to the per-distribution notices, not the PDF itself.
 */
const ISSUER_DISTRIBUTIONS_URL = {
  // YieldMax option-income ETFs — MSTY, NVDY, ULTY, MARO, FIAT, etc.
  // Publishes a 19(a) notice for every weekly distribution.
  'YieldMax': 'https://www.yieldmaxetfs.com/our-etfs/',
  // Defiance Daily Income ETFs — QQQY, JEPY, IWMY, RDTE, etc.
  'Defiance ETFs': 'https://www.defianceetfs.com/distributions/',
  'Defiance': 'https://www.defianceetfs.com/distributions/',
  // Roundhill weekly-pay ETFs — XDTE, QDTE, RDTE, etc.
  'Roundhill Investments': 'https://www.roundhillinvestments.com/etf-distributions/',
  'Roundhill': 'https://www.roundhillinvestments.com/etf-distributions/',
  // GraniteShares — leveraged single-stock ETFs (NVDL, TSLY peers etc).
  'GraniteShares': 'https://graniteshares.com/institutional/us/en-us/etfs/',
  // Tidal Investments (parent of YieldMax / Defiance — kept for fallback).
  'Tidal Investments': 'https://www.tidalfinancialgroup.com/our-funds',
  // JPMorgan Premium Income — JEPI, JEPQ.
  'JPMorgan': 'https://am.jpmorgan.com/us/en/asset-management/adv/products/jpmorgan-equity-premium-income-etf-etf-shares-46641q332',
  'JPMorgan Asset Management': 'https://am.jpmorgan.com/us/en/asset-management/adv/products/jpmorgan-equity-premium-income-etf-etf-shares-46641q332',
  // Global X covered-call / Income family.
  'Global X': 'https://www.globalxetfs.com/funds/',
  // Cohen & Steers CEFs — UTG, RNP, RQI, etc.
  'Cohen & Steers': 'https://www.cohenandsteers.com/strategies/closed-end-funds/',
  'Cohen': 'https://www.cohenandsteers.com/strategies/closed-end-funds/',
  // First Trust — RC% reported in some option-income ETFs.
  'First Trust': 'https://www.ftportfolios.com/Retail/etf/ETFFamily.aspx',
  // Cornerstone CEFs — CLM and CRF have heavy ROC.
  'Cornerstone': 'https://www.cornerstoneadvisorsinc.com/funds/',
  // Eaton Vance CEFs.
  'Eaton Vance': 'https://funds.eatonvance.com/closed-end-funds.php',
  // BlackRock / iShares CEFs.
  'BlackRock': 'https://www.blackrock.com/us/individual/products/investment-funds#!type=cef',
  // Nuveen CEFs (very ROC-heavy lineup).
  'Nuveen': 'https://www.nuveen.com/en-us/closed-end-funds',
  // PIMCO CEFs (PDI, PTY, etc).
  'PIMCO': 'https://www.pimco.com/en-us/investments/closed-end-funds',
  // Calamos CEFs (CHI, CHY).
  'Calamos': 'https://www.calamos.com/closed-end-funds/',
  // Tortoise Capital — energy/MLP CEFs.
  'Tortoise': 'https://tortoiseadvisors.com/closed-end-funds/',
  // Reaves Asset Management — UTG CEF.
  'Reaves': 'https://www.reavesfunds.com/closed-end-funds/',
  // Barings BDCs.
  'Barings': 'https://www.barings.com/us/individual/funds/closed-end',
  // Kayne Anderson MLP CEFs.
  'Kayne Anderson': 'https://kaynefunds.com/products/closed-end-funds/',
  // Pioneer Natural Resources / Amplify covered-call ETFs.
  'Amplify': 'https://amplifyetfs.com/etfs/',
};

function issuerDistributionsUrl(label) {
  if (!label) return null;
  return ISSUER_DISTRIBUTIONS_URL[label] || null;
}

/**
 * Round 86 (Feb 2026) — Top-level resolver used by `HeadlineInsight`
 * to linkify the "Section 19(a) notices" phrase in the high-yield
 * warning banner. Mirrors `IssuerAttribution`'s decision tree:
 *   1. NestYield ticker list
 *   2. Per-ticker override (PER_TICKER_ISSUER)
 *   3. Issuer family regex match on stock.name
 * Returns the issuer's distributions landing page URL, or null.
 */
function resolveIssuerDistributionsUrl(stock) {
  if (!stock) return null;
  const upper = (stock.ticker || '').toUpperCase();
  if (NESTYIELD_TICKERS.includes(upper)) {
    return issuerDistributionsUrl('NestYield');
  }
  const perTicker = PER_TICKER_ISSUER[upper];
  if (perTicker) {
    return issuerDistributionsUrl(perTicker.label);
  }
  if (SUPPRESS_AUTO_ISSUER.has(upper)) return null;
  const family = ISSUER_FAMILIES.find((f) => f.match.test(stock.name || stock.long_name || ''));
  if (!family) return null;
  return issuerDistributionsUrl(family.label);
}

function PeerCompareLink({ keyword, currentTicker, onNavigate }) {
  const navigate = useNavigate();
  const location = useLocation();
  if (!keyword) return null;
  const onClick = (e) => {
    e.preventDefault();
    if (typeof onNavigate === 'function') onNavigate();
    // Round 79 — encode the modal's ticker + page-level fromPath so
    // BackOrHomeButton can re-open this modal at this ticker when the
    // user hits Back on the screener.
    const fromPath = location.pathname + location.search;
    if (currentTicker) {
      navigate(`/screener?q=${encodeURIComponent(keyword)}`, {
        state: { modalOrigin: { fromPath, ticker: currentTicker } },
      });
    } else {
      navigate(`/screener?q=${encodeURIComponent(keyword)}`);
    }
  };
  return (
    <a
      href={`/screener?q=${encodeURIComponent(keyword)}`}
      onClick={onClick}
      className="text-[10px] text-primary hover:underline mt-0.5 inline-block"
      data-testid={`issuer-peer-compare-${(currentTicker || '').toUpperCase()}`}
    >
      Compare peer funds from same issuer →
    </a>
  );
}

/**
 * IssuerDistributionsLink — opens the issuer's distributions page in a
 * new tab. Used by ETF / CEF stock modals so users can read Section
 * 19(a) notices (RoC / Net Income / Capital Gain breakdowns) directly
 * from the source. We never embed or download the PDFs ourselves —
 * the link points to the issuer's landing/index page so users can
 * choose which notice to read.
 *
 * Self-hides if we don't have a URL mapped for this issuer (most large
 * ROC-prone issuers are mapped; smaller niche ones default to no link).
 */
function IssuerDistributionsLink({ issuerLabel, currentTicker }) {
  const url = issuerDistributionsUrl(issuerLabel);
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-[10px] text-primary hover:underline mt-0.5 inline-block ml-3"
      data-testid={`issuer-19a-link-${(currentTicker || '').toUpperCase()}`}
      title="Opens the issuer's distributions page in a new tab — read Section 19(a) notices for Return-of-Capital breakdowns."
    >
      Section 19(a) notices →
    </a>
  );
}

const PEER_API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Reuse the same base for the tax-lot indicator fetch (Round 71c).
const API = PEER_API;

/**
 * PeerSpecCard — "shopping mode" spec preview that pops out when the
 * user hovers a peer row in PeerFundsMiniTable. Shows yield, annual
 * div, frequency, sector, price, 1y/6m/1m perf, payout ratio, plus
 * any risk badges (BDC / ADR / Leveraged / Futures). Lower commitment
 * than clicking the row to swap modals — lets users price-shop across
 * an issuer's family in seconds.
 */
function PeerSpecCard({ peer, inWatchlist }) {
  const fmtPct = (n) => (typeof n === 'number' ? `${n >= 0 ? '+' : ''}${n.toFixed(2)}%` : '—');
  const fmtPctPlain = (n) => (typeof n === 'number' ? `${n.toFixed(2)}%` : '—');
  const fmtUSD = (n) => (typeof n === 'number' ? `$${n.toFixed(2)}` : '—');
  const tone = (n) =>
    typeof n !== 'number'
      ? 'text-muted-foreground'
      : n >= 0
        ? 'text-green-600 dark:text-green-400'
        : 'text-red-600 dark:text-red-400';
  const div = peer.dividend_rate;
  const freq = peer.frequency || '—';
  const yld = peer.dividend_yield;
  const sector = peer.sector || '—';
  const Stat = ({ label, value, valueClass }) => (
    <div className="flex flex-col">
      <span className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className={`text-[12px] font-mono font-semibold ${valueClass || 'text-foreground'}`}>{value}</span>
    </div>
  );
  return (
    <div className="space-y-2">
      <div>
        <div className="flex items-baseline gap-2">
          <span className="font-mono font-bold text-sm text-yellow-700 dark:text-yellow-400">{peer.ticker}</span>
          <span className="text-[11px] text-muted-foreground truncate">{peer.name || ''}</span>
          {inWatchlist && (
            <span className="ml-auto inline-flex items-center gap-0.5 text-[9px] font-semibold text-primary shrink-0">
              <Star className="w-2.5 h-2.5 fill-primary text-primary" />
              Watching
            </span>
          )}
        </div>
        <div className="text-[10px] text-muted-foreground/80 mt-0.5">{sector}</div>
      </div>

      <div className="grid grid-cols-2 gap-2 pt-1">
        <Stat label="Yield" value={fmtPctPlain(yld)} valueClass="text-green-600 dark:text-green-400" />
        <Stat label="Annual Div" value={fmtUSD(div)} />
        <Stat label="Frequency" value={freq} />
        <Stat label="Price" value={fmtUSD(peer.price)} />
        <Stat label="1Y" value={fmtPct(peer.perf_1y)} valueClass={tone(peer.perf_1y)} />
        <Stat label="6M" value={fmtPct(peer.perf_6m)} valueClass={tone(peer.perf_6m)} />
        <Stat label="1M" value={fmtPct(peer.perf_1m)} valueClass={tone(peer.perf_1m)} />
        <Stat label="Payout" value={fmtPctPlain(peer.payout_ratio)} />
      </div>

      {(peer.is_bdc || peer.is_cef || peer.is_adr || peer.is_reit || peer.is_leveraged || peer.uses_futures) && (
        <div className="flex flex-wrap gap-1 pt-1.5 border-t border-border/40">
          {peer.is_bdc && (
            <span className="inline-flex items-center px-1.5 py-0 rounded text-[9px] font-bold uppercase bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300 border border-indigo-300/60 dark:border-indigo-700/60">BDC</span>
          )}
          {peer.is_cef && (
            <span className="inline-flex items-center px-1.5 py-0 rounded text-[9px] font-bold uppercase bg-teal-100 text-teal-800 dark:bg-teal-950/60 dark:text-teal-300 border border-teal-300/60 dark:border-teal-700/60">CEF</span>
          )}
          {peer.is_adr && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase bg-pink-100 text-red-800 dark:bg-pink-200/90 dark:text-red-900 border border-red-300/60 dark:border-red-400/60">
              {peer.country_code && <span className="text-sm leading-none">{ccToFlag(peer.country_code)}</span>}
              ADR
            </span>
          )}
          {peer.is_reit && (
            <span className="inline-flex items-center px-1.5 py-0 rounded text-[9px] font-bold uppercase bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300/60 dark:border-emerald-700/60">REIT</span>
          )}
          {peer.is_leveraged && (
            <span className="inline-flex items-center px-1.5 py-0 rounded text-[9px] font-bold uppercase bg-orange-100 text-orange-800 dark:bg-orange-950/60 dark:text-orange-300 border border-orange-300/60 dark:border-orange-700/60">Leveraged</span>
          )}
          {peer.uses_futures && (
            <span className="inline-flex items-center px-1.5 py-0 rounded text-[9px] font-bold uppercase bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-300/60 dark:border-amber-700/60">Futures</span>
          )}
        </div>
      )}

      <div className="text-[10px] text-muted-foreground/70 italic pt-1 border-t border-border/40">
        Click row to open full {peer.ticker} modal →
      </div>
    </div>
  );
}

/**
 * PeerFundsMiniTable — inline 5-row table inside the Stock Modal About
 * card showing the highest-yielding peer funds from the same issuer.
 * Powered by the same `/api/stocks/search?q=<keyword>` endpoint that
 * the peer-compare link uses, then sorted client-side by yield desc.
 *
 * If `onSelectStock` is provided, clicking a peer row swaps the modal
 * to that ticker without closing. Otherwise falls through to navigating
 * to /screener?q=<keyword>.
 */
function PeerFundsMiniTable({ keyword, currentTicker, onSelectStock, onClose, watchlist, onToggleWatchlist, user, onLoginRequired }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [peers, setPeers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const upperCurrent = (currentTicker || '').toUpperCase();

  useEffect(() => {
    if (!keyword) return;
    let cancelled = false;
    axios
      .get(`${PEER_API}/stocks/search`, { params: { q: keyword, limit: 100 }, withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        const arr = (res.data || []).filter(
          (s) =>
            (s.ticker || '').toUpperCase() !== upperCurrent &&
            typeof s.dividend_yield === 'number' &&
            s.dividend_yield > 0 &&
            s.price &&
            s.frequency,
        );
        arr.sort((a, b) => (b.dividend_yield || 0) - (a.dividend_yield || 0));
        setPeers(arr.slice(0, 5));
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => { cancelled = true; };
  }, [keyword, upperCurrent]);

  if (!keyword || !loaded) return null;
  if (peers.length === 0) return null;

  const onRowClick = (tkr) => {
    if (typeof onSelectStock === 'function') {
      onSelectStock(tkr);
    } else {
      if (typeof onClose === 'function') onClose();
      // Round 79 — same back-restore encoding as PeerCompareLink.
      const fromPath = location.pathname + location.search;
      if (currentTicker) {
        navigate(`/screener?q=${encodeURIComponent(keyword)}`, {
          state: { modalOrigin: { fromPath, ticker: currentTicker } },
        });
      } else {
        navigate(`/screener?q=${encodeURIComponent(keyword)}`);
      }
    }
  };

  return (
    <PeerFundsTableInner
      peers={peers}
      keyword={keyword}
      currentTicker={upperCurrent}
      onRowClick={onRowClick}
      watchlist={watchlist}
      onToggleWatchlist={onToggleWatchlist}
      user={user}
      onLoginRequired={onLoginRequired}
    />
  );
}

/**
 * PeerFundsTableInner — the actual table UI + the centered hover
 * popover. Split out so we can use hooks (hover state, animation
 * machine) only when there are peers to render. The popover is
 * portaled into a `position: fixed` element centered in the viewport
 * so it never gets clipped by the modal scroll-container — this
 * fixes the "popover off the right edge" bug the user reported.
 *
 * On peer→peer hover transitions the popover briefly shrinks (scale
 * 0.5 + opacity 0) and grows back to full size with the new ticker's
 * data, matching the user's requested motion pattern.
 */
function PeerFundsTableInner({ peers, keyword, currentTicker, onRowClick, watchlist, onToggleWatchlist, user, onLoginRequired }) {
  const [hoveredTicker, setHoveredTicker] = useState(null);
  const [activePeer, setActivePeer] = useState(null);
  const [phase, setPhase] = useState('idle'); // 'idle' | 'visible' | 'shrinking'

  // Phase machine: when hoveredTicker changes from peer A → peer B,
  // first shrink the visible card, then swap data and grow back.
  useEffect(() => {
    if (!hoveredTicker) {
      setActivePeer(null);
      setPhase('idle');
      return;
    }
    const incoming = peers.find((p) => p.ticker === hoveredTicker);
    if (!incoming) return;
    if (!activePeer) {
      setActivePeer(incoming);
      setPhase('visible');
      return;
    }
    if (activePeer.ticker === incoming.ticker) {
      setPhase('visible');
      return;
    }
    // Shrink → swap → grow back
    setPhase('shrinking');
    const t = setTimeout(() => {
      setActivePeer(incoming);
      // Two RAFs to ensure the scale-50 frame paints before scale-100.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setPhase('visible'));
      });
    }, 130);
    return () => clearTimeout(t);
  }, [hoveredTicker, peers, activePeer]);

  return (
    <>
      <div
        className="mt-2 rounded border border-border/60 bg-muted/30 overflow-hidden"
        data-testid={`peer-funds-table-${currentTicker}`}
        onMouseLeave={() => setHoveredTicker(null)}
      >
        <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border/60 bg-muted/50 flex items-center justify-between">
          <span>{SHARE_CLASS_KEYWORDS.has(keyword) ? 'Other share classes' : 'Top peer funds by yield'}</span>
          <span className="text-[9px] font-normal normal-case text-muted-foreground/70">{keyword}</span>
        </div>
        <ul className="divide-y divide-border/40">
          {peers.map((p) => {
            const inWatch = Array.isArray(watchlist) && watchlist.includes(p.ticker);
            const handleStarClick = (e) => {
              // Stop the click bubbling up to the row swap handler.
              e.stopPropagation();
              e.preventDefault();
              if (!user) {
                if (typeof onLoginRequired === 'function') onLoginRequired();
                return;
              }
              if (typeof onToggleWatchlist === 'function') onToggleWatchlist(p.ticker);
              // "Just-saved" toast — fires regardless of add vs remove
              // so the user always gets quick visual confirmation.
              const wasIn = inWatch;
              if (wasIn) {
                toast(`★ ${p.ticker} removed from watchlist`, { duration: 2200 });
              } else {
                toast.success(`★ ${p.ticker} added to watchlist`, {
                  duration: 2800,
                  description: 'View it from the header → Watchlist',
                });
              }
            };
            return (
              <li key={p.ticker}>
                <button
                  type="button"
                  onClick={() => onRowClick(p.ticker)}
                  onMouseEnter={() => setHoveredTicker(p.ticker)}
                  onFocus={() => setHoveredTicker(p.ticker)}
                  className="w-full flex items-center gap-2 pl-1.5 pr-2 py-1 text-[11px] hover:bg-primary/5 transition-colors text-left"
                  data-testid={`peer-fund-row-${p.ticker}`}
                >
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={handleStarClick}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleStarClick(e); }}
                    className="shrink-0 p-0.5 rounded hover:bg-primary/10 transition-colors cursor-pointer"
                    aria-label={inWatch ? `Remove ${p.ticker} from watchlist` : `Add ${p.ticker} to watchlist`}
                    title={inWatch ? `Remove ${p.ticker} from watchlist` : `Add ${p.ticker} to watchlist`}
                    data-testid={`peer-fund-star-${p.ticker}`}
                  >
                    <Star
                      className={`w-3 h-3 ${
                        inWatch
                          ? 'fill-primary text-primary'
                          : 'text-muted-foreground hover:text-primary'
                      }`}
                    />
                  </span>
                  <span className="font-mono font-bold text-yellow-700 dark:text-yellow-400 w-12 shrink-0">{p.ticker}</span>
                  <span className="text-muted-foreground truncate flex-1">
                    {p.name || ''}
                    {SHARE_CLASS_KEYWORDS.has(keyword) && /[A-Z]$/.test(p.ticker || '') && (
                      <span className="ml-1 text-[10px] font-medium opacity-80">
                        (Class {(p.ticker || '').slice(-1)})
                      </span>
                    )}
                  </span>
                  <span className="font-mono font-semibold text-green-600 dark:text-green-400 shrink-0">
                    {p.dividend_yield.toFixed(2)}%
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Centered hover popover — fixed-positioned so it never gets
          clipped by the modal's scroll container. Shrinks on peer
          swap, then grows back to full size. */}
      {activePeer && (
        <div
          className="fixed inset-0 z-[60] pointer-events-none flex items-center justify-center"
          aria-hidden="true"
        >
          <div
            key={activePeer.ticker}
            className={`pointer-events-none w-72 max-w-[calc(100vw-2rem)] rounded-lg border bg-popover text-popover-foreground shadow-xl p-3 transition-all duration-150 ease-out ${
              phase === 'shrinking'
                ? 'scale-50 opacity-0'
                : 'scale-100 opacity-100'
            }`}
            data-testid={`peer-fund-spec-${activePeer.ticker}`}
          >
            <PeerSpecCard
              peer={activePeer}
              inWatchlist={Array.isArray(watchlist) && watchlist.includes(activePeer.ticker)}
            />
          </div>
        </div>
      )}
    </>
  );
}

function IssuerAttribution({ ticker, name, onNavigate, onSelectStock, watchlist, onToggleWatchlist, user, onLoginRequired }) {
  const upper = (ticker || '').toUpperCase();
  if (NESTYIELD_TICKERS.includes(upper)) {
    return (
      <div className="text-[10px] text-muted-foreground mt-1.5" data-testid="issuer-nestyield">
        <div>
          Issuer:{' '}
          <a
            href="https://nestyield.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
            data-testid="issuer-nestyield-link"
          >
            NestYield
          </a>
        </div>
        <PeerCompareLink keyword="NestYield" currentTicker={ticker} onNavigate={onNavigate} />
        <IssuerDistributionsLink issuerLabel="NestYield" currentTicker={ticker} />
        <PeerFundsMiniTable
          keyword="NestYield"
          currentTicker={ticker}
          onSelectStock={onSelectStock}
          onClose={onNavigate}
          watchlist={watchlist}
          onToggleWatchlist={onToggleWatchlist}
          user={user}
          onLoginRequired={onLoginRequired}
        />
      </div>
    );
  }
  // Per-ticker override (e.g., BCAT → specific BlackRock product page).
  const perTicker = PER_TICKER_ISSUER[upper];
  if (perTicker) {
    const slug = perTicker.label.toLowerCase().replace(/\s+/g, '-');
    const kw = issuerSearchKeyword(perTicker.label);
    return (
      <div className="text-[10px] text-muted-foreground mt-1.5" data-testid={`issuer-${slug}`}>
        <div>
          Issuer:{' '}
          <a
            href={perTicker.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
            data-testid={`issuer-${slug}-link`}
          >
            {perTicker.label}
          </a>
        </div>
        <PeerCompareLink keyword={kw} currentTicker={ticker} onNavigate={onNavigate} />
        <IssuerDistributionsLink issuerLabel={perTicker.label} currentTicker={ticker} />
        <PeerFundsMiniTable
          keyword={kw}
          currentTicker={ticker}
          onSelectStock={onSelectStock}
          onClose={onNavigate}
          watchlist={watchlist}
          onToggleWatchlist={onToggleWatchlist}
          user={user}
          onLoginRequired={onLoginRequired}
        />
      </div>
    );
  }
  // Suppress auto-attribution for operating companies whose names
  // happen to match a fund-issuer regex (e.g., GS, MS, JPM, FNF, MATW).
  if (SUPPRESS_AUTO_ISSUER.has(upper)) return null;
  const family = ISSUER_FAMILIES.find((f) => f.match.test(name || ''));
  if (!family) return null;
  const slug = family.label.toLowerCase().replace(/\s+/g, '-');
  const kw = issuerSearchKeyword(family.label);
  return (
    <div className="text-[10px] text-muted-foreground mt-1.5" data-testid={`issuer-${slug}`}>
      <div>
        Issuer:{' '}
        <a
          href={family.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline"
          data-testid={`issuer-${slug}-link`}
        >
          {family.label}
        </a>
      </div>
      <PeerCompareLink keyword={kw} currentTicker={ticker} onNavigate={onNavigate} />
      <IssuerDistributionsLink issuerLabel={family.label} currentTicker={ticker} />
      <PeerFundsMiniTable
        keyword={kw}
        currentTicker={ticker}
        onSelectStock={onSelectStock}
        onClose={onNavigate}
        watchlist={watchlist}
        onToggleWatchlist={onToggleWatchlist}
        user={user}
        onLoginRequired={onLoginRequired}
      />
    </div>
  );
}


function InfoCard({ icon: Icon, title, children, accent }) {
  return (
    <div className="rounded-lg border bg-card p-3 space-y-1" data-testid="info-card">
      <div className="flex items-center gap-2">
        {Icon && <Icon className={`w-3.5 h-3.5 ${accent || 'text-muted-foreground'}`} />}
        <h4 className="text-xs font-bold">{title}</h4>
      </div>
      <div className="space-y-0">
        {children}
      </div>
    </div>
  );
}

function InfoRow({ label, value, bold, defKey }) {
  // Round 92o++++++++N++++++++++ (Jun 2026) — optional `defKey` wraps
  // the label in an `AbbrTip` popover (the same component used in
  // `MetricRow` on the Metrics tab). Tap the label → contained
  // popover with a definition + Glossary deep link. No layout shift,
  // phone-safe (popover is fixed-positioned with `max-w-[calc(100vw-
  // 2rem)]`). Added so the Dividend Info tab can disclose the
  // "broker-settled vs issuer-announced" date distinction without
  // adding a permanent caption row that would push the action
  // buttons down on small screens.
  //
  // Labels are `font-bold` (Jun 2026 UI pass) so the small text in
  // the Dividend Info cards holds its own next to the bold values.
  // Values keep their existing `font-bold` when the `bold` prop is
  // set; otherwise default to `font-medium`.
  return (
    <div className="flex items-center justify-between py-1 border-b border-border/50 last:border-0">
      <span className="text-xs font-bold text-muted-foreground">
        {defKey ? <AbbrTip term={defKey}>{label}</AbbrTip> : label}
      </span>
      <span className={`text-xs font-mono ${bold ? 'font-bold' : 'font-medium'}`}>{value}</span>
    </div>
  );
}

function MetricCard({ label, value, subtitle, valueColor }) {
  return (
    <div className="rounded-lg border bg-card p-3 text-center" data-testid="metric-card">
      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-1">{label}</p>
      <p className={`text-xl font-bold font-mono ${valueColor || ''}`}>{value}</p>
      {subtitle && <p className="text-[10px] font-bold text-muted-foreground mt-0.5">{subtitle}</p>}
    </div>
  );
}

// Phase 6a (Jun 2026) — Current Yield card augmented with a live
// "vs 10Y Treasury" spread badge. The 10Y is the institutional
// risk-free benchmark every dividend stock implicitly competes
// against. Showing the spread reframes the yield: a 4% dividend
// looks great in isolation but loses meaning when the 10Y is at
// 4.5% (negative real spread for taking equity risk).
//
// Falls back to the plain "trailing 12mo" subtitle when the curve
// hasn't been fetched yet or the stock has no dividend.
function CurrentYieldCard({ stock }) {
  const curve = useTreasuryYields();
  const inflation = useInflation();
  const y = stock?.dividend_yield;
  const ten = curve?.yield_10_year;
  const cpi = inflation?.cpi_yoy;
  const hasYield = typeof y === 'number' && Number.isFinite(y) && y > 0;
  const hasTen = typeof ten === 'number' && Number.isFinite(ten);
  const hasCpi = typeof cpi === 'number' && Number.isFinite(cpi);
  const spread = (hasYield && hasTen) ? (y - ten) : null;
  const realYield = (hasYield && hasCpi) ? computeRealYield(y, cpi) : null;
  const isProfit = spread != null && spread >= 0;
  const realIsProfit = realYield != null && realYield >= 0;

  const subtitle = (spread == null && realYield == null) ? 'trailing 12mo' : (
    <>
      <span
        className="cursor-help"
        title={spread != null ? `Dividend yield ${y.toFixed(2)}% vs 10Y Treasury ${ten.toFixed(2)}%. ${
          isProfit
            ? `Earning +${spread.toFixed(2)}pp OVER the risk-free rate \u2014 fair compensation for taking equity risk.`
            : `Yielding ${Math.abs(spread).toFixed(2)}pp BELOW the 10Y \u2014 you're taking equity risk for less income than a Treasury bond pays.`
        }` : 'trailing 12mo'}
      >
        {spread == null ? 'trailing 12mo' : (
          <>trailing 12mo · vs 10Y{' '}
            <span className={`font-mono font-bold ${
              isProfit ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
            }`}>
              {isProfit ? '+' : ''}{spread.toFixed(2)}pp
            </span>
          </>
        )}
      </span>
      {realYield != null && (
        <span
          className="block cursor-help"
          title={`Real Yield (Fisher-exact) = (1 + ${y.toFixed(2)}%) / (1 + ${cpi.toFixed(2)}% CPI YoY) − 1 = ${realYield.toFixed(2)}%. ${
            realIsProfit
              ? 'Genuinely growing your purchasing power — your dividend stream is outpacing inflation.'
              : 'Purchasing power is eroding — your dividend cash is being out-run by inflation.'
          }`}
        >
          Real Yield{' '}
          <span className={`font-mono font-bold ${
            realIsProfit ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
          }`}>
            {realIsProfit ? '+' : ''}{realYield.toFixed(2)}%
          </span>
          <span className="text-muted-foreground/70"> · <AbbrTip term="CPI">CPI</AbbrTip> {cpi.toFixed(2)}%</span>
        </span>
      )}
    </>
  );

  return (
    <MetricCard
      label="Current Yield"
      value={hasYield ? `${y.toFixed(2)}%` : '--'}
      subtitle={subtitle}
      valueColor="text-green-600 dark:text-green-400"
    />
  );
}

function estimateNextPayment(stock) {
  // If backend already provided estimates, use them
  if (stock.next_ex_date && stock.next_payment_date) {
    return { nextEx: stock.next_ex_date, nextPay: stock.next_payment_date };
  }
  // Compute from ex_dividend_date + frequency
  const exStr = stock.ex_dividend_date;
  const freq = stock.frequency;
  if (!exStr || !freq) return { nextEx: null, nextPay: null };

  const exDate = new Date(exStr);
  if (isNaN(exDate.getTime())) return { nextEx: null, nextPay: null };

  // P158·8 (Feb 2026) — Frequency-aware step. The previous single
  // freqMonths ladder collapsed Daily and Weekly into the default
  // 3-month bump, producing garbage next-ex dates for daily payers
  // (SATA-style) and weekly payers when the backend didn't supply
  // one. Now Daily walks 1 calendar day, Weekly walks 7.
  const isDaily = freq === 'Daily';
  const isWeekly = freq === 'Weekly';
  const freqMonths = freq === 'Monthly' ? 1 : freq === 'Semi-Annually' ? 6 : freq === 'Annually' ? 12 : 3;
  const now = new Date();
  let next = new Date(exDate);

  // Advance until next is in the future
  let safety = 0;
  const maxIter = isDaily ? 400 : 100;
  while (next <= now && safety < maxIter) {
    if (isDaily) next.setDate(next.getDate() + 1);
    else if (isWeekly) next.setDate(next.getDate() + 7);
    else next.setMonth(next.getMonth() + freqMonths);
    safety++;
  }

  // Skip weekends for ex-div
  while (next.getDay() === 0 || next.getDay() === 6) {
    next.setDate(next.getDate() + 1);
  }

  const payDate = new Date(next);
  // Payment ~3 business days after ex-div
  let bdays = 0;
  while (bdays < 3) {
    payDate.setDate(payDate.getDate() + 1);
    if (payDate.getDay() !== 0 && payDate.getDay() !== 6) bdays++;
  }

  const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  return { nextEx: fmt(next), nextPay: fmt(payDate) };
}

// Visual tab order for mobile swipe navigation (StockModal). Mirrors the
// JSX TabsTrigger order so a left/right swipe walks the tab strip exactly
// as it appears. `intraday` (Today) is conditionally rendered when the
// backend reports intraday is enabled — kept here too because for end
// users it's currently always-on (anonymous-accessible). The runtime
// swipe handler filters out the intraday entry when the tab is hidden
// (see useMemo'd TAB_ORDER inside StockModal).
const TAB_ORDER_FULL = [
  'dividend-info',
  'stock-metrics',
  'financial-health',
  'insights',
  'intraday',
  'div-growth',
  'non-div-growth',
  'triangle',
  'capital-analytics',
  'my-position',
  'tenk-8k-risks',
  'insider-activity',
  'my-portfolio',
  'journal',
  'yield-history',
  'news',
  'drip-calc',
];

// Round 104c (Jun 2026) — "Recently listed" header badge. Young
// tickers (IPO / spin-off < 24 months ago, e.g. SNDK re-listed
// Feb 2025) get a subtle chip next to the exchange tag so users
// know why beta, 52-week range, SMA200 and streak metrics look
// thin. `list_date` comes from Massive ticker details (already on
// the /api/stocks payload).
function RecentlyListedBadge({ listDate }) {
  if (!listDate) return null;
  let listed;
  try {
    listed = new Date(`${listDate}T00:00:00Z`);
  } catch {
    return null;
  }
  if (Number.isNaN(listed?.getTime?.())) return null;
  const ageMonths = (Date.now() - listed.getTime()) / (30.44 * 24 * 3600 * 1000);
  if (ageMonths < 0 || ageMonths >= 24) return null;
  const label = listed.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
  return (
    <AbbrTip term="RECENTLY_LISTED">
      <span
        className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-sky-100 dark:bg-sky-950/40 text-sky-800 dark:text-sky-300 ring-1 ring-sky-300/50 dark:ring-sky-700/50"
        data-testid="recently-listed-badge"
      >
        Listed {label}
      </span>
    </AbbrTip>
  );
}


export default function StockModal({ stock: rawStock, open, onClose, loading, isInWatchlist, onToggleWatchlist, isDark, prefetchedNews, user, onLoginRequired, onPositionsChanged, onViewPortfolio, initialTab, initialChartPeriod, onCompareClick, onSelectStock, watchlist, onBackToCompare, highlightFiling }) {
  // Oct 2026 — a money market fund can never be a closed-end fund. Strip
  // any stray CEF flag at render time so a bad DB stamp (an old-code
  // instance re-running the CEF name heuristic) never shows the CEF pill
  // or the CEF explainer card next to the MMF card.
  const stock = useMemo(() => {
    if (!rawStock) return rawStock;
    const isMmf = rawStock.is_money_market_fund === true || /^[A-Z]{3}XX$/.test(String(rawStock.ticker || '').toUpperCase());
    if (isMmf && (rawStock.is_cef || rawStock.cef_strategy_tag)) {
      return { ...rawStock, is_money_market_fund: true, is_cef: false, cef_strategy_tag: null };
    }
    return rawStock;
  }, [rawStock]);
  const gaTicker = open ? stock?.ticker : null;
  useEffect(() => {
    if (gaTicker) trackEvent('stock_modal_open', { ticker: gaTicker, pays_dividend: stock?.pays_dividend === false ? 'no' : 'yes' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gaTicker]);
  // Hooks must run unconditionally — early-return below on !open.
  const navigate = useNavigate();
  const location = useLocation();
  // Round P140 (Feb 2026) — Warrant lookup. Cheap 404-tolerant call
  // that lights up the WarrantPanel + WARRANT/REDEEM badge on the
  // header when the current ticker is a stock warrant. See
  // /api/warrants/{ticker} + services/warrants_pipeline.py.
  const { warrant } = useWarrant(stock?.ticker);
  // Round 79 — StockModal back-restore. Capture the page-level URL
  // at modal mount so any in-modal navigation can encode it into the
  // destination's history state for the BackOrHomeButton to honor.
  // `fromPath` is locked at mount; re-derives if the modal remounts
  // (e.g., user navigates between stocks via the chip rail). Safe to
  // include search params — the origin page reads them via useLocation
  // on mount and re-hydrates its sub-view filters.
  const fromPath = location.pathname + location.search;
  const modalTicker = stock?.ticker;
  // activeTab state is declared further below, but we need to read it
  // inside navigateWithOrigin so that the URL-replace + modalOrigin
  // both capture the tab the user was on at click time. We stash the
  // latest activeTab in a ref that gets updated by an effect right
  // after the state is declared, ensuring closures fired from event
  // handlers always see the current value.
  const activeTabRef = useRef(null);
  const navigateWithOrigin = (toPath) => {
    if (modalTicker) {
      const currentTab = activeTabRef.current || null;
      // Round 96n (Jul 3 2026) — before navigating away, replace the
      // CURRENT React-Router history entry with `?stock=TICKER&tab=TAB`
      // so the browser back-button (not just the in-app BackOrHomeButton)
      // returns to a URL Dashboard's deep-link handler can consume to
      // automatically reopen the modal on the SAME tab the user was on.
      // Uses navigate(..., {replace}) instead of window.history.replaceState
      // so React Router's internal history stays in sync.
      try {
        const url = new URL(window.location.href);
        let dirty = false;
        if (url.searchParams.get('stock') !== modalTicker) {
          url.searchParams.set('stock', modalTicker);
          dirty = true;
        }
        if (currentTab && url.searchParams.get('tab') !== currentTab) {
          url.searchParams.set('tab', currentTab);
          dirty = true;
        }
        if (dirty) {
          const relative = url.pathname + (url.search ? url.search : '') + (url.hash || '');
          navigate(relative, { replace: true });
        }
      } catch {
        // Defensive — modal-origin state below is authoritative for
        // the in-app back button anyway.
      }
      navigate(toPath, {
        state: { modalOrigin: { fromPath, ticker: modalTicker, tab: currentTab } },
      });
    } else {
      navigate(toPath);
    }
  };

  // Round 104 (Jun 2026) — in-modal ticker history. When the user
  // hops to another ticker from INSIDE the modal (ETF top holdings,
  // peer tables, issuer attribution, insights links), we stack the
  // origin ticker so a "< Back" button can walk back. Cleared when
  // the modal closes so a fresh open never shows a stale Back.
  //
  // Round 105 (Jun 2026) — entries are now {ticker, tab, scrollY}
  // snapshots so Back restores the exact tab AND scroll position the
  // user was at when they clicked away (e.g. deep in the Insights
  // peer-group card or the ETF ownership card), instead of resetting
  // to the default tab.
  const [tickerHistory, setTickerHistory] = useState([]);
  const backRestoreRef = useRef(null);
  useEffect(() => {
    if (!open) {
      setTickerHistory([]);
      backRestoreRef.current = null;
    }
  }, [open]);
  const navigateToTicker = useCallback((t) => {
    if (typeof onSelectStock !== 'function' || !t) return;
    const cur = stock?.ticker;
    if (cur && t !== cur) {
      setTickerHistory((h) => [...h, {
        ticker: cur,
        tab: activeTabRef.current || null,
        scrollY: scrollableRef.current ? scrollableRef.current.scrollTop : 0,
      }]);
    }
    onSelectStock(t);
  }, [onSelectStock, stock?.ticker]);
  const handleBackTicker = useCallback(() => {
    setTickerHistory((h) => {
      const prev = h[h.length - 1];
      if (prev && typeof onSelectStock === 'function') {
        backRestoreRef.current = { ...prev, tabApplied: false };
        onSelectStock(prev.ticker);
      }
      return h.slice(0, -1);
    });
  }, [onSelectStock]);
  // Round 105b — breadcrumb jump: click any earlier crumb to pop
  // straight back N levels, restoring that entry's tab + scroll.
  const handleJumpToCrumb = useCallback((idx) => {
    setTickerHistory((h) => {
      const entry = h[idx];
      if (entry && typeof onSelectStock === 'function') {
        backRestoreRef.current = { ...entry, tabApplied: false };
        onSelectStock(entry.ticker);
      }
      return h.slice(0, idx);
    });
  }, [onSelectStock]);

  // Round 79c — back-restore wrapper for the "View Portfolio" link in
  // MyPortfolioTab. The host page's `onViewPortfolio` callback does
  // multiple things (close the modal, refresh lists, then navigate),
  // so we can't replace it with a plain `navigateWithOrigin`. Instead
  // we let the original callback run, then patch the new history
  // entry's `state.usr.modalOrigin` so the destination's BackOrHomeButton
  // can read it. 80ms is comfortably after React Router's microtask
  // for setting up the new entry; tested against the Dashboard's
  // `showPortfolio` → `navigate('/?view=portfolio')` path.
  const onViewPortfolioWithRestore = onViewPortfolio
    ? () => {
        const captureFrom = fromPath;
        const captureTicker = modalTicker;
        onViewPortfolio();
        if (captureTicker) {
          setTimeout(() => {
            try {
              const cur = window.history.state || {};
              const usr = { ...(cur.usr || {}), modalOrigin: { fromPath: captureFrom, ticker: captureTicker } };
              window.history.replaceState({ ...cur, usr }, '');
            } catch {
              // Defensive — replaceState failures should never break
              // the user's primary navigation.
            }
          }, 80);
        }
      }
    : undefined;
  const [activeTab, setActiveTab] = useState(initialTab || 'dividend-info');

  // Keep activeTabRef in sync with the latest activeTab so
  // `navigateWithOrigin` (declared before the state) can read it
  // synchronously at click time. Required for browser-back tab
  // restoration to land on the tab the user was actually viewing.
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  // Round 96n (Jul 3 2026) — Session-storage cache of modal state.
  // Persists `{ticker, tab, scrollY}` so a browser refresh mid-view
  // restores exactly where you were (auto-reopens the modal on the
  // same tab, at the same scroll offset). Entries expire after 30
  // minutes so a much-later revisit doesn't feel spooky.
  const MODAL_STATE_KEY = 'dividrip:modal-state';
  const MODAL_STATE_TTL_MS = 30 * 60 * 1000;
  const scrollableRef = useRef(null);
  const modalShellRef = useRef(null);
  // Sep 2026 — drag-to-resize (desktop/tablet). Tab content unmounts while
  // dragging and remounts 0.5s after release so charts draw exactly once.
  const resizeScrollRef = useRef(null);
  const onResizeStart = useCallback(() => {
    resizeScrollRef.current = scrollableRef.current ? scrollableRef.current.scrollTop : 0;
  }, []);
  const modalResize = useModalResize({ settleMs: 500, onStart: onResizeStart });
  const { resizing: isResizing } = modalResize;
  useEffect(() => {
    if (isResizing || resizeScrollRef.current == null) return undefined;
    const target = resizeScrollRef.current;
    resizeScrollRef.current = null;
    if (target <= 0) return undefined;
    let n = 0;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const el = scrollableRef.current;
      if (el) el.scrollTop = target;
      if (++n < 12) setTimeout(tick, 100);
    };
    tick();
    return () => { cancelled = true; };
  }, [isResizing]);
  // Save-effect: any time ticker/tab changes, write current state.
  // Round 96n note — skip the FIRST render for a given ticker so we
  // don't overwrite the cached scrollY (500) with the mount-time
  // scrollTop (0) BEFORE the restore-effect below can read it. Once
  // the user scrolls, the throttled scroll listener starts writing
  // fresh values.
  const savedTickerRef = useRef(null);
  // Tab-to-tab switch on the SAME ticker starts the new tab at the top.
  // Ticker changes (incl. breadcrumb/back restore) are left alone so
  // the back-restore effects below can put the user right where they were.
  const prevTabTickerRef = useRef({ ticker: null, tab: null });
  useEffect(() => {
    const prev = prevTabTickerRef.current;
    prevTabTickerRef.current = { ticker: modalTicker, tab: activeTab };
    if (!modalTicker || prev.ticker !== modalTicker || prev.tab === activeTab) return;
    const br = backRestoreRef.current;
    if (br && br.ticker === modalTicker) return;
    const el = scrollableRef.current;
    if (el) el.scrollTop = 0;
  }, [modalTicker, activeTab]);
  useEffect(() => {
    if (!modalTicker) return;
    if (savedTickerRef.current !== modalTicker) {
      savedTickerRef.current = modalTicker;
      return; // first render for this ticker → let restore run first
    }
    try {
      const el = scrollableRef.current;
      const payload = {
        ticker: modalTicker,
        tab: activeTab,
        scrollY: el ? el.scrollTop : 0,
        ts: Date.now(),
      };
      sessionStorage.setItem(MODAL_STATE_KEY, JSON.stringify(payload));
    } catch { /* ignore */ }
  }, [modalTicker, activeTab]);
  // Throttled scroll persistence: update `scrollY` at most every 250ms.
  useEffect(() => {
    const el = scrollableRef.current;
    if (!el || !modalTicker) return;
    let throttle = null;
    const onScroll = () => {
      if (throttle) return;
      throttle = setTimeout(() => {
        throttle = null;
        try {
          const raw = sessionStorage.getItem(MODAL_STATE_KEY);
          if (!raw) return;
          const cur = JSON.parse(raw);
          if (cur && cur.ticker === modalTicker) {
            cur.scrollY = el.scrollTop;
            cur.ts = Date.now();
            sessionStorage.setItem(MODAL_STATE_KEY, JSON.stringify(cur));
          }
        } catch { /* ignore */ }
      }, 250);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (throttle) clearTimeout(throttle);
    };
  }, [modalTicker, activeTab]);

  // P17 (Feb 2026) — Clear the sessionStorage restore cache when the
  // modal transitions from open → closed via ANY path (X button,
  // Escape, backdrop click, in-modal "View Portfolio" link, etc.),
  // regardless of which page mounted the modal.
  //
  // Bug this fixes: the cache was designed to survive a browser
  // REFRESH mid-view (so F5 reopens the modal at the same tab/scroll).
  // But the Dashboard's `useEffect([location.search])` also reads
  // this cache on every in-app navigation. Result: user opens a
  // modal from Screener/Learn/KingsChallenge/etc., closes it, then
  // clicks "My Portfolio" or "Watchlist" in the header → the
  // navigate('/?view=…') fires Dashboard's useEffect → it reads the
  // still-populated sessionStorage → reopens the modal. Only the
  // Dashboard's OWN onClose was clearing the cache; every other
  // page's onClose left it stale.
  //
  // Refresh-restore still works because the cleanup only fires on
  // an intentional CLOSE (open: true → false). A browser refresh
  // tears the tab down without running React effect cleanups, so
  // the sessionStorage entry survives to the next mount as intended.
  //
  // NOTE: back-button-restore uses `location.state.reopenModalTicker`
  // (React Router state written by BackOrHomeButton) — a completely
  // separate mechanism. Clearing sessionStorage here does NOT affect
  // the back-restore flow.
  const prevOpenRef = useRef(open);
  useEffect(() => {
    if (prevOpenRef.current && !open) {
      try { sessionStorage.removeItem(MODAL_STATE_KEY); } catch { /* ignore */ }
      // Round P130 (Feb 2026) — Notify subscribers (StockMetricsTab
      // cache, potentially others) that the modal has closed so
      // their in-memory session caches can reset. Prevents stale
      // chart data from leaking into a re-opened modal for the same
      // ticker later in the session.
      try { window.dispatchEvent(new CustomEvent('stock-modal-closed')); } catch { /* ignore */ }
    }
    prevOpenRef.current = open;
  }, [open]);

  // Round P131 (Feb 2026) — Prefetch the slow Massive-backed
  // dividend-growth payload the instant the modal opens, before the
  // user even clicks the Dividend Info tab. Cache misses take 10-15 s
  // on Massive's slow path (streak risk chart is the direct consumer,
  // but multiple sub-cards throughout the modal read the same
  // endpoint). Firing this fetch in the background at modal-open
  // gives it a 1-3 s head start on the user's click, so by the time
  // they land on the tab the response is usually already cached
  // server-side.
  //
  // We DO NOT store the result client-side — the backend's Mongo
  // `dividend_growth_cache` already handles that. This request's
  // only purpose is to warm that server cache so the eventual real
  // fetch hits the 24-hour TTL branch and returns in ~100 ms.
  useEffect(() => {
    if (!open || !modalTicker) return undefined;
    const controller = new AbortController();
    axios
      .get(`${API}/stocks/${modalTicker}/dividend-growth`, {
        withCredentials: true,
        signal: controller.signal,
      })
      .catch(() => { /* silent — real click will retry with visible loader */ });
    return () => controller.abort();
  }, [open, modalTicker]);
  // On mount / tab change, restore cached scrollY once the tab
  // content is painted. Retries for up to 1s because tab content
  // loads asynchronously (charts, API-driven cards) and the
  // scrollable div's scrollHeight grows over time. We keep re-
  // applying scrollTop until either it "sticks" (i.e. next frame
  // still reports the same value) or the retry budget expires.
  useEffect(() => {
    if (!modalTicker) return;
    let cached;
    try {
      const raw = sessionStorage.getItem(MODAL_STATE_KEY);
      if (!raw) return;
      cached = JSON.parse(raw);
    } catch { return; }
    if (!cached || cached.ticker !== modalTicker || cached.tab !== activeTab) return;
    if ((Date.now() - (cached.ts || 0)) > MODAL_STATE_TTL_MS) return;
    const target = Number(cached.scrollY) || 0;
    if (target <= 0) return;
    let cancelled = false;
    let elapsed = 0;
    const STEP = 100;
    const MAX = 1500;
    const tick = () => {
      if (cancelled) return;
      const el = scrollableRef.current;
      if (el) {
        // Set unconditionally — browser clamps to scrollHeight-clientHeight
        // if we're asking for more than currently exists. Once content
        // finishes loading and scrollHeight expands, subsequent ticks
        // will land on the exact target.
        el.scrollTop = target;
      }
      elapsed += STEP;
      if (elapsed < MAX) setTimeout(tick, STEP);
    };
    tick();
    return () => { cancelled = true; };
  }, [modalTicker, activeTab]);

  // Round 95bt (Jun 2026) — Financial Health tab availability gate.
  // ETFs, mutual funds, and many foreign-filer ADRs return `has_data:
  // false` from `/stocks/{ticker}/financial-health` because they don't
  // file their own income/balance-sheet (they pass through to
  // underlying holdings). Without this preflight the user clicks the
  // tab, waits for the spinner, then sees an empty-state message —
  // bad UX, especially when the SAME info is already available via
  // the Stock Metrics + Insights tabs. We hide the tab entirely for
  // this modal-open session when financials aren't filed.
  //
  // Default `true` (optimistic) so the tab stays visible during the
  // initial preflight roundtrip — avoids flicker for the common case
  // where the financials DO exist. Flips false on either an explicit
  // `has_data: false` response or any network error (treat as missing).
  // Resets on ticker change so re-opening the modal at a different
  // ticker re-runs the check.
  const [hasFinancialHealth, setHasFinancialHealth] = useState(true);
  useEffect(() => {
    if (!stock?.ticker) {
      setHasFinancialHealth(true);
      return;
    }
    let cancelled = false;
    setHasFinancialHealth(true);  // re-arm optimistically on ticker change
    axios.get(`${API}/stocks/${stock.ticker}/financial-health`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        setHasFinancialHealth(r?.data?.has_data === true);
      })
      .catch((err) => {
        if (cancelled) return;
        // Sep 2026 — only an explicit 404 hides the tab; timeouts / 5xx keep it (transient).
        if (err?.response?.status === 404) setHasFinancialHealth(false);
      });
    return () => { cancelled = true; };
  }, [stock?.ticker]);

  // Feb 2026 — Capital Analytics tab preflight. Hide the trigger
  // entirely when the ticker has no `equity_quality.rank` cached.
  // Stocks fall through here for two reasons:
  //   1. The weekly EQR cron hasn't scored them yet (new IPO, edge
  //      ticker, dividend-payer that recently flipped).
  //   2. Their Massive financials are too thin to compute the 4
  //      factors (most pre-revenue biotechs land in this bucket).
  // Dividend stocks skip the preflight entirely — the tab is gated
  // off by `!isDividendStock` anyway.
  // NOTE: state declared here, but the effect that *consumes*
  // `isDividendStock` is registered below — after the
  // `isDividendStock` const exists — to avoid a TDZ ReferenceError
  // (testing-agent fix, iteration_97).
  const [hasCapitalAnalytics, setHasCapitalAnalytics] = useState(false);
  // Round 96f (Feb 2026) — cache the rank + verdict from the preflight
  // so the new `CapitalScoreBadge` in the modal header can render
  // without firing a second GET.
  const [capitalSummary, setCapitalSummary] = useState(null);

  // Round 97c (Feb 27, 2026) — Non-Div Growth tab gate. Many non-div
  // tickers (Chinese ADRs like YI, certain ETFs, micro-caps without
  // SEC filings) have NO Massive financials. Previously the Growth
  // tab still rendered for those, showing only an "Awaiting Massive
  // financials" empty state — pure visual noise. We now run a
  // preflight identical to `hasCapitalAnalytics` and hide the tab
  // entirely when revenue.annual AND eps.annual are both empty.
  const [hasNonDivGrowth, setHasNonDivGrowth] = useState(false);

  // Round 92n — "My Position" tab gate. Renders ONLY when the user
  // actually OWNS ≥1 share of this ticker in the active portfolio
  // context. Watching (watchlist-only) does NOT qualify — the
  // /portfolio/{ticker}/my-position endpoint returns 200 in BOTH cases
  // (full payload for owners, slim `is_owned=false, is_watched=true`
  // payload for watchers), so we must inspect the `is_owned` flag, not
  // just the HTTP status. Round 95bs (Jun 2026) — tightened from
  // "endpoint returned 200" to "is_owned === true" after user flagged
  // APLE (watchlist-only REIT) showing an empty My Position tab.
  const [ownsThisStock, setOwnsThisStock] = useState(false);
  useEffect(() => {
    if (!stock?.ticker || !user) {
      setOwnsThisStock(false);
      return;
    }
    let cancelled = false;
    const checkOwnership = () =>
      axios.get(`${API}/portfolio/${stock.ticker}/my-position`, { withCredentials: true })
        .then((r) => !cancelled && setOwnsThisStock(r?.data?.is_owned === true))
        .catch(() => !cancelled && setOwnsThisStock(false));
    checkOwnership();
    // Listen to portfolio-changed so adding/selling shares re-evaluates.
    const refresh = () => {
      axios.get(`${API}/portfolio/${stock.ticker}/my-position`, { withCredentials: true })
        .then((r) => setOwnsThisStock(r?.data?.is_owned === true))
        .catch(() => setOwnsThisStock(false));
    };
    window.addEventListener('portfolio-changed', refresh);
    return () => {
      cancelled = true;
      window.removeEventListener('portfolio-changed', refresh);
    };
  }, [stock?.ticker, user]);
  // Round 84k Phase C scaffolding (Feb 15, 2026) — subscribe to live
  // last-trade prices for the open ticker. When the paid WebSocket
  // plan is active, `livePrice` becomes a flickering real-time value;
  // until then it's `undefined` and the modal falls back to the
  // polling-driven `stock.price` exactly as before.
  const _modalSubTickers = useMemo(
    () => (stock?.ticker ? [String(stock.ticker).toUpperCase()] : []),
    [stock?.ticker]
  );
  const { prices: streamedPrices, status: modalStreamStatus } = usePriceStream(_modalSubTickers);
  const livePrice = stock?.ticker ? streamedPrices[String(stock.ticker).toUpperCase()]?.price : undefined;
  const displayPrice = livePrice ?? stock?.price;

  // Round 84k Phase B (Feb 15, 2026) — probe the intraday endpoint
  // once per modal-open. The endpoint returns `enabled: false` until
  // the paid Massive plan is activated (MASSIVE_INTRADAY_ENABLED=true),
  // at which point the "Today" tab appears. Cached in module memory so
  // we don't re-probe every modal open within a session.
  const [intradayEnabled, setIntradayEnabled] = useState(false);
  useEffect(() => {
    if (!open || !stock?.ticker) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/stocks/${String(stock.ticker).toUpperCase()}/intraday`,
          { withCredentials: true }
        );
        if (!cancelled) setIntradayEnabled(data?.enabled === true);
      } catch {
        if (!cancelled) setIntradayEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, stock?.ticker]);
  // Round 92o++++++++N++++++++ — Gating flags for the new
  // "10K/8K/Risks" tab + the My-Position dividend filter.
  //
  // `isAdmin` is the temporary paywall proxy for "Plus" users. When
  // the real billing/`useTier()` context ships, swap this for
  // `isPlus`. The new TenK8KRisksTab and the FilingAlertsBell in
  // AppHeader both gate on the same `user.role === 'admin'` check.
  //
  // `isDividendStock` gates the "My Position" tab so it doesn't
  // render on growth-only holdings (i.e. the user owns the ticker
  // but Massive has no recent dividend payments for it). We accept
  // any positive forward annual rate OR a positive yield as proof
  // the stock pays dividends.
  const isAdmin = user && user.role === 'admin';
  // Round 97a (Feb 2026) — `pays_dividend === false` is the
  // authoritative flag set by the daily non-div classifier. If it's
  // explicitly false we treat the ticker as non-div even when the
  // cache still carries legacy `dividend_rate` / `dividend_yield`
  // values (e.g. NVR's one-off 2008 special dividend, or SNDK's
  // 2013-15 Sandisk history before the Western Digital spin-off).
  //
  // This was the production bug: on the first click, stale dividend
  // dollar amounts from the screener row payload were tripping the
  // old `dividend_rate > 0` check and the modal flashed the
  // Dividend Info / Triangle / Div Growth tabs with historical
  // numbers on a non-div ticker. The second click hit the cleaned
  // detail endpoint and rendered correctly. Anchoring the check to
  // the explicit flag eliminates the race regardless of how dirty
  // the cache is — the cron eventually catches up, but the UI no
  // longer depends on that cron running first.
  const isDividendStock = (
    stock?.pays_dividend === false
      ? // Round 95p·10 — REIT/BDC IPOs may legitimately have
        // `pays_dividend=false` in the cache before their first
        // declaration, even though they're structurally required
        // to distribute. Keep them on the dividend UI so the tabs
        // don't flicker to non-div for a KVUE/BXDC-type ticker.
        !!(stock?.is_reit || stock?.is_bdc)
      : !!(
          stock && (
            (Number(stock.dividend_rate) > 0) ||
            (Number(stock.dividend_yield) > 0) ||
            (Number(stock.trailing_annual_dividend_rate_usd) > 0) ||
            // eligible-pending REIT / BDC that hasn't been marked
            // pays_dividend=false yet
            stock.is_reit || stock.is_bdc
          )
        )
  );

  // Capital Analytics preflight (moved here after `isDividendStock`
  // so it can be referenced as a dep without hitting the temporal
  // dead zone). See note above the `hasCapitalAnalytics` useState.
  useEffect(() => {
    if (!stock?.ticker || isDividendStock) {
      setHasCapitalAnalytics(false);
      setCapitalSummary(null);
      return;
    }
    let cancelled = false;
    setHasCapitalAnalytics(false);
    setCapitalSummary(null);
    axios.get(`${API}/stocks/${stock.ticker}/equity-quality`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        const eq = r?.data?.equity_quality || {};
        const rank = eq.rank;
        const ok = r?.data?.available === true && rank != null;
        setHasCapitalAnalytics(ok);
        if (ok) {
          setCapitalSummary({
            rank,
            verdict: eq.verdict,
            verdict_label: eq.verdict_label,
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setHasCapitalAnalytics(false);
        setCapitalSummary(null);
      });
    return () => { cancelled = true; };
  }, [stock?.ticker, isDividendStock]);

  // Round 97c (Feb 27, 2026) — Non-Div Growth preflight. Mirror of
  // `hasCapitalAnalytics` above. Fires only for non-div tickers (div
  // stocks use the Div Growth tab instead). Marks the tab as
  // available iff Massive returned at least one annual revenue OR
  // EPS row. Uses `withCredentials` to share the cookie/session so
  // it doesn't double up on auth.
  useEffect(() => {
    if (!stock?.ticker || isDividendStock) {
      setHasNonDivGrowth(false);
      return;
    }
    let cancelled = false;
    setHasNonDivGrowth(false);
    axios.get(`${API}/stocks/${stock.ticker}/non-div-growth`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        const d = r?.data || {};
        const revRows = d.revenue && Array.isArray(d.revenue.annual) ? d.revenue.annual.length : 0;
        const epsRows = d.eps && Array.isArray(d.eps.annual) ? d.eps.annual.length : 0;
        setHasNonDivGrowth(revRows > 0 || epsRows > 0);
      })
      .catch(() => {
        if (cancelled) return;
        setHasNonDivGrowth(false);
      });
    return () => { cancelled = true; };
  }, [stock?.ticker, isDividendStock]);

  // Round 86c (May 2026) — Filtered tab order for mobile swipe nav.
  // Drops `intraday` when the Today tab is hidden (backend reports
  // disabled or probe failed) so a swipe never lands on a tab that
  // doesn't exist in the DOM. Memoised so the swipe-handler ref stays
  // stable between renders.
  const TAB_ORDER = useMemo(
    () => TAB_ORDER_FULL.filter((t) => {
      // Round 95z (Jun 2026) — Section 6 of the Non-Dividend Stock
      // Expansion. Hide dividend-only tabs for non-dividend tickers
      // so the modal doesn't show empty/meaningless content for
      // pure growth stocks (FLWS, TSLA, AMZN). News stays — every
      // stock has news; Drip Calc goes because compounding requires
      // a dividend cash flow.
      const DIV_ONLY_TABS = new Set([
        'dividend-info', 'div-growth', 'triangle', 'yield-history', 'drip-calc',
      ]);
      if (!isDividendStock && DIV_ONLY_TABS.has(t)) return false;
      // Feb 2026 — Capital Analytics tab mirrors Triangle for non-div
      // stocks. Hide it for dividend payers (they get the Triangle tab
      // instead) AND for non-div stocks that have no equity_quality
      // data cached (the preflight in `hasCapitalAnalytics` decides).
      if (t === 'capital-analytics' && (isDividendStock || !hasCapitalAnalytics)) return false;
      // Round 96b (Feb 2026) — Section 6. `non-div-growth` is the
      // inverse — replaces Div Growth + Triangle + Yield History
      // for pure growth stocks. Hide it for dividend payers so the
      // tab strip doesn't double up. Round 97c (Feb 27, 2026) —
      // ALSO hide when Massive has no financials hydrated for this
      // ticker (preflight sets `hasNonDivGrowth = false`) so we
      // don't show an empty tab that only renders "Awaiting Massive
      // financials".
      if (t === 'non-div-growth' && (isDividendStock || !hasNonDivGrowth)) return false;
      if (t === 'intraday' && !intradayEnabled) return false;
      // Round 92n — `my-position` only exists in the DOM when the user
      // owns ≥1 share. Swipe must skip it for everyone else so a left/
      // right gesture doesn't dead-end on a tab whose trigger isn't
      // rendered. Round 92o++++++++N++++++++ added the dividend-stock
      // gate — non-dividend holdings hide this tab entirely.
      if (t === 'my-position' && !(ownsThisStock && isDividendStock)) return false;
      // P158·9 (Feb 2026) — Hide Triangle for Money Market Funds.
      // MMFs target a constant $1.00 NAV so the Revenue × EPS ×
      // Dividend Growth triangle simply doesn't apply. Also drop
      // it from TAB_ORDER so mobile swipe walks straight from
      // `div-growth` to the next visible tab without dead-ending
      // on a hidden TabsTrigger (recurring past bug pattern per
      // user).
      if (t === 'triangle' && stock?.is_money_market_fund) return false;
      // Round 95bt (Jun 2026) — Hide Financial Health for tickers that
      // don't file standalone financials (ETFs, mutual funds, foreign
      // ADRs). The preflight in `hasFinancialHealth` effect already
      // resolved by the time the tab strip first paints.
      if (t === 'financial-health' && !hasFinancialHealth) return false;
      // Round 92o++++++++N++++++++ — admin-only paywall proxy for the
      // 10K/8K/Risks tab. Will switch to `isPlus` when billing ships.
      if (t === 'tenk-8k-risks' && !isAdmin) return false;
      // Round 92o++++++++N++++++++++ (Feb 2026) — Insider Activity tab
      // is also admin-gated as a paywall proxy. Renders Form-4 trades
      // + 13-F institutional holders using the cache hydrated by the
      // `thirteen-f-bucket` admin warmer. Swap `isAdmin` for `isPlus`
      // when billing ships.
      if (t === 'insider-activity' && !isAdmin) return false;
      return true;
    }),
    [intradayEnabled, ownsThisStock, isDividendStock, isAdmin, hasFinancialHealth, hasCapitalAnalytics, hasNonDivGrowth, stock?.is_money_market_fund],
  );

  // Round 95z+ (Jun 2026) — Bounce away from `dividend-info` if the
  // modal lands on a non-dividend ticker (the default initialTab for
  // legacy callers). Skip to `stock-metrics` which exists for every
  // ticker.
  useEffect(() => {
    if (!isDividendStock && activeTab === 'dividend-info') {
      setActiveTab('stock-metrics');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDividendStock, stock?.ticker]);

  // Round 95bs/95bt (Jun 2026) — If the user was on `my-position` or
  // `financial-health` and the ticker changes to one where that tab is
  // no longer rendered (watchlist-only ticker / ETF without filed
  // financials), bounce them back to the safe default tab so they
  // don't sit on a now-hidden TabsContent with no visible trigger.
  //
  // Round 96b (Feb 2026) — `dividend-info` is the wrong fallback target
  // because it's the very tab that's hidden for non-dividend tickers.
  // For a non-div stock (TAB_ORDER excludes 'dividend-info'), this
  // effect was kicking activeTab BACK to 'dividend-info' the same
  // render cycle the sibling bounce-effect (1963) tried to push it
  // away to 'stock-metrics' — net result: activeTab='dividend-info',
  // no matching trigger, and the dividend-info TabsContent renders
  // its dividend cards onto a non-div stock with no active tab
  // indicator. Fall back to 'stock-metrics' which is the one trigger
  // every ticker shows.
  useEffect(() => {
    if (TAB_ORDER.length > 0 && !TAB_ORDER.includes(activeTab)) {
      setActiveTab('stock-metrics');
    }
  }, [TAB_ORDER, activeTab]);
  // Ref to the TabsList DOM node so we can smooth-scroll the active tab
  // label into view whenever activeTab changes on mobile. This makes the
  // tab strip and the content feel "attached" during a swipe.
  const tabsListRef = useRef(null);
  // Mobile-only: show a one-time swipe affordance (subtle ← / → chevrons
  // that fade in then fade out) so first-time users discover the
  // swipe-between-tabs gesture. Persists via localStorage so it never
  // shows again after the first successful load on a device.
  const [showSwipeHint, setShowSwipeHint] = useState(false);
  // Round 71c (Feb 2026): Tax-lot harvest indicator. Fires a single
  // /tax-lots fetch on modal open (auth-required → silent on logged
  // out users) and filters down to lots of the currently-displayed
  // ticker. Surfaces as a small ribbon above the tabs whenever there's
  // either a harvest opportunity OR a wash-sale risk specifically on
  // THIS ticker. The fetch is cheap (already cached in this same
  // session if the user just visited /tax-lots) and the data is
  // computed from positions the user already has, so no extra cost.
  const [taxLotInfo, setTaxLotInfo] = useState(null);

  // Computed early so other hooks below can branch on it.
  const isPreviewOnly = !!stock?._preview;

  // Round 95y (Jun 2026) — DiviDrip's Opinion verdict for the current
  // stock, surfaced from the Triangle endpoint via TriangleScorecard's
  // `onDataLoaded` callback. Used to tint the Triangle tab icon
  // (emerald for Buy, rose for Avoid, default for Neutral / unknown)
  // so users see the verdict at a glance without opening the tab.
  const [triangleVerdict, setTriangleVerdict] = useState(null);
  // Round 95ah (Jun 2026) — Executive Summary full-tab takeover state.
  // When true, the Triangle tab content is hidden behind an absolute-
  // positioned overlay rendering the brief. Close X returns the user
  // to the Triangle tab they came from.
  const [execSummaryOpen, setExecSummaryOpen] = useState(false);
  useEffect(() => { setExecSummaryOpen(false); }, [stock?.ticker]);
  // Reset whenever the modal target changes — stale verdict from the
  // previous ticker would mis-tint the icon on first paint.
  useEffect(() => { setTriangleVerdict(null); }, [stock?.ticker]);
  // Eager-fetch the verdict so the Triangle tab icon colors
  // immediately, even when the user lands on the default Dividend
  // Info tab. Radix `TabsContent` lazy-mounts so without this the
  // verdict only arrives after the user clicks Triangle. Backend is
  // 24h cached so this is effectively free, and the TriangleScorecard
  // call reuses the same cache when the tab actually opens.
  useEffect(() => {
    const tkr = stock?.ticker;
    if (!tkr || isPreviewOnly) return;
    let cancelled = false;
    axios
      .get(`${API}/stocks/${tkr}/triangle`, { withCredentials: true })
      .then((r) => {
        if (!cancelled) setTriangleVerdict(r?.data?.opinion?.verdict || null);
      })
      .catch(() => { /* silent — icon stays uncolored on failure */ });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stock?.ticker, isPreviewOnly]);

  // Reset active tab whenever the caller changes the stock / initialTab.
  // For non-dividend "preview only" stocks (e.g. AMZN, TSLA pulled in via
  // the peer-chip on-the-fly endpoint), most dividend-centric tabs are
  // hidden, so default to stock-metrics instead of dividend-info.
  useEffect(() => {
    // Round 105 — Back-restore takes priority over the default reset:
    // when this ticker change came from the in-modal Back button,
    // land on the tab the user was on, not the default.
    const br = backRestoreRef.current;
    if (br && br.ticker === stock?.ticker && br.tab) {
      setActiveTab(br.tab);
      return;
    }
    const fallback = isPreviewOnly ? 'stock-metrics' : 'dividend-info';
    // If the caller explicitly asked for a tab that's hidden in preview
    // mode, fall through to stock-metrics so the modal never shows an
    // empty tab. The dividend-only tabs are the ones we hide below.
    const HIDDEN_IN_PREVIEW = new Set(['dividend-info', 'div-growth', 'triangle', 'my-portfolio', 'yield-history', 'drip-calc']);
    const requested = initialTab || fallback;
    setActiveTab(isPreviewOnly && HIDDEN_IN_PREVIEW.has(requested) ? 'stock-metrics' : requested);
  }, [stock?.ticker, initialTab, isPreviewOnly]);

  // Round 105 — re-apply the back-restored tab once TAB_ORDER catches
  // up. Async preflights (hasCapitalAnalytics, hasFinancialHealth,
  // hasNonDivGrowth) mean the bounce effect above can kick a restored
  // tab to 'stock-metrics' before its trigger exists. One-shot per
  // restore: `tabApplied` stops us from fighting a manual tab change.
  useEffect(() => {
    const br = backRestoreRef.current;
    if (!br || br.tabApplied || br.ticker !== stock?.ticker || !br.tab) return;
    if (TAB_ORDER.includes(br.tab)) {
      if (activeTab !== br.tab) setActiveTab(br.tab);
      br.tabApplied = true;
    }
  }, [TAB_ORDER, activeTab, stock?.ticker]);

  // Round 105 — restore the saved scroll offset once we're back on
  // the restored ticker + tab. Same retry pattern as the 96n session
  // restore: tab content loads async so keep re-applying scrollTop
  // until the retry budget expires, then clear the restore snapshot.
  useEffect(() => {
    const br = backRestoreRef.current;
    if (!br || br.ticker !== stock?.ticker || activeTab !== br.tab) return;
    const target = Number(br.scrollY) || 0;
    if (target <= 0) { backRestoreRef.current = null; return; }
    let cancelled = false;
    let elapsed = 0;
    const STEP = 100;
    // 4s budget — Insights/Metrics cards load async and keep growing
    // the scrollHeight, so short budgets land shy of the target.
    const MAX = 4000;
    const el0 = scrollableRef.current;
    const stop = () => {
      cancelled = true;
      cleanup();
      if (backRestoreRef.current === br) backRestoreRef.current = null;
    };
    const cleanup = () => {
      if (el0) {
        el0.removeEventListener('wheel', stop);
        el0.removeEventListener('touchstart', stop);
      }
    };
    // User grabbing the scroll cancels the restore immediately.
    if (el0) {
      el0.addEventListener('wheel', stop, { passive: true });
      el0.addEventListener('touchstart', stop, { passive: true });
    }
    const tick = () => {
      if (cancelled) return;
      const el = scrollableRef.current;
      if (el) el.scrollTop = target;
      elapsed += STEP;
      if (elapsed < MAX) setTimeout(tick, STEP);
      else stop();
    };
    tick();
    return () => { cancelled = true; cleanup(); };
  }, [stock?.ticker, activeTab]);

  // Round 96g (Feb 2026) — Re-apply initialTab once TAB_ORDER catches up.
  // When a CapitalScoreBadge click opens the modal with
  // `initialTab='capital-analytics'`, the bounce effect above sets it
  // immediately — but the bounce-back effect (next useEffect) reverts it
  // to 'stock-metrics' because `hasCapitalAnalytics` is still pending its
  // async preflight, so TAB_ORDER doesn't yet include 'capital-analytics'.
  // Watch TAB_ORDER and re-apply the caller-requested tab once it's
  // available. Fires-and-forgets: no-ops on every render after the first
  // successful application.
  const [capitalReapplyDone, setCapitalReapplyDone] = useState(false);
  useEffect(() => {
    setCapitalReapplyDone(false);
  }, [stock?.ticker, initialTab]);
  useEffect(() => {
    if (capitalReapplyDone) return;
    // Round 105 — a Back-restore owns the tab for this ticker change;
    // mark done so the caller's initialTab never overrides it.
    if (backRestoreRef.current && backRestoreRef.current.ticker === stock?.ticker) {
      setCapitalReapplyDone(true);
      return;
    }
    // P107d (Feb 2026) — Bug: previously we only marked
    // `capitalReapplyDone=true` when we ACTUALLY re-applied
    // `initialTab`. But if the modal opened already on that tab (the
    // common case — e.g. Squeeze Radar → 'stock-metrics'), the guard
    // never flipped, so the FIRST time the user switched to another
    // tab (e.g. clicked "View Today's"), this effect would fire,
    // bounce them straight back to `initialTab`, and then finally
    // mark done. Second click would stick. Fix: mark done whenever
    // `initialTab` is present in TAB_ORDER, even if no re-apply was
    // needed — the caller's intent has been honoured either way.
    if (initialTab && TAB_ORDER.includes(initialTab)) {
      if (initialTab !== activeTab) {
        setActiveTab(initialTab);
      }
      setCapitalReapplyDone(true);
    }
  }, [TAB_ORDER, initialTab, activeTab, capitalReapplyDone]);

  // Tax-lot indicator fetch — only when modal is open AND a logged-in
  // user is present (anonymous users get nothing to show anyway). Runs
  // once per (open, ticker) and bails out silently if the user has no
  // positions or the call 401s.
  useEffect(() => {
    if (!open || !user || !stock?.ticker) {
      setTaxLotInfo(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/portfolio/tax-lots`, { withCredentials: true });
        if (cancelled) return;
        const t = (stock.ticker || '').toUpperCase();
        const lots = (data?.lots || []).filter(l => (l.ticker || '').toUpperCase() === t);
        if (lots.length === 0) { setTaxLotInfo(null); return; }
        const harvestLots = lots.filter(l => l.is_loss && !l.wash_sale_risk);
        const washLots = lots.filter(l => l.wash_sale_risk);
        const harvestable = harvestLots.reduce((sum, l) => sum + Math.abs(l.gain_loss || 0), 0);
        // Round P140·5 (Feb 2026) — Weighted average cost for the
        // WarrantPnlCalculator. Uses `cost_basis_per_share` when the
        // API returned it; falls back to `total_cost / shares` when
        // only aggregates are available.
        const totalShares = lots.reduce((sum, l) => sum + (Number(l.shares) || 0), 0);
        const totalCost = lots.reduce((sum, l) => {
          const shares = Number(l.shares) || 0;
          const perShare = Number(l.cost_basis_per_share ?? l.cost_per_share ?? 0);
          return sum + shares * perShare;
        }, 0);
        const avgCost = totalShares > 0 ? totalCost / totalShares : null;
        setTaxLotInfo({
          totalLots: lots.length,
          totalShares,
          avgCost,
          harvestLots: harvestLots.length,
          harvestable,
          washLots: washLots.length,
        });
      } catch {
        if (!cancelled) setTaxLotInfo(null);
      }
    })();
    return () => { cancelled = true; };
  }, [open, user, stock?.ticker]);

  // Whenever activeTab changes, smooth-scroll the TabsList so the active
  // tab label is centered in view. Makes the label strip and the content
  // appear to move together during a swipe, instead of the label jumping
  // while only the content animates.
  useEffect(() => {
    if (!tabsListRef.current) return;
    const el = tabsListRef.current.querySelector(`[data-testid="tab-${activeTab}"]`);
    if (!el || typeof el.scrollIntoView !== 'function') return;
    try {
      el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    } catch {
      // Older browsers without options support: fall through to hard scroll.
      el.scrollIntoView();
    }
  }, [activeTab]);

  // Round 95v (Jun 2026) — listen for the "Tag your lots →" link inside
  // the My Position WHT compare pill so a click jumps the user straight
  // to the My Portfolio tab where each lot has a clickable account-type
  // / broker badge. Custom event keeps the wiring decoupled.
  //
  // Round P107c (Feb 2026) — also used by the "— View Today's" link in
  // the Price Chart title. On mobile the modal content may be scrolled
  // way down when the link is clicked, which would leave the tab
  // strip off-screen and the user with no idea which tab is now
  // active. So on switch we reset the modal's scrollable area to the
  // top; the TabsList's own smooth-scroll effect (further down) then
  // centers the active tab label in the strip. Same treatment for any
  // future cross-tab link that dispatches this event.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      const targetTab = e?.detail?.tab;
      if (typeof targetTab !== 'string') return;
      setActiveTab(targetTab);
      // Reset the modal's scrollable area so the tab strip is
      // visible again. Guard against no ref (StrictMode/preview).
      const el = scrollableRef.current;
      if (el && typeof el.scrollTo === 'function') {
        try { el.scrollTo({ top: 0, behavior: 'smooth' }); } catch { el.scrollTop = 0; }
      }
    };
    window.addEventListener('stock-modal-switch-tab', handler);
    return () => window.removeEventListener('stock-modal-switch-tab', handler);
  }, [open]);

  // First-visit swipe-hint bootstrap. Shown once per device on any touch-
  // friendly viewport (phones + tablets, < 1024px). Desktop never sees it.
  useEffect(() => {
    if (!open) return;
    if (typeof window === 'undefined') return;
    if (window.innerWidth >= 1024) return;
    try {
      if (window.localStorage.getItem('divitrack_swipe_hint_seen') === '1') return;
    } catch { /* localStorage blocked — fall through and show once per session */ }
    setShowSwipeHint(true);
    // Persist immediately so rapid modal opens don't re-trigger.
    try { window.localStorage.setItem('divitrack_swipe_hint_seen', '1'); } catch { /* ignore */ }
    // Auto-dismiss after the CSS fade-in-then-out animation (~3.5s total).
    const timer = setTimeout(() => setShowSwipeHint(false), 3500);
    return () => clearTimeout(timer);
  }, [open, stock?.ticker]);

  // Mobile-only: swipe left/right anywhere inside the tabs area to navigate
  // between tabs. Disabled on desktop (≥ 768px). Uses the same
  // scroller-aware guard pattern as the StockTable fix so swiping the
  // TabsList (which has its own overflow-x scroll) to see hidden tab
  // labels doesn't accidentally change the tab.
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const touchEndX = useRef(null);
  const touchEndY = useRef(null);
  const scrollerRef = useRef(null);
  const startScrollLeft = useRef(0);
  // Debounce: last time a swipe advanced the tab. Prevents a single
  // lingering drag (especially on tablet where fingers are slower) from
  // firing multiple touchend events and advancing 2+ tabs at once.
  const lastSwipeAt = useRef(0);
  const MIN_TAB_SWIPE = 60;
  const SWIPE_COOLDOWN_MS = 250;

  const findHorizontalScroller = (el) => {
    let cur = (el && el.nodeType === 1) ? el : (el ? el.parentElement : null);
    while (cur) {
      const style = window.getComputedStyle(cur);
      const ox = style.overflowX;
      if ((ox === 'auto' || ox === 'scroll') && cur.scrollWidth - cur.clientWidth > 1) {
        return cur;
      }
      cur = cur.parentElement;
    }
    return null;
  };

  const onTabTouchStart = useCallback((e) => {
    // Touch-friendly viewports only. Match Tailwind's `lg:` breakpoint
    // (1024px) so phones AND tablets get the swipe gesture while genuine
    // desktop (>= 1024px) keeps its click-only behaviour.
    if (window.innerWidth >= 1024) return;
    // Round P112h (Jul 31, 2026) — Swipe-lock regions. Any element
    // ancestor with `data-swipe-lock="true"` opts out of the tab-swipe
    // gesture so touch events reach the underlying widget instead
    // (e.g. Recharts intraday chart needs finger-drag for its
    // hover tooltip and can't get it if we hijack the touch here).
    if (e.target?.closest?.('[data-swipe-lock="true"]')) return;
    const t = e.targetTouches[0];
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
    touchEndX.current = null;
    touchEndY.current = null;
    scrollerRef.current = findHorizontalScroller(e.target);
    startScrollLeft.current = scrollerRef.current ? scrollerRef.current.scrollLeft : 0;
  }, []);

  const onTabTouchMove = useCallback((e) => {
    if (window.innerWidth >= 1024) return;
    const t = e.targetTouches[0];
    touchEndX.current = t.clientX;
    touchEndY.current = t.clientY;
  }, []);

  const onTabTouchEnd = useCallback(() => {
    if (window.innerWidth >= 1024) return;
    const startX = touchStartX.current;
    const startY = touchStartY.current;
    const endX = touchEndX.current;
    const endY = touchEndY.current;
    const scroller = scrollerRef.current;
    const scrolled = scroller && Math.abs(scroller.scrollLeft - startScrollLeft.current) > 2;
    touchStartX.current = null;
    touchStartY.current = null;
    touchEndX.current = null;
    touchEndY.current = null;
    scrollerRef.current = null;

    if (startX == null || endX == null) return;
    // User was scrolling the TabsList (or any inner horizontal scroller) →
    // let that gesture handle itself, don't change tabs.
    if (scrolled) return;
    const dx = startX - endX;
    const dy = (startY ?? 0) - (endY ?? 0);
    // Vertical dominated → treat as content scroll.
    if (Math.abs(dy) > Math.abs(dx)) return;
    if (Math.abs(dx) < MIN_TAB_SWIPE) return;

    // Cooldown: ignore rapid-fire swipes so one gesture = one tab change.
    const now = Date.now();
    if (now - lastSwipeAt.current < SWIPE_COOLDOWN_MS) return;
    lastSwipeAt.current = now;

    const idx = TAB_ORDER.indexOf(activeTab);
    if (idx === -1) return;
    if (dx > 0 && idx < TAB_ORDER.length - 1) {
      setActiveTab(TAB_ORDER[idx + 1]);
    } else if (dx < 0 && idx > 0) {
      setActiveTab(TAB_ORDER[idx - 1]);
    }
    // User discovered the gesture — hide the hint if it was still showing.
    if (showSwipeHint) setShowSwipeHint(false);
  }, [activeTab, showSwipeHint, TAB_ORDER]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 backdrop-blur-sm p-2 sm:p-4"
      data-testid="stock-modal-overlay"
    >
      <div
        ref={modalShellRef}
        style={modalResize.style}
        className={`relative w-full h-[92vh] sm:h-[90vh] max-w-3xl lg:max-w-[1196px] lg:h-[calc(100vh-100px)] bg-background rounded-xl overflow-hidden flex flex-col border shadow-2xl animate-fade-in-up ${isResizing ? 'select-none ring-2 ring-primary/40' : ''}`}
        data-testid="stock-modal"
        data-resizing={isResizing ? 'true' : undefined}
      >
        {/* Sep 2026 — phone view: every informational hover `title`
            inside the modal becomes a tap popover via one delegated
            listener (see TitleTapLayer). */}
        <TitleTapLayer containerRef={modalShellRef} />
        <ModalResizeGrips start={modalResize.start} reset={modalResize.reset} />
        {/* Loading state — keep the modal close button reachable so
            the user can bail out if the fetch hangs (Yahoo timeouts,
            slow network, etc.). Without this, a 2-minute fetch would
            trap the user behind the spinner. */}
        {loading && (
          <>
            <div className="absolute top-3 right-3 z-50">
              <ModalCloseButton onClick={onClose} testId="stock-modal-close-loading" />
            </div>
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Loading stock data...</p>
                <p className="text-[11px] text-muted-foreground mt-1">Tap the X above to cancel.</p>
              </div>
            </div>
          </>
        )}

        {/* Stock data */}
        {!loading && stock && (
          <>
            {/* Header */}
            <div className="flex items-start justify-between gap-3 p-3 sm:px-4 sm:pt-3 sm:pb-2 border-b shrink-0">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Round 92o++++++++j — Company icon, proxied from
                      Massive's /v3/reference/tickers branding block.
                      Self-managing component never shows a broken
                      placeholder: it unmounts silently on error and
                      uses opacity (not display:none) so Chromium
                      actually performs the fetch. */}
                  <CompanyIcon ticker={stock.ticker} size={20} zoomable />
                  <h2 className="text-base font-bold" style={{ fontFamily: 'Chivo' }}>
                    <RecycledChevron ticker={stock.ticker} />
                    {stock.ticker}
                  </h2>
                  {stock.exchange && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-muted rounded font-mono">{stock.exchange}</span>
                  )}
                  {/* Feb 2026 — Liquidation countdown/status pill. If
                      `liquidation_last_trading_date` is in the future
                      we count down; if it's in the past (or the fund
                      is already flagged liquidated) we show a static
                      "Liquidated" pill. Either way the user cannot
                      miss it before they buy. Reads directly from
                      stock_cache-echoed fields — the /dividend-growth
                      endpoint already surfaces them but they're also
                      on the base `stock` payload for the modal. */}
                  {(() => {
                    const ltd = stock.liquidation_last_trading_date;
                    const isLiquidated = stock.liquidated || stock.delisted;
                    if (!ltd && !isLiquidated) return null;
                    let label = 'Liquidated';
                    let tone = 'bg-rose-600 text-white ring-rose-700';
                    let title = stock.liquidation_note || 'Fund has been liquidated / delisted.';
                    if (ltd) {
                      const last = new Date(ltd);
                      if (!Number.isNaN(last.getTime())) {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        last.setHours(0, 0, 0, 0);
                        const days = Math.ceil((last - today) / 86400000);
                        if (days > 0) {
                          label = `Liquidating in ${days} day${days === 1 ? '' : 's'}`;
                          tone = 'bg-amber-500 text-white ring-amber-600';
                          title = `Last trading day: ${String(ltd).slice(0, 10)}. ${stock.liquidation_note || 'See the Div Info tab for the prospectus supplement.'}`;
                        } else if (days === 0) {
                          label = 'Last trading day';
                          tone = 'bg-rose-500 text-white ring-rose-600';
                        }
                      }
                    }
                    return (
                      <span
                        className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider ring-1 cursor-help ${tone}`}
                        title={title}
                        data-testid="stock-modal-liquidation-badge"
                      >
                        {label}
                      </span>
                    );
                  })()}
                  {/* Round 84k Phase E3.2 (May 2026): preview pill
                      for non-dividend tickers that aren't in DiviDrip's
                      4,595-ticker universe. Backend `/preview` endpoint
                      sets `_preview: true` — data is fetched on the fly
                      and NEVER persisted to stock_cache. */}
                  {stock._preview && (
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-amber-100 dark:bg-amber-950/40 text-amber-900 dark:text-amber-200 ring-1 ring-amber-300/50 dark:ring-amber-700/50 cursor-help"
                      title="Not in DiviDrip's dividend database — this is a one-time preview fetch and is NOT saved or cached."
                      data-testid="stock-modal-preview-pill"
                    >
                      Preview only
                    </span>
                  )}
                  <TierHeaderBadge tier={stock.dividend_tier} />
                  {stock.dividend_tier && (
                    <BadgeHelpIcon
                      badgeKey={(stock.dividend_tier || '').toLowerCase()}
                      data-testid="modal-tier-help"
                    />
                  )}
                  {/* Round 96b (Feb 2026) — Non-Div quality tier pill
                      mirroring the dividend TierHeaderBadge slot. Shows
                      ONLY for non-dividend stocks that have been scored
                      by the nightly Massive Ratios + Income job. */}
                  {!isDividendStock && stock.non_dividend_quality_tier && (
                    <button
                      type="button"
                      onClick={() => setActiveTab('non-div-growth')}
                      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-tight border transition-colors hover:brightness-110 cursor-pointer ${
                        stock.non_dividend_quality_tier === 'GROWTH-S' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-300/60 dark:border-emerald-700/60'
                        : stock.non_dividend_quality_tier === 'GROWTH-A' ? 'bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300 border-sky-300/60 dark:border-sky-700/60'
                        : stock.non_dividend_quality_tier === 'GROWTH-B' ? 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300 border-blue-300/60 dark:border-blue-700/60'
                        : stock.non_dividend_quality_tier === 'GROWTH-C' ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-300/60 dark:border-amber-700/60'
                        : 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border-rose-300/60 dark:border-rose-700/60'
                      }`}
                      title={`Non-Dividend quality tier · score ${stock.non_dividend_quality_score ?? '—'}/100. Click to view the per-signal breakdown in the Growth tab.`}
                      data-testid="modal-non-div-tier-pill"
                    >
                      {stock.non_dividend_quality_tier.replace('GROWTH-', 'G-')}
                    </button>
                  )}
                  {/* Feb 2026 — Dividend stocks see the Dividend Quality
                      Score pill. Non-dividend stocks see the Capital
                      Analytics Rank pill instead (clicking it switches
                      to the Capital Analytics tab). Each tab's gate
                      keeps the wrong pill from ever showing on the
                      wrong ticker. */}
                  {isDividendStock && (
                    <>
                      <QualityBadge stock={stock} size="md" showLabel interactive testIdSuffix="modal-header" />
                      <BadgeHelpIcon badgeKey="quality" />
                    </>
                  )}
                  {!isDividendStock && hasCapitalAnalytics && capitalSummary && (
                    <CapitalScoreBadge
                      rank={capitalSummary.rank}
                      verdict={capitalSummary.verdict}
                      verdictLabel={capitalSummary.verdict_label}
                      size="md"
                      showLabel
                      ticker={stock?.ticker}
                      testIdSuffix="modal-header"
                      onClick={() => setActiveTab('capital-analytics')}
                    />
                  )}
                  {/* Round 85 (Feb 2026) — Yield-Trap warning badge, only
                      renders when composite signal score ≥ 2. The
                      BadgeHelpIcon is rendered INSIDE YieldTrapBadge so
                      it only shows together with the badge.
                      Round 96d (Feb 2026) — Hidden for non-dividend
                      stocks (IQST etc.); a "yield trap" concept does
                      not apply when there's no yield. */}
                  {stock?.pays_dividend !== false && (
                    <YieldTrapBadge ticker={stock.ticker} assetClass={stock.asset_class} />
                  )}
                  {/* Feb 2026 — Ghost/inactive dividend badge. Shown when
                      the backend ghost-dividend guard has classified the
                      row as historically-paying but currently inactive
                      (last real payment > 18 months ago, no recent
                      history, not a pending REIT/BDC IPO). See
                      `server._is_ghost_dividend`. */}
                  {/* Round P140·2 (Feb 2026) — Suppress the "Inactive
                      Dividend" pill for warrants. Warrants have never
                      paid a dividend and never will — labelling them
                      "Inactive Dividend" is misleading (implies they
                      used to pay). The WarrantPanel already communicates
                      "pays NO dividends". */}
                  {stock?.effective_dividend_status === 'inactive' && !warrant && (
                    <span
                      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-200 border border-slate-400/60 dark:border-slate-600/60 cursor-help"
                      title={
                        stock?.historical_last_dividend_date
                          ? `Historical dividend — last paid ${stock.historical_last_dividend_date}. No active dividend metrics are shown because the last known payment is more than 18 months old.`
                          : 'Historical dividend — currently inactive (no payment in the last 18+ months). Active dividend metrics are hidden.'
                      }
                      data-testid="modal-inactive-dividend-badge"
                    >
                      Inactive Dividend
                    </span>
                  )}
                  {stock?.effective_dividend_status === 'inactive' && !warrant && <BadgeHelpIcon badgeKey="inactive_dividend" testIdSuffix="modal" />}
                  {stock.is_leveraged && (
                    <span className="inline-flex items-center gap-1">
                      <span
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300 border border-red-300/60 dark:border-red-800/60 cursor-help"
                        title="Leveraged / inverse ETF — amplified risk; not suitable for long-term dividend investing"
                        data-testid="modal-leveraged-badge"
                      >
                        <AlertTriangle className="w-2.5 h-2.5" />
                        Leveraged
                      </span>
                      <BadgeHelpIcon badgeKey="leveraged" />
                    </span>
                  )}
                  {/* Round P140 (Feb 2026) — Warrant badge in the modal
                      header, next to Leveraged / CEF / BDC. Reflects
                      the redemption status (active / soon / redeemed)
                      based on the /api/warrants/{ticker} response. */}
                  {warrant && (
                    <WarrantBadge
                      status={warrant.status}
                      deadline={warrant.redemption_deadline}
                      price={warrant.redemption_price}
                      isUnit={!!warrant.is_unit}
                      testIdSuffix="modal-header"
                    />
                  )}
                  {!warrant && isSpacRight(stock) && (
                    <span className="inline-flex items-center gap-1">
                      <RightBadge testIdSuffix="modal-header" />
                      <BadgeHelpIcon badgeKey="right" testIdSuffix="modal" />
                    </span>
                  )}
                  {stock.uses_futures && !stock.is_leveraged && (
                    <span className="inline-flex items-center gap-1">
                      <span
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-300/60 dark:border-amber-800/60 cursor-help"
                        title="Futures-based fund — uses derivatives for exposure. Carries margin/leverage risk and roll costs; returns can diverge from the underlying over time."
                        data-testid="modal-futures-badge"
                      >
                        <AlertTriangle className="w-2.5 h-2.5" />
                        Futures
                      </span>
                      <BadgeHelpIcon badgeKey="futures" />
                    </span>
                  )}
                  {/* Round 98e (Jul 2026) — Founder Stake chip. Rendered
                      right before ADR / BDC / CEF / REIT so it sits
                      just before the last structure/risk flag when
                      those are present (matches Screener row order).
                      If no other flags follow, this simply becomes the
                      last badge. Clicking opens the Insider &
                      Institutional tab so users can jump straight to
                      the Form 3 baseline table that fired the
                      detection. Admin-gated to match the Insider tab
                      gate at line 2216. */}
                  {isAdmin && stock.form3_founder_stake?.detected && (
                    <span className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setActiveTab('insider-activity'); }}
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300 border border-sky-300/60 dark:border-sky-700/60 hover:brightness-110 cursor-pointer"
                        title={(() => {
                          const fs = stock.form3_founder_stake || {};
                          const owner = fs.top_owner || 'Top insider';
                          const role = fs.top_role || 'Insider';
                          const sharesStr = fs.top_shares
                            ? fs.top_shares >= 1_000_000
                              ? `${(fs.top_shares / 1_000_000).toFixed(1)}M`
                              : `${(fs.top_shares / 1_000).toFixed(0)}K`
                            : '—';
                          const pct = fs.top_pct != null
                            ? ` (${(fs.top_pct * 100).toFixed(1)}% of float)`
                            : '';
                          return `Founder Stake Detector — ${owner} (${role}) holds ${sharesStr} baseline shares${pct}. Click to open the Insider & Institutional tab.`;
                        })()}
                        data-testid="modal-founder-stake-badge"
                      >
                        <Flag className="w-2.5 h-2.5" />
                        Founder
                      </button>
                      <BadgeHelpIcon badgeKey="founder_stake" />
                    </span>
                  )}
                  {stock.is_adr && (
                    <span className="inline-flex items-center gap-1">
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-pink-100 text-red-800 dark:bg-pink-200/90 dark:text-red-900 border border-red-300/60 dark:border-red-400/60 cursor-help"
                        title={`American Depositary Receipt — U.S.-listed certificate representing shares of a foreign company. Trades and pays dividends in USD.${stock.country_code ? `\n\nCountry of origin: ${ccName(stock.country_code)}` : ''}`}
                        data-testid="modal-adr-badge"
                      >
                        {/* Round 95q (Jun 2026) — country flag for ADR
                            tax-treaty awareness. Falls back gracefully
                            when country_code is missing. */}
                        {stock.country_code && (
                          <span className="text-sm leading-none">{ccToFlag(stock.country_code)}</span>
                        )}
                        ADR
                      </span>
                      <BadgeHelpIcon badgeKey="adr" />
                    </span>
                  )}
                  {stock.is_bdc && (
                    <span className="inline-flex items-center gap-1">
                      <span
                        className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300 border border-indigo-300/60 dark:border-indigo-700/60 cursor-help"
                        title="Business Development Company — closed-end fund that loans to small/mid-sized private businesses. By law, distributes 90%+ of taxable income; yields of 8-12% are normal."
                        data-testid="modal-bdc-badge"
                      >
                        BDC
                      </span>
                      <BadgeHelpIcon badgeKey="bdc" />
                    </span>
                  )}
                  {/* Sep 2026 — Mega Tier Elite badge (server-checked each open; vanishes when the stock leaves the list). */}
                  <MegaTierEliteBadge ticker={stock.ticker} onNavigate={navigateWithOrigin} />
                  {/* Round P121 (Feb 2026) — SPAC / Blank-Check badge.
                      Distinct from BDC: SPACs are cash shells with
                      zero dividends. Grey/slate palette signals
                      "not a dividend pick" without alarm colours. */}
                  {stock.is_treasury_vehicle && (
                    <span className="inline-flex items-center gap-1">
                      <AbbrTip term="TREASURY_VEHICLE" onGuideNavigate={navigateWithOrigin} className="no-underline">
                        <span
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300 border border-indigo-300/60 dark:border-indigo-700/60 cursor-help hover:bg-indigo-200/70 dark:hover:bg-indigo-900/60 transition-colors"
                          data-testid="modal-treasury-vehicle-badge"
                        >
                          Treasury Vehicle
                        </span>
                      </AbbrTip>
                      <BadgeHelpIcon badgeKey="pref_treasury" testIdSuffix="modal" />
                      {/* Round P152 — Call-risk / par chip. Rendered for
                          treasury-vehicle preferreds too (e.g. STRD) so
                          admin-confirmed par value + call terms surface
                          in the modal. */}
                      <PreferredCallChip stock={stock} />
                    </span>
                  )}
                  {/* Round P151 (Jun 2026) — Generalized Preferred badge
                      (non-treasury preferreds like STRRP/WVVIP). Amber
                      palette. Shows cumulative status inline when known
                      and a parent-common chip that swaps the modal to
                      the issuer's common stock (STRRP → STRR). */}
                  {stock.is_preferred && !stock.is_treasury_vehicle && (
                    <span className="inline-flex items-center gap-1">
                      <AbbrTip
                        term={stock.preferred_cumulative === true ? 'PREFERRED_STOCK_CUMULATIVE' : stock.preferred_cumulative === false ? 'PREFERRED_STOCK_NON_CUMULATIVE' : 'PREFERRED_STOCK'}
                        onGuideNavigate={navigateWithOrigin}
                        className="no-underline"
                      >
                        <span
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-300/60 dark:border-amber-700/60 cursor-help hover:bg-amber-200/70 dark:hover:bg-amber-900/60 transition-colors"
                          data-testid="modal-preferred-badge"
                        >
                          Preferred{stock.preferred_cumulative === true ? ' · Cumulative' : stock.preferred_cumulative === false ? ' · Non-Cumulative' : ''}
                        </span>
                      </AbbrTip>
                      <BadgeHelpIcon badgeKey={stock.preferred_cumulative === true ? 'pref_cumulative' : stock.preferred_cumulative === false ? 'pref_non_cum' : 'pref_preferred'} testIdSuffix="modal" />
                      {stock.preferred_parent_ticker && typeof onSelectStock === 'function' && (
                        <button
                          type="button"
                          onClick={() => navigateToTicker(stock.preferred_parent_ticker)}
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-muted text-muted-foreground border border-border cursor-pointer hover:bg-accent hover:text-accent-foreground transition-colors"
                          title={`Open ${stock.preferred_parent_ticker} — the issuer's common stock`}
                          data-testid="modal-preferred-parent-chip"
                        >
                          Common: {stock.preferred_parent_ticker}
                        </button>
                      )}
                      {/* Round P152 (Jun 2026) — Call-risk chip from
                          admin-confirmed terms. Red when trading above
                          par AND past the call date (a call = instant
                          loss). Amber when above par with a future call
                          date. Quiet par note otherwise. */}
                      <PreferredCallChip stock={stock} />
                    </span>
                  )}
                  {stock.is_spac && (
                    <span className="inline-flex items-center gap-1">
                      <span
                        className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-slate-200 text-slate-800 dark:bg-slate-800/70 dark:text-slate-200 border border-slate-400/60 dark:border-slate-600/60 cursor-help"
                        title="Blank-Check / SPAC — Special Purpose Acquisition Company. A cash shell listed to acquire a private business. Pays NO dividends. Ticker often ends in U/UN (a 'unit' = share + warrant)."
                        data-testid="modal-spac-badge"
                      >
                        SPAC
                      </span>
                      <BadgeHelpIcon badgeKey="spac" />
                    </span>
                  )}
                  {stock.is_cef && (
                    <span className="inline-flex items-center gap-1">
                      <span
                        className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-teal-100 text-teal-800 dark:bg-teal-950/60 dark:text-teal-300 border border-teal-300/60 dark:border-teal-700/60 cursor-help"
                        title="Closed-End Fund — exchange-traded fund with a fixed share count that can trade at a premium or discount to NAV. Often uses leverage; many distribute monthly with high yields."
                        data-testid="modal-cef-badge"
                      >
                        CEF
                        {stock.cef_strategy_tag && (
                          <span
                            className="ml-1 pl-1 border-l border-teal-400/50 dark:border-teal-600/60 font-semibold normal-case tracking-normal"
                            title={`CEF strategy: ${stock.cef_strategy_tag} — derived from the fund's mandate (Municipal = tax-exempt muni bonds, Credit = taxable bonds/loans, Equity Income = dividend stocks / covered calls).`}
                            data-testid="modal-cef-strategy-tag"
                          >
                            {stock.cef_strategy_tag}
                          </span>
                        )}
                      </span>
                      <BadgeHelpIcon badgeKey="cef" />
                      <LeveragedCefIcon
                        ticker={stock.ticker}
                        isCef={!!stock.is_cef}
                        size="md"
                        testIdPrefix="modal-lev-cef-badge"
                        withHelp
                      />
                    </span>
                  )}
                  {/* Sep 2026 — Money Market Fund type badge (Government /
                      Treasury / Prime / Municipal) + Badge Key opener, so
                      the type is visible outside the Screener. */}
                  {stock.is_money_market_fund && (
                    <span className="inline-flex items-center gap-1">
                      <span
                        className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide border ${
                          stock.mmf_type === 'Treasury'
                            ? 'bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300 border-sky-300/60 dark:border-sky-700/60'
                            : stock.mmf_type === 'Government'
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-300/60 dark:border-emerald-700/60'
                            : stock.mmf_type === 'Municipal'
                            ? 'bg-violet-100 text-violet-800 dark:bg-violet-950/60 dark:text-violet-300 border-violet-300/60 dark:border-violet-700/60'
                            : 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-300/60 dark:border-amber-700/60'
                        }`}
                        title={`${stock.mmf_type || 'Prime'} money market fund — tap the ? for what that means for yield, credit risk and taxes.`}
                        data-testid={`modal-mmf-type-badge-${stock.ticker}`}
                      >
                        {stock.mmf_type || 'MMF'}
                      </span>
                      <BadgeHelpIcon badgeKey={`mmf_${String(stock.mmf_type || 'prime').toLowerCase()}`} />
                    </span>
                  )}
                  {stock.is_reit && (
                    <span className="inline-flex items-center gap-1">
                      <span
                        className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300/60 dark:border-emerald-700/60 cursor-help"
                        title="Real Estate Investment Trust — company that owns, operates, or finances income-producing real estate. Required by law to distribute 90%+ of taxable income, so yields of 4-8% are typical."
                        data-testid="modal-reit-badge"
                      >
                        REIT
                      </span>
                      <BadgeHelpIcon badgeKey="reit" />
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground break-words leading-snug" data-testid="modal-stock-name">
                  {stock.name}
                  {' '}
                  <RecentlyListedBadge listDate={stock.list_date || stock.massive_list_date} />
                </p>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <span
                    className={`text-base font-bold font-mono ${livePrice !== undefined ? 'transition-colors duration-300' : ''}`}
                    data-testid="modal-stock-price"
                    title={livePrice !== undefined ? 'Live last trade — Massive WebSocket' : undefined}
                  >
                    ${(displayPrice ?? 0).toFixed(2)}
                  </span>
                  <LivePriceIndicator status={modalStreamStatus} />
                  {stock.change !== undefined && (
                    <span className={`text-xs font-mono ${stock.change >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {stock.change >= 0 ? '+' : ''}{stock.change?.toFixed(2)} ({stock.change_pct >= 0 ? '+' : ''}{stock.change_pct?.toFixed(2)}%)
                    </span>
                  )}
                  {/* Round 84k Phase 2 — replaced the "via Yahoo / Massive"
                      debug pill (which was internal-only) with a community
                      sentiment-tag summary so users see crowd consensus
                      at-a-glance. Clicking the chip takes them to the
                      ticker-filtered community feed to read or post. */}
                  {COMMUNITY_ENABLED && (
                    <CommunityTagsSummaryChip
                      ticker={stock.ticker}
                      onClick={() => navigate(`/community?tab=feed&ticker=${encodeURIComponent(stock.ticker)}`)}
                    />
                  )}
                  {/* Right-aligned link group — Round 95br (Jun 2026):
                      Reordered to Snapshot · Compare · Calculator · Glossary
                      so the most-clicked entries (Snapshot/Compare) sit
                      leftmost and the Glossary stays rightmost as the
                      "always-on-reference" affordance. Calculator opens
                      the existing global Basic Calculator via a window
                      event so it shares the same draggable instance as
                      the toolbox + Capital Tracker launches. */}
                  <div className="ml-auto inline-flex items-center gap-3">
                    <Link
                      to={`/stock/${encodeURIComponent(stock.ticker)}`}
                      onClick={(e) => {
                        // Round 91r — open Snapshot in the SAME tab and
                        // encode `modalOrigin` so BackOrHomeButton on the
                        // Snapshot page jumps back to this exact page +
                        // reopens THIS modal at this ticker.
                        e.preventDefault();
                        navigateWithOrigin(`/stock/${encodeURIComponent(stock.ticker)}`);
                        if (typeof onClose === 'function') onClose();
                      }}
                      className="inline-flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-300 hover:underline underline-offset-2"
                      title={`Open the public ${stock.ticker} Snapshot page — Back returns here`}
                      data-testid="modal-snapshot-link"
                    >
                      <Camera className="w-3.5 h-3.5" />
                      Snapshot
                    </Link>
                    {/* Round 92o++++++++L — Compare moved here from
                        the top-right button row so it sits next to
                        Glossary + Snapshot on every viewport (incl.
                        phones where it used to be icon-only at the
                        top corner). Same handler logic — including
                        the "Back to Compare" priority swap when the
                        user arrived from the Compare modal. */}
                    {onBackToCompare ? (
                      <button
                        type="button"
                        onClick={onBackToCompare}
                        data-testid="modal-back-to-compare-link"
                        className="inline-flex items-center gap-1 text-[11px] text-fuchsia-700 dark:text-fuchsia-300 hover:underline underline-offset-2"
                        title="Return to Side-by-Side Comparison with both tickers preserved"
                      >
                        <ArrowLeft className="w-3.5 h-3.5" />
                        Back to Compare
                      </button>
                    ) : (
                      onCompareClick && stock?.ticker && (
                        <button
                          type="button"
                          onClick={() => !isPreviewOnly && onCompareClick(stock.ticker)}
                          disabled={isPreviewOnly}
                          data-testid="modal-compare-link"
                          className="inline-flex items-center gap-1 text-[11px] text-fuchsia-700 dark:text-fuchsia-300 hover:underline underline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
                          title={isPreviewOnly
                            ? "Non-dividend stock — Compare requires both tickers to be in DiviDrip's dividend database."
                            : `Compare ${stock.ticker} with another stock`}
                        >
                          <GitCompare className="w-3.5 h-3.5" />
                          Compare
                        </button>
                      )
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        // Opens the SAME draggable Basic Calculator
                        // instance already wired up to the Toolbox and
                        // the Capital Tracker card. Window-event pattern
                        // mirrors `open-all-transactions-modal`, so the
                        // launcher doesn't need a prop drilled through
                        // every parent.
                        window.dispatchEvent(new Event('open-basic-calculator'));
                      }}
                      className="inline-flex items-center gap-1 text-[11px] text-emerald-700 dark:text-emerald-300 hover:underline underline-offset-2"
                      title="Open the Basic Calculator (draggable, share-anywhere window)"
                      data-testid="modal-calculator-link"
                    >
                      <Calculator className="w-3.5 h-3.5" />
                      Calculator
                    </button>
                    <Link
                      to="/glossary"
                      target="divdrip-glossary"
                      className="inline-flex items-center gap-1 text-[11px] text-sky-700 dark:text-sky-300 hover:underline underline-offset-2"
                      title="Open the Glossary of Terms in a shared tab"
                      data-testid="modal-glossary-link"
                    >
                      <BookOpen className="w-3.5 h-3.5" />
                      Glossary
                    </Link>
                    {modalResize.enabled && modalResize.size && (
                      <button
                        type="button"
                        onClick={modalResize.reset}
                        className="hidden sm:inline-flex items-center gap-1 text-[11px] font-semibold text-violet-800 dark:text-violet-300 hover:underline underline-offset-2"
                        title="Put the stock modal back to its original size"
                        data-testid="modal-reset-size-link"
                      >
                        <Maximize2 className="w-3.5 h-3.5 -scale-x-100" />
                        Reset Size
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {tickerHistory.length === 1 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBackTicker}
                    data-testid="modal-ticker-back-button"
                    className="gap-1 hidden sm:inline-flex"
                    title={`Back to ${tickerHistory[tickerHistory.length - 1]?.ticker || ''}`}
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back
                  </Button>
                )}
                {/* Round 105b — breadcrumb trail once 2+ levels deep.
                    Right-facing chevrons read as the path traveled
                    (KO → PEP → MNST); clicking a crumb jumps straight
                    back to that ticker at its saved tab + scroll. */}
                {tickerHistory.length >= 2 && (
                  <div
                    className="hidden sm:flex items-center gap-1 max-w-[300px] overflow-x-auto rounded-md border px-2 py-1.5 bg-card"
                    data-testid="modal-breadcrumb-trail"
                  >
                    {tickerHistory.map((h, i) => (
                      <span key={`${h.ticker}-${i}`} className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => handleJumpToCrumb(i)}
                          data-testid={`modal-breadcrumb-${h.ticker}`}
                          className="text-xs font-semibold text-primary hover:underline underline-offset-2"
                          title={`Back to ${h.ticker}`}
                        >
                          {h.ticker}
                        </button>
                        <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />
                      </span>
                    ))}
                    <span className="text-xs font-bold text-foreground shrink-0" data-testid="modal-breadcrumb-current">
                      {stock?.ticker}
                    </span>
                  </div>
                )}
                {/* Round 105h — phone layout: Watch + ★ live in the SAME
                    row as the X (anchored, never pushed); the breadcrumb
                    trail renders BELOW them and WRAPS downward within a
                    fixed width as it grows. */}
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => !isPreviewOnly && onToggleWatchlist(stock.ticker)}
                      disabled={isPreviewOnly}
                      data-testid="watch-button"
                      className="gap-1.5"
                      title={isPreviewOnly
                        ? "Non-dividend stock — can't be watched. Browse only."
                        : (isInWatchlist ? `Remove ${stock.ticker} from watchlist` : `Add ${stock.ticker} to watchlist`)}
                    >
                      <Star className={`w-4 h-4 ${!isPreviewOnly && isInWatchlist ? 'fill-primary text-primary' : ''}`} />
                      {isInWatchlist ? 'Watching' : 'Watch'}
                    </Button>
                    <ModalCloseButton onClick={onClose} testId="close-modal-button" />
                  </div>
                  {tickerHistory.length === 1 && (
                    <button
                      type="button"
                      onClick={handleBackTicker}
                      data-testid="modal-ticker-back-button-mobile"
                      className="sm:hidden inline-flex items-center gap-0.5 text-[11px] font-semibold text-primary hover:underline underline-offset-2"
                      title={`Back to ${tickerHistory[tickerHistory.length - 1]?.ticker || ''}`}
                    >
                      <ArrowLeft className="w-3.5 h-3.5" />
                      Back
                    </button>
                  )}
                  {tickerHistory.length >= 2 && (
                    <div
                      className="sm:hidden flex flex-wrap items-center justify-end gap-y-0.5 gap-x-0.5 w-[96px]"
                      data-testid="modal-breadcrumb-trail-mobile"
                    >
                      {tickerHistory.map((h, i) => (
                        <span key={`${h.ticker}-${i}`} className="flex items-center gap-0.5 shrink-0">
                          <button
                            type="button"
                            onClick={() => handleJumpToCrumb(i)}
                            data-testid={`modal-breadcrumb-mobile-${h.ticker}`}
                            className="text-[10px] font-semibold text-primary hover:underline underline-offset-2"
                            title={`Back to ${h.ticker}`}
                          >
                            {h.ticker}
                          </button>
                          <ChevronRight className="w-2.5 h-2.5 text-muted-foreground shrink-0" />
                        </span>
                      ))}
                      <span className="text-[10px] font-bold text-foreground shrink-0">{stock?.ticker}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Tabs — mobile swipe-to-navigate attached on the outer wrapper.
                Desktop (>= 640px) ignores the touch handlers (see onTabTouchEnd). */}
            <div
              className="flex-1 min-h-0 flex flex-col relative"
              onTouchStart={onTabTouchStart}
              onTouchMove={onTabTouchMove}
              onTouchEnd={onTabTouchEnd}
            >
              {/* First-visit swipe affordance — touch-friendly viewports
                  only (< 1024px = phones + tablets). Desktop hides it. */}
              {showSwipeHint && (
                <div
                  className="pointer-events-none absolute inset-0 z-10 flex items-center justify-between px-3 lg:hidden animate-swipe-hint"
                  data-testid="modal-swipe-hint"
                  aria-hidden="true"
                >
                  <div className="flex flex-col items-center gap-1 bg-background/70 backdrop-blur-sm rounded-full px-2 py-3 border border-border/40 shadow-sm">
                    <ChevronLeft className="w-4 h-4 text-muted-foreground" />
                    <span className="text-[9px] font-medium uppercase tracking-wider text-muted-foreground">swipe</span>
                  </div>
                  <div className="flex flex-col items-center gap-1 bg-background/70 backdrop-blur-sm rounded-full px-2 py-3 border border-border/40 shadow-sm">
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    <span className="text-[9px] font-medium uppercase tracking-wider text-muted-foreground">swipe</span>
                  </div>
                </div>
              )}
              {/* Headline insight ribbon — one-sentence narrative that
                  surfaces the most actionable "why" for this ticker
                  (trap > stretched payout > strong streak > clean).
                  Sits above the tax-lot ribbon so users see the most
                  important signal first. "More" pill jumps to the
                  Stock Metrics tab where the full narrative lives. */}
              <HeadlineInsight
                stock={stock}
                onSeeMore={() => setActiveTab('stock-metrics')}
                onJumpToCashLens={() => {
                  setActiveTab('stock-metrics');
                  // Wait one tick so the tab actually mounts before scrolling
                  setTimeout(() => {
                    const el = document.getElementById('reit-cash-lens-anchor');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 120);
                }}
                section19aUrl={resolveIssuerDistributionsUrl(stock)}
              />
              {/* Tax-lot indicator ribbon (Round 71c). Only shows if
                  the user actually has positions in this ticker AND
                  there's something tax-relevant to surface (a harvest
                  opportunity OR a wash-sale risk). Click → /tax-lots
                  for the full breakdown. Stays compact (single 28-30px
                  row) so it doesn't dominate the modal header. */}
              {taxLotInfo && (taxLotInfo.harvestLots > 0 || taxLotInfo.washLots > 0) && (
                <button
                  type="button"
                  onClick={() => navigateWithOrigin('/tax-lots')}
                  className={`w-full flex items-center justify-between gap-2 px-3 sm:px-4 py-1.5 border-b text-[11px] font-semibold transition-colors group text-left ${
                    taxLotInfo.washLots > 0
                      ? 'bg-rose-50/70 dark:bg-rose-950/30 border-rose-200/60 dark:border-rose-800/40 text-rose-800 dark:text-rose-200 hover:bg-rose-100/80 dark:hover:bg-rose-950/50'
                      : 'bg-emerald-50/70 dark:bg-emerald-950/30 border-emerald-200/60 dark:border-emerald-800/40 text-emerald-800 dark:text-emerald-200 hover:bg-emerald-100/80 dark:hover:bg-emerald-950/50'
                  }`}
                  data-testid="stock-modal-tax-indicator"
                  title={
                    taxLotInfo.washLots > 0
                      ? `You sold ${stock.ticker} within the past 30 days. Selling additional loss lots could trigger a wash-sale disallowance.`
                      : `You have ${taxLotInfo.harvestLots} loss lot${taxLotInfo.harvestLots === 1 ? '' : 's'} of ${stock.ticker} eligible for tax-loss harvest. Click for the full breakdown.`
                  }
                >
                  <span className="flex items-center gap-1.5 min-w-0 truncate">
                    {taxLotInfo.washLots > 0 ? (
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    ) : (
                      <Receipt className="w-3.5 h-3.5 shrink-0" />
                    )}
                    {taxLotInfo.washLots > 0 ? (
                      <span className="truncate">
                        Wash-sale risk on {stock.ticker} — recent sell within 30 days
                      </span>
                    ) : (
                      <span className="truncate">
                        <span className="font-mono font-bold">{taxLotInfo.harvestLots}</span> lot{taxLotInfo.harvestLots === 1 ? '' : 's'} of {stock.ticker} eligible to harvest
                        {taxLotInfo.harvestable > 0 && (
                          <>
                            {' · '}
                            <span className="font-mono font-bold">${taxLotInfo.harvestable.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </>
                        )}
                      </span>
                    )}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider opacity-70 group-hover:opacity-100 shrink-0">
                    Open Tax Lots →
                  </span>
                </button>
              )}
              {COMMUNITY_ENABLED && stock?.ticker ? (
                <div className="px-3 sm:px-4 pt-2">
                  <StockModalCommunitySurfacer
                    ticker={stock.ticker}
                    onNavigate={(target) => {
                      if (typeof navigateWithOrigin === 'function') navigateWithOrigin(target);
                      else window.location.href = target;
                    }}
                  />
                </div>
              ) : null}
              {/* Round 86 (Feb 2026) — Massive Insights moved from
                  above-tabs panel into its own dedicated "Insights"
                  tab (see TabsContent value="insights" below). Frees up
                  modal real estate on phone view. */}
              {/* Round P141·5 (Feb 2026) — Removed the above-tabs
                  WarrantPanel. It duplicated the one already rendered
                  inside the Insights tab (below), pushing the tab
                  strip way down the modal on warrant tickers. The
                  Insights-tab copy is now the single source of truth. */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 min-h-0 flex flex-col">
                {/* Round 91x — Phone-only "Swipe for More" hint sits
                    between any above-tabs banners and the tab strip.
                    Hidden at sm: and up (desktop has all tabs visible
                    inline). Arrows show conditionally based on the
                    current tab's position in TAB_ORDER. */}
                {(() => {
                  const idx = TAB_ORDER.indexOf(activeTab);
                  const canLeft = idx > 0;
                  const canRight = idx >= 0 && idx < TAB_ORDER.length - 1;
                  return (
                    <div
                      className="sm:hidden flex items-center justify-center gap-2 py-1.5 px-4 select-none"
                      data-testid="swipe-for-more-hint"
                      aria-hidden="true"
                    >
                      <span
                        className={`text-[10px] dark:text-[#b8b0e6] text-[#4a307d] ${canLeft ? 'opacity-100' : 'opacity-0'}`}
                        data-testid="swipe-hint-arrow-left"
                      >
                        ◀
                      </span>
                      {/* Dark-theme gradient (light → deep) */}
                      <span
                        className="hidden dark:inline text-[11px] font-bold tracking-[0.2em] uppercase"
                        style={{
                          background: 'linear-gradient(180deg, #b8b0e6 0%, #8765c2 55%, #5a3d9c 100%)',
                          WebkitBackgroundClip: 'text',
                          backgroundClip: 'text',
                          color: 'transparent',
                        }}
                      >
                        Swipe for More
                      </span>
                      {/* Light-theme gradient (deeper palette for white-bg contrast) */}
                      <span
                        className="dark:hidden inline text-[11px] font-bold tracking-[0.2em] uppercase"
                        style={{
                          background: 'linear-gradient(180deg, #6d4c9f 0%, #4a307d 55%, #2d1d5e 100%)',
                          WebkitBackgroundClip: 'text',
                          backgroundClip: 'text',
                          color: 'transparent',
                        }}
                      >
                        Swipe for More
                      </span>
                      <span
                        className={`text-[10px] dark:text-[#6d4c9f] text-[#2d1d5e] ${canRight ? 'opacity-100' : 'opacity-0'}`}
                        data-testid="swipe-hint-arrow-right"
                      >
                        ▶
                      </span>
                    </div>
                  );
                })()}
                <TabsScrollSlider targetRef={tabsListRef} />
                <TabsList ref={tabsListRef} className="w-full justify-start rounded-none bg-transparent border-b px-4 h-auto hide-scrollbar overflow-x-auto shrink-0 scroll-smooth">
                  {!isPreviewOnly && isDividendStock && (
                    <TabsTrigger value="dividend-info" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-sm" data-testid="tab-dividend-info">
                      Dividend Info
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="stock-metrics" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-sm" data-testid="tab-stock-metrics">
                    Stock Metrics
                  </TabsTrigger>
                  {hasFinancialHealth && (
                    <TabsTrigger value="financial-health" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-sm" data-testid="tab-financial-health">
                      Financial Health
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="insights" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-sm" data-testid="tab-insights">
                    Insights
                  </TabsTrigger>
                  {intradayEnabled && (
                    <TabsTrigger value="intraday" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-sm" data-testid="tab-intraday">
                      Today
                    </TabsTrigger>
                  )}
                  {!isPreviewOnly && isDividendStock && (
                    <TabsTrigger value="div-growth" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-sm" data-testid="tab-div-growth">
                      Div Growth
                    </TabsTrigger>
                  )}
                  {!isPreviewOnly && !isDividendStock && hasNonDivGrowth && (
                    <TabsTrigger value="non-div-growth" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-sm" data-testid="tab-non-div-growth">
                      Growth
                    </TabsTrigger>
                  )}
                  {!isPreviewOnly && isDividendStock && !stock?.is_money_market_fund && (
                    <TabsTrigger value="triangle" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-sm" data-testid="tab-triangle">
                      <TriangleIcon
                        className={`w-3.5 h-3.5 mr-1 inline-block -mt-0.5 ${
                          // Round P133 (Feb 2026) — extended from 3 → 5
                          // verdict tiers to match the DiviDrip Opinion
                          // redesign. Palette mirrors the banner: green
                          // shades for positive, gray for neutral,
                          // amber for soft-negative caution, rose for
                          // hard-negative would-not-buy.
                          //
                          // Round P133b (Feb 2026) — dropped
                          // `transition-colors` here. The 150 ms CSS
                          // interpolation between an unset default
                          // color and rose-red briefly painted a
                          // muddy amber/olive midpoint that looked
                          // like the "caution" state, confusing users
                          // who saw the icon "flash yellow" before
                          // settling on red. Snap the color instead.
                          triangleVerdict === 'buy'
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : triangleVerdict === 'consider'
                              ? 'text-lime-600 dark:text-lime-400'
                              : triangleVerdict === 'caution'
                                ? 'text-yellow-500 dark:text-yellow-400'
                                : triangleVerdict === 'avoid'
                                  ? 'text-rose-600 dark:text-rose-400'
                                  : ''
                        }`}
                        data-testid={`tab-triangle-icon-${triangleVerdict || 'unset'}`}
                      /> Triangle
                    </TabsTrigger>
                  )}
                  {!isPreviewOnly && !isDividendStock && hasCapitalAnalytics && (
                    <TabsTrigger value="capital-analytics" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-sm" data-testid="tab-capital-analytics">
                      <TriangleIcon className="w-3.5 h-3.5 mr-1 inline-block -mt-0.5" />
                      Capital Analytics
                    </TabsTrigger>
                  )}
                  {!isPreviewOnly && ownsThisStock && isDividendStock && (
                    <TabsTrigger value="my-position" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-sm" data-testid="tab-my-position">
                      My Position
                    </TabsTrigger>
                  )}
                  {!isPreviewOnly && isAdmin && (
                    <TabsTrigger value="tenk-8k-risks" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-sm" data-testid="tab-tenk-8k-risks">
                      10K / 8K / Risks
                    </TabsTrigger>
                  )}
                  {!isPreviewOnly && isAdmin && (
                    <TabsTrigger value="insider-activity" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-sm" data-testid="tab-insider-activity">
                      Insider &amp; Institutional
                    </TabsTrigger>
                  )}
                  {!isPreviewOnly && (
                    <TabsTrigger value="my-portfolio" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-sm" data-testid="tab-my-portfolio">
                      My Portfolio
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="journal" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-sm" data-testid="tab-journal">
                    Journal
                  </TabsTrigger>
                  {!isPreviewOnly && isDividendStock && (
                    <TabsTrigger value="yield-history" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-sm" data-testid="tab-yield-history">
                      Yield History
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="news" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-sm" data-testid="tab-news">
                    News
                  </TabsTrigger>
                  {!isPreviewOnly && isDividendStock && (
                    <TabsTrigger value="drip-calc" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-sm" data-testid="tab-drip-calc">
                      DRIP Calc
                    </TabsTrigger>
                  )}
                </TabsList>

                {/* Round 92o++++++++N++++++++++ (Feb 2026) — phone
                    bottom-padding. The Emergent dev badge on preview
                    plus the iOS Safari bottom toolbar both clip the
                    last ~80px of any scrollable region. Adding `pb-24`
                    on phone (`sm:pb-0` on tablet+) ensures the last
                    InfoRow / chart axis label / DRIP toggle on any
                    tab — `My Position` being the worst offender —
                    can scroll above those overlays. */}
                <div ref={scrollableRef} className="flex-1 min-h-0 overflow-y-auto pb-24 sm:pb-0">
                {isResizing ? (
                  <div className="h-full min-h-[240px] flex items-center justify-center" data-testid="stock-modal-resizing">
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 mx-auto animate-spin text-muted-foreground" />
                      <p className="mt-3 text-sm font-semibold">Please wait…</p>
                      <p className="text-xs text-muted-foreground">Charts redraw when you stop resizing.</p>
                    </div>
                  </div>
                ) : (
                <>
                {stock?.special_only && <div className="px-3 sm:px-4 pt-3"><SpecialPayoutCard stock={stock} /></div>}
                <TabsContent value="dividend-info" className="p-3 sm:p-4 mt-0">
                  <div className="space-y-3">
                    {Number(stock?.special_payer_count) >= 3 && (
                      <div className="flex justify-end" data-testid="special-payer-row"><SpecialPayerTag stock={stock} /></div>
                    )}
                    {/* Round P112d (Jul 31, 2026) — Thin-history caveat.
                        When Massive has ≤1 payment observation, the
                        backend flags the row `frequency_inferred_thin`
                        and defaults frequency to Annually. This banner
                        warns the user that the annualized cadence is
                        unconfirmed BEFORE they act on the projected
                        income — critical because portfolio math,
                        watchlist yield, screener sorts, and the DRIP
                        calculator all trust `frequency` × `dividend_rate`
                        as if it were gospel. */}
                    {stock.frequency_declared_thin && !stock.frequency_inferred_thin && (
                      <div
                        className="flex items-start gap-2 px-3 py-2 rounded-md border border-sky-500/50 bg-sky-50 dark:bg-sky-950/40 text-sky-900 dark:text-sky-100 text-xs"
                        data-testid="dividend-info-declared-cadence-badge"
                      >
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <div className="font-bold">Single payment observed — cadence taken from the issuer&apos;s declared schedule ({stock.frequency}).</div>
                          <p className="mt-0.5 leading-snug">
                            Only one distribution has posted so far. The issuer files this as a {String(stock.frequency || '').toLowerCase()} payer, so the annual figure below is that payment × {({ 'Semi-Annually': 2, Quarterly: 4, Monthly: 12, Weekly: 52 })[stock.frequency] || 1}. Payout size can vary until a second payment confirms the run-rate — treat projected income as provisional.
                          </p>
                        </div>
                      </div>
                    )}
                    {stock.frequency_inferred_thin && (
                      <div
                        className="flex items-start gap-2 px-3 py-2 rounded-md border border-amber-500/50 bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-100 text-xs"
                        data-testid="dividend-info-thin-history-badge"
                      >
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-bold">Single payment observed — annualized cadence unconfirmed.</div>
                            {/* Round P112f (Jul 31, 2026) — Ownership
                                chip. If the current user holds this
                                ticker, surface the personal impact so
                                the warning can't be dismissed as
                                "someone else's problem." */}
                            {taxLotInfo && taxLotInfo.totalShares > 0 && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-600 text-white text-[10px] font-bold uppercase tracking-wide"
                                data-testid="dividend-info-thin-history-ownership-chip"
                                title="You currently own this ticker — the warning below applies to your position."
                              >
                                <Wallet className="w-3 h-3" />
                                You own {taxLotInfo.totalShares.toLocaleString(undefined, { maximumFractionDigits: 4 })} sh
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-amber-800/90 dark:text-amber-200/90 mt-0.5">
                            Only one dividend has been recorded for this ticker. The frequency shown below is a conservative default (Annually) and the annual figure equals the observed payment. Actual cadence will be confirmed once a second payment posts. Treat any projected income for this holding as provisional.
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Payment Cards — Massive-driven Last / Next payment (shares its pull with Recent Events) */}
                    <DividendScheduleCards ticker={stock.ticker} />

                    {/* Dividend policy momentum — shows when forward
                        rate has diverged from trailing-12-month rate.
                        Rendered only when the Massive financials fields
                        are present AND the delta is material (>=2%
                        either direction). Hike = bullish, cut = red
                        flag (often precedes a formal cut announcement). */}
                    {(() => {
                      const trailing = Number(stock.trailing_annual_dividend_rate_usd);
                      const delta = Number(stock.dividend_policy_delta);
                      if (!Number.isFinite(trailing) || trailing <= 0) return null;
                      if (!Number.isFinite(delta)) return null;
                      const ratio = delta / trailing;
                      if (Math.abs(ratio) < 0.02) return null; // not material
                      const isHike = ratio > 0;
                      const tone = isHike
                        ? 'border-emerald-300/60 bg-emerald-50/70 dark:bg-emerald-950/20 dark:border-emerald-700/60 text-emerald-800 dark:text-emerald-300'
                        : 'border-rose-300/60 bg-rose-50/70 dark:bg-rose-950/20 dark:border-rose-700/60 text-rose-800 dark:text-rose-300';
                      const forward = Number(stock.dividend_rate);
                      return (
                        <div
                          className={`rounded-lg border px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs ${tone}`}
                          data-testid="dividend-policy-banner"
                        >
                          <span className="font-bold uppercase tracking-wider text-[10px]">
                            {isHike ? 'Recent Hike' : 'Recent Cut'}
                          </span>
                          <span className="font-mono font-bold">
                            {isHike ? '+' : ''}{(ratio * 100).toFixed(1)}%
                          </span>
                          <span className="opacity-70">
                            Forward ${Number.isFinite(forward) ? forward.toFixed(4) : '--'} vs Trailing ${trailing.toFixed(4)}
                          </span>
                          <span className="ml-auto text-[10px] opacity-70">
                            {isHike
                              ? 'Forward rate above trailing — bullish dividend momentum.'
                              : 'Forward rate below trailing — watch for a formal cut.'}
                          </span>
                        </div>
                      );
                    })()}

                    {/* Metric Summary */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {isDividendStock ? (
                        <MetricCard
                          label="Annual Dividend"
                          value={stock.dividend_rate ? `$${stock.dividend_rate.toFixed(2)}` : '--'}
                          subtitle="per share"
                        />
                      ) : (
                        <MetricCard
                          label="Revenue (TTM)"
                          value={stock.massive_revenue_ttm
                            ? (stock.massive_revenue_ttm >= 1e9
                                ? `$${(stock.massive_revenue_ttm / 1e9).toFixed(2)}B`
                                : `$${(stock.massive_revenue_ttm / 1e6).toFixed(1)}M`)
                            : '--'}
                          subtitle="trailing twelve months"
                        />
                      )}
                      {isDividendStock ? (
                        <CurrentYieldCard stock={stock} />
                      ) : (
                        <MetricCard
                          label="1Y Return"
                          value={stock.perf_1y != null ? `${stock.perf_1y >= 0 ? '+' : ''}${stock.perf_1y.toFixed(1)}%` : '--'}
                          subtitle="price change"
                        />
                      )}
                      {isDividendStock && (
                      <MetricCard
                        label="Payout Ratio"
                        value={stock.is_treasury_vehicle
                          ? <>N/A — <AbbrTip term="ROC">ROC</AbbrTip></>
                          : (stock.payout_ratio ? `${stock.payout_ratio.toFixed(1)}%` : '--')}
                        subtitle={
                          stock.is_treasury_vehicle ? 'funded by capital raises'
                          : stock.is_reit ? 'of EPS · see FFO Cash Lens'
                          : stock.is_mlp ? 'of EPS · see DCF Cash Lens'
                          : 'of earnings'
                        }
                      />
                      )}
                      {!isDividendStock && (
                        <MetricCard
                          label="Quality Tier"
                          value={stock.non_dividend_quality_tier
                            ? stock.non_dividend_quality_tier.replace('GROWTH-', 'G-')
                            : '--'}
                          subtitle={stock.non_dividend_quality_score != null
                            ? `score ${stock.non_dividend_quality_score}/100`
                            : 'not scored'}
                        />
                      )}
                      {/* Round 96d (Feb 2026) — Income Reliability is a
                          dividend-specific safety score (payout ratio,
                          yield extremity, structure flags, tier). It
                          has no meaning for non-dividend names like
                          IQST/AIIO — hide it there. */}
                      {isDividendStock && (() => {
                        const risk = computeRisk(stock);
                        if (!risk) return (
                          <MetricCard label="Income Reliability" value="--" subtitle="needs more data" />
                        );
                        const cls = riskColorClasses(risk.category);
                        return (
                          <div
                            className="rounded-lg border bg-card p-3 text-center cursor-help"
                            data-testid="metric-card-risk"
                            title={riskTooltip(risk)}
                          >
                            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-1">
                              Income Reliability
                            </p>
                            <div className="flex items-center justify-center gap-1.5">
                              <span className={`w-2.5 h-2.5 rounded-full ${cls.dot}`} />
                              <p className={`text-xl font-bold font-mono ${cls.text}`}>
                                {risk.score}<span className="text-base text-muted-foreground">/10</span>
                              </p>
                            </div>
                            <p className={`text-[10px] mt-0.5 font-semibold ${cls.text}`}>
                              {riskLabel(risk.category)}
                            </p>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Streak Risk Chart — annual dividend history with
                        special-dividend distortion detection. Lives
                        on every Dividend Info tab so the "Special
                        Dividends & CAGR Math" Learn Guide can point
                        users here for any ticker (COST's 2024 $15
                        special auto-fires the cyan distortion callout).
                        Pure UI on the already-cached
                        `/api/stocks/{ticker}/dividend-growth` payload —
                        no extra Massive or Yahoo calls. */}
                    {stock?.ticker && (
                      <div
                        className="rounded-lg border bg-card px-4 py-3"
                        data-testid="dividend-info-streak-risk-chart"
                      >
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <AlertTriangle className="w-3.5 h-3.5 text-yellow-500 shrink-0" />
                          <h4 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                            Streak Risk Chart
                          </h4>
                          <span className="text-[10px] text-muted-foreground/80">
                            Annual dividends · catches special-dividend distortion automatically
                          </span>
                          {/* Round 96n (Jul 3 2026) — swapped from a
                              plain <Link> to a button calling
                              navigateWithOrigin so the browser
                              back-button returns here with the modal
                              auto-reopened via Dashboard's ?stock=
                              deep-link handler. Matches the pattern
                              used by every other in-modal cross-link
                              (Triangle "Open full view", Squeeze
                              "How squeezes work", etc.). */}
                          <button
                            type="button"
                            onClick={() => navigateWithOrigin('/learn/special-dividends-in-cagr-math')}
                            className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold text-primary hover:underline cursor-pointer"
                            data-testid="streak-risk-chart-why-link"
                            title="Open the Learn guide on special-dividend CAGR math. Back button returns here with this stock modal reopened."
                          >
                            <BookOpen className="w-3 h-3" />
                            Why this matters
                          </button>
                        </div>
                        <StreakRiskChart ticker={stock.ticker} compact />
                        {/* Round P119c (Feb 2026) — Admin quick-confirm.
                            One click fires the Massive check and, if
                            delisted, updates stock_cache.liquidated so
                            the definitive banner replaces the softer
                            "possible liquidation" warning site-wide. */}
                        {isAdmin && (
                          <div className="mt-2 flex justify-end">
                            <AdminCheckLiquidationButton ticker={stock.ticker} />
                          </div>
                        )}
                        {/* Round 95h (Feb 2026) — Home-currency streak card.
                            Auto-renders only for foreign ADRs where Massive
                            stores dividends in a non-USD currency (Nestlé,
                            Roche, Novartis, SAP, etc.). Reveals when a USD
                            "cut" is actually a currency-conversion artifact. */}
                        <HomeCurrencyDividendCard ticker={stock.ticker} />
                      </div>
                    )}

                    {/* Round 95c (Jun 2026) — Recent Events card moved
                        here from the Insights tab. Sits directly under
                        the Streak Risk Chart per user spec: the events
                        timeline (dividends paid, splits, ticker changes)
                        belongs with the dividend payment context, not
                        next to fund-overview / asset-class material. */}
                    {stock?.ticker && <RecentEventsCard ticker={stock.ticker} />}
                  </div>
                </TabsContent>

                {/* My Portfolio Tab */}
                <TabsContent value="my-position" className="p-3 sm:p-4 mt-0">
                  {/* Round 92n — Conditionally mounted live position
                      detail tab. Only rendered when ownsThisStock is
                      true (the tab trigger itself is hidden otherwise,
                      but this guard prevents the SSE subscription from
                      firing if someone deep-links to the tab without
                      a position). */}
                  {ownsThisStock && isDividendStock && stock?.ticker && (
                    <MyPositionTab ticker={stock.ticker} stock={stock} />
                  )}
                </TabsContent>

                {/* Round 92o++++++++N++++++++ — 10K / 8K / Risk
                    Factors tab. Premium content; admin-only as a
                    paywall proxy until billing ships. The
                    `highlightFiling` prop is consumed by the bell
                    deep-link flow to pulse a specific 8-K row when
                    navigated to from the FilingAlertsBell. */}
                <TabsContent value="tenk-8k-risks" className="p-3 sm:p-4 mt-0">
                  {isAdmin && stock?.ticker && (
                    <TenK8KRisksTab
                      ticker={stock.ticker}
                      highlightAccession={highlightFiling}
                    />
                  )}
                </TabsContent>

                {/* Round 92o++++++++N++++++++++ (Feb 2026) — Insider
                    Activity tab. Renders Form-4 trades + 13-F holders
                    using the cache hydrated by the `thirteen-f-bucket`
                    admin warmer. Admin-only paywall proxy. */}
                <TabsContent value="insider-activity" className="p-3 sm:p-4 mt-0">
                  {isAdmin && stock?.ticker && (
                    <InsiderActivityTab ticker={stock.ticker} user={user} />
                  )}
                </TabsContent>

                <TabsContent value="my-portfolio" className="p-3 sm:p-4 mt-0">
                  {user === false ? (
                    <div className="flex flex-col items-center text-center" data-testid="portfolio-login-prompt">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center gap-1 mb-3">
                        <Star className="w-5 h-5 text-primary" />
                        <Briefcase className="w-5 h-5 text-primary" />
                      </div>
                      <h3 className="text-base font-bold mb-1">Sign in to unlock all features</h3>
                      <p className="text-xs text-muted-foreground mb-3 max-w-[320px]">Create a free account to track positions and get full access to:</p>

                      <div className="w-full max-w-sm grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-left mb-4">
                        {[
                          { icons: [Star, Briefcase], text: 'Watchlist & Portfolio — holdings, gains, dividends' },
                          { icon: Upload, text: 'Robinhood CSV import — auto-build positions' },
                          { icon: Receipt, text: 'Tax-Lot Optimizer — harvestable losses' },
                          { icon: FileText, text: 'Year-End & Tax-Harvest reports' },
                          { icon: BookOpen, text: 'Investing Journal & weekly review' },
                          { icon: Target, text: 'Monthly Income Goal' },
                          { icon: Calculator, text: 'DRIP Calculator' },
                          { icon: BarChart3, text: 'Yield History & Time Machine' },
                          { icon: CalendarDays, text: 'Dividend Calendar' },
                          { icon: PieChart, text: 'Sector Rebalancer' },
                          { icon: Wallet, text: 'Cash Tracking & history' },
                          { icon: FileSpreadsheet, text: '9-tab Excel / Sheets download' },
                          { icon: Share2, text: 'Public Portfolio Share Page' },
                        ].map((f, i) => {
                          const icons = f.icons || [f.icon];
                          return (
                            <div key={i} className="flex items-center gap-2 text-xs">
                              <span className="inline-flex items-center gap-0.5 shrink-0">
                                {icons.map((Icon, j) => (
                                  <Icon key={j} className="w-3.5 h-3.5 text-primary" />
                                ))}
                              </span>
                              <span>{f.text}</span>
                            </div>
                          );
                        })}
                      </div>

                      <button
                        onClick={() => {
                          // Round 86c — Stash deep-link so LoginModal's
                          // Google flow returns the user to THIS same
                          // stock modal on the My Portfolio tab. Email/
                          // password login stays in-place so no extra
                          // wiring needed there.
                          try {
                            const t = String(stock?.ticker || '').toUpperCase();
                            if (t) sessionStorage.setItem('post_login_redirect', `/?stock=${encodeURIComponent(t)}&tab=my-portfolio`);
                          } catch (e) { /* ignore */ }
                          onLoginRequired && onLoginRequired();
                        }}
                        className="px-5 py-2.5 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:bg-primary/90 transition-colors"
                        data-testid="portfolio-tab-login-btn"
                      >
                        Sign Up / Log In — It's Free
                      </button>
                      <p className="text-[10px] text-muted-foreground/60 mt-3">Dividend Info is always free — no account needed.</p>
                    </div>
                  ) : (
                    <MyPortfolioTab
                      stock={stock}
                      onPositionsChanged={onPositionsChanged}
                      onViewPortfolio={onViewPortfolioWithRestore}
                      onOpenSnapshot={() => {
                        // Round 91r — same-tab Snapshot navigation with
                        // modal-restore on Back.
                        navigateWithOrigin(`/stock/${encodeURIComponent(stock.ticker)}`);
                        if (typeof onClose === 'function') onClose();
                      }}
                    />
                  )}
                </TabsContent>

                {/* Stock Metrics Tab */}
                <TabsContent value="stock-metrics" className="p-3 sm:p-4 mt-0">
                  <StockMetricsTab ticker={stock?.ticker} stock={stock} initialPeriod={initialChartPeriod} livePrice={livePrice} onOpenStock={navigateToTicker} intradayEnabled={intradayEnabled} onNavigateWithOrigin={navigateWithOrigin} />
                  {/* Round 95c (Jun 2026) — Technicals (SMA50/RSI14),
                      Short Interest+Float, Short Volume cards moved
                      here from the Insights tab. These are
                      speculative-sentiment metrics that belong with
                      the price/volume stock metrics, not next to
                      fund-overview material. */}
                  {stock?.ticker && (
                    <div className="mt-3">
                      <TechnicalsAndShortDataCards ticker={stock.ticker} navigateWithOrigin={navigateWithOrigin} />
                    </div>
                  )}
                </TabsContent>

                {/* Round 93n — Financial Health tab (Plan C Massive
                    financials: ratios + income + cash flow + balance
                    sheet). Open to every visitor. */}
                <TabsContent value="financial-health" className="p-3 sm:p-4 mt-0">
                  <FinancialHealthTab stock={stock} />
                </TabsContent>

                {/* Round 86 — Insights tab. Used to be a cramped panel
                    above the tabs; now its own roomy tab with each
                    Massive surface in its own card. */}
                <TabsContent value="insights" className="p-3 sm:p-4 mt-0">
                  {/* Round P141·5 (Feb 2026) — Warrant vs Underlying
                      chart + P/L Calculator now injected INTO the
                      MassiveInsightsTab via `afterTopSlot` so Fund
                      Overview (topSlot) sits ABOVE the warrant compare
                      chart. Silent when the ticker isn't a warrant OR
                      when the underlying has no shared history (< 7
                      days). */}
                  <MassiveInsightsTab
                    ticker={stock?.ticker}
                    stock={stock}
                    livePrice={displayPrice}
                    onOpenTicker={(t) => {
                      navigateToTicker(t);
                    }}
                    onNavigateWithOrigin={navigateWithOrigin}
                    afterTopSlot={warrant ? (
                      <div className="mb-4 space-y-4">
                        {warrant.is_unit ? (
                          <UnitPanel warrant={warrant} onOpenTicker={navigateToTicker} onNavigate={navigateWithOrigin} />
                        ) : (
                          <>
                            <WarrantVsUnderlyingChart ticker={stock?.ticker} days={90} />
                            <WarrantPanel
                              warrant={warrant}
                              currentPrice={stock?.price ?? stock?.current_price ?? null}
                              underlyingPrice={stock?.underlying_price ?? null}
                              userAvgCost={taxLotInfo?.avgCost ?? null}
                              userShares={taxLotInfo?.totalShares ?? null}
                              onOpenTicker={navigateToTicker}
                            />
                          </>
                        )}
                      </div>
                    ) : isSpacRight(stock) ? (
                      <div className="mb-4">
                        <RightPanel ticker={stock.ticker} name={rightName(stock)} cik={stock.cik} shareTicker={rightShareTicker(stock.ticker)} onOpenTicker={navigateToTicker} onNavigate={navigateWithOrigin} />
                      </div>
                    ) : null}
                    /* Round 95c (Jun 2026) — About / Fund Overview /
                       Tier / Leveraged / Futures / ADR / BDC / CEF /
                       REIT / Baby-Bond / Preferred-Share cards moved
                       here from the Dividend Info tab. Passed as a
                       prop so MassiveInsightsTab.jsx doesn't need to
                       know about every helper component in scope. */
                    topSlot={stock ? (
                    <>
                      {/* P158·9 (Feb 2026) — Money Market Fund overview
                          slots ABOVE the About/Fund Overview block so
                          users landing on FGXXX / SPAXX / VMFXX / etc.
                          immediately see "this is an MMF, targets $1.00
                          NAV" before any of the equity-oriented cards. */}
                      <MoneyMarketFundOverviewCard stock={stock} />
                      {/* Sep 2026 — CLO ETF card sits at the top of the
                          slot so it renders for the Reckoner wrappers
                          (RAAR/RAAY…) that have no profile_data and would
                          otherwise show nothing but the benchmark card. */}
                      {stock.is_clo && <CloExplainerCard stock={stock} onSelectStock={navigateToTicker} />}
                      {(() => {
                        // Round 95c v2 (Jun 2026) — fallback chain for the
                        // About / Fund Overview body. Massive's manual
                        // `business_summary` is curated for stocks but
                        // empty for most ETFs/CEFs. Fall back to the
                        // top-level Yahoo `description` (longBusinessSummary)
                        // and finally to `massive_description` so funds
                        // like JEPI/JEPQ/SCHD never render an empty About.
                        const aboutText = (
                          stock.profile_data?.business_summary
                          || stock.description
                          || stock.massive_description
                          || ''
                        ).trim();
                        if (!aboutText) return null;
                        return (
                        <div className="rounded-lg border bg-card px-4 py-3" data-testid="business-summary">
                          <div className="flex items-start justify-between gap-3 mb-2 flex-wrap sm:flex-nowrap">
                            <div className="flex items-start gap-2 min-w-0 flex-1">
                              {/* Round 92o++++++++k — bigger logo as a
                                  visual anchor for the About section.
                                  Self-unmounts on failure (no broken
                                  placeholder), so ETFs without a logo
                                  still look identical to today.
                                  Round 97a (Feb 2026) — bumped from
                                  36 → 40 to match the +2px headline. */}
                              <CompanyLogo ticker={stock.ticker} size={40} zoomable />
                              {/* Round 97a — Title color/size now
                                  mirrors the Valuation Matrix header
                                  (fuchsia, +2px) so the Insights tab
                                  feels visually consistent end-to-end
                                  instead of a muted grey at the top. */}
                              <h4 className="text-[12px] font-bold uppercase tracking-widest text-fuchsia-500 dark:text-fuchsia-400 pt-1.5">
                                {stock.profile_data?.quote_type === 'stock' ? 'About' : 'Fund Overview'}
                              </h4>
                            </div>
                            {/* Round 92o++++++++L — industry/HQ chips:
                                on phone (< sm) they wrap to a new line
                                UNDER the title via `basis-full` so long
                                industry strings ("SURGICAL & MEDICAL
                                INSTRUMENTS & APPARATUS") never collide
                                with the "Fund Overview" header. On
                                tablet+ they sit on the same row, top-
                                right of the section, exactly as today. */}
                            <div className="flex items-center gap-3 text-[10px] font-bold text-muted-foreground shrink-0 basis-full sm:basis-auto order-last sm:order-none flex-wrap">
                              {stock.profile_data?.industry && <span>{stock.profile_data.industry}</span>}
                              {stock.profile_data?.headquarters && <span>{stock.profile_data.headquarters}</span>}
                            </div>
                          </div>
                          <ExpandableAboutText
                            text={aboutText}
                            clamp={!stock.profile_data?.business_summary_bullets?.length}
                            resetKey={stock.ticker}
                          />
                          {/* Optional follow-up bullets — used by hand-written
                              summaries (e.g., BCAT) where the issuer's product
                              page calls out a key fact like "12-year limited
                              term" right under the main paragraph. */}
                          {stock.profile_data?.business_summary_bullets?.length > 0 && (
                            <ul className="mt-1.5 space-y-0.5 list-disc list-outside pl-4 text-xs font-bold text-muted-foreground leading-relaxed" data-testid="business-summary-bullets">
                              {stock.profile_data.business_summary_bullets.map((b, i) => (
                                <li key={i} className="break-words">{b}</li>
                              ))}
                            </ul>
                          )}
                          {stock.profile_data?.website && (
                            <a href={stock.profile_data.website} target="_blank" rel="noopener noreferrer" className="text-[10px] font-bold text-primary hover:underline mt-1.5 inline-block">
                              {stock.profile_data.website.replace(/^https?:\/\/(www\.)?/, '')}
                            </a>
                          )}
                          {/* Round 92o++++++++k — Massive ticker-overview
                              enrichment strip: Listed since · Employees ·
                              HQ · Industry · Website. Each chip hides
                              itself if Massive doesn't have that field;
                              whole strip vanishes for tickers Massive
                              doesn't know. */}
                          <CompanyDetailEnrichment ticker={stock.ticker} sector={stock.sector} industry={stock.industry || stock.profile_data?.industry} />
                          {/* Issuer attribution — surfaces a link to
                              the fund family's website when we can detect
                              it (YieldMax / Direxion / Roundhill / Bitwise
                              / ProShares / NestYield). */}
                          <IssuerAttribution
                            ticker={stock.ticker}
                            name={stock.name}
                            onNavigate={onClose}
                            onSelectStock={navigateToTicker}
                            watchlist={watchlist}
                            onToggleWatchlist={onToggleWatchlist}
                            user={user}
                            onLoginRequired={onLoginRequired}
                          />
                          {/* Dividend Tier explainer (after website link / about text) */}
                          <TierExplainerCard
                            tier={stock.dividend_tier}
                            streakYears={stock.dividend_streak_years}
                            onNavigate={onClose}
                            onNavigateWithOrigin={navigateWithOrigin}
                          />
                          {/* Leveraged / Futures / ADR / BDC / CEF risk explainers (at the bottom of the About area) */}
                          {stock.is_leveraged && <LeveragedExplainerCard />}
                          {stock.uses_futures && <FuturesExplainerCard />}
                          {stock.is_adr && <AdrExplainerCard ticker={stock.ticker} />}
                          {stock.is_bdc && <BdcExplainerCard />}
                          {stock.is_cef && <CefExplainerCard ticker={stock.ticker} />}
                          {stock.is_reit && <ReitExplainerCard />}
                        </div>
                        );
                      })()}

                      {/* Round 95c v2 — Tier-only fallback when there's no
                          business_summary AND no Yahoo description. Shows
                          the explainer cards / tier badge / issuer link so
                          the About slot isn't visually empty for tickers
                          like brand-new SPACs or thinly-covered preferreds.
                          Rendered as a SEPARATE sibling (no IIFE branch)
                          so we don't double-render when description is
                          present. */}
                      {!(stock.profile_data?.business_summary || stock.description || stock.massive_description) && (stock.dividend_tier || stock.is_leveraged || stock.uses_futures || stock.is_adr || stock.is_bdc || stock.is_cef || stock.is_reit || NESTYIELD_TICKERS.includes(stock.ticker?.toUpperCase()) || PER_TICKER_ISSUER[stock.ticker?.toUpperCase()] || (!SUPPRESS_AUTO_ISSUER.has(stock.ticker?.toUpperCase()) && ISSUER_FAMILIES.some((f) => f.match.test(stock.name || '')))) && (
                        <div className="rounded-lg border bg-card px-4 py-3" data-testid="business-summary-tier-only">
                          <h4 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">
                            About
                          </h4>
                          <IssuerAttribution
                            ticker={stock.ticker}
                            name={stock.name}
                            onNavigate={onClose}
                            onSelectStock={navigateToTicker}
                            watchlist={watchlist}
                            onToggleWatchlist={onToggleWatchlist}
                            user={user}
                            onLoginRequired={onLoginRequired}
                          />
                          <TierExplainerCard
                            tier={stock.dividend_tier}
                            streakYears={stock.dividend_streak_years}
                            onNavigate={onClose}
                            onNavigateWithOrigin={navigateWithOrigin}
                          />
                          {stock.is_leveraged && <LeveragedExplainerCard />}
                          {stock.uses_futures && <FuturesExplainerCard />}
                          {stock.is_adr && <AdrExplainerCard ticker={stock.ticker} />}
                          {stock.is_bdc && <BdcExplainerCard />}
                          {stock.is_cef && <CefExplainerCard ticker={stock.ticker} />}
                          {stock.is_reit && <ReitExplainerCard />}
                          <CompanyDetailEnrichment ticker={stock.ticker} sector={stock.sector} industry={stock.industry || stock.profile_data?.industry} />
                        </div>
                      )}

                      {/* Oct 2026 — shared Asset Class card (par, coupon,
                          cumulative, admin-confirmed call terms). Gated on
                          is_preferred too so PFD-typed rows that never got
                          an asset_class stamp (OXLCM) still get it. */}
                      {isFixedRateAsset(stock) && <FixedRateAssetCard stock={stock} className="mt-4" onNavigateWithOrigin={navigateWithOrigin} />}
                    </>
                  ) : null}
                  />
                </TabsContent>

                {/* Today Tab — Massive minute-bar aggregates (Phase B, hidden until paid plan) */}
                {intradayEnabled && (
                  <TabsContent value="intraday" className="mt-0">
                    <IntradayTab ticker={stock?.ticker} isLoggedOut={!user} onSignInClick={onLoginRequired} />
                  </TabsContent>
                )}

                {/* Div Growth Tab */}
                <TabsContent value="div-growth" className="p-3 sm:p-4 mt-0">
                  <DivGrowthTab ticker={stock?.ticker} isDark={isDark} />
                </TabsContent>

                {/* Round 96b (Feb 2026) — Non-Dividend Growth Tab.
                    Replaces Div Growth + Triangle + Yield History
                    for pure growth stocks. Shows annual Revenue +
                    EPS bar charts plus the Quality Score breakdown
                    so the user can see why this stock got its tier. */}
                <TabsContent value="non-div-growth" className="p-3 sm:p-4 mt-0">
                  <NonDivGrowthTab ticker={stock?.ticker} isDark={isDark} />
                </TabsContent>

                {/* Dividend Triangle Tab — 3-axis radar of Revenue / EPS / Dividend CAGR */}
                <TabsContent value="triangle" className="p-3 sm:p-4 mt-0 relative">
                  {COMMUNITY_ENABLED && stock?.ticker && <TriangleSentimentRibbon ticker={stock.ticker} onNavigateWithOrigin={navigateWithOrigin} />}
                  <TriangleScorecard
                    ticker={stock?.ticker}
                    isDark={isDark}
                    compact
                    onOpenStandalone={() => stock?.ticker && navigateWithOrigin(`/triangle/${stock.ticker}`)}
                    onDataLoaded={(d) => {
                      // Round P133d (Feb 2026) — Always let the
                      // TriangleScorecard fetch be authoritative for
                      // the icon color, so it MATCHES the banner the
                      // user is looking at. The P133b guard kept the
                      // pre-fetch's verdict even when the tab fetch
                      // returned something different — that produced
                      // a visible mismatch (icon gray, banner green)
                      // on tickers where the two calls didn't
                      // agree (ATEN was one). The mismatch is a
                      // worse UX than either fresh verdict being
                      // wrong, so let the tab's own data win.
                      setTriangleVerdict(d?.opinion?.verdict || null);
                    }}
                    onOpenExecutiveSummary={() => setExecSummaryOpen(true)}
                  />
                  {/* Round 95ah — full-tab takeover overlay. Rendered
                      INSIDE the TabsContent so the Close X returns the
                      user to the Triangle tab they were on. */}
                  <ExecutiveSummaryTab
                    ticker={stock?.ticker}
                    open={execSummaryOpen}
                    onClose={() => setExecSummaryOpen(false)}
                  />
                </TabsContent>

                {/* Capital Analytics Tab — non-dividend stocks only.
                    Mirrors Triangle but evaluates capital allocation
                    via the 4-factor Equity Quality Rank model. */}
                <TabsContent value="capital-analytics" className="mt-0">
                  <CapitalAnalyticsTab ticker={stock?.ticker} stockName={stock?.name} />
                </TabsContent>

                {/* Yield History Tab */}
                <TabsContent value="yield-history" className="p-3 sm:p-4 mt-0">
                  <YieldHistoryTab ticker={stock?.ticker} isDark={isDark} />
                </TabsContent>

                {/* Journal Tab — per-ticker entries from the Journal feature */}
                <TabsContent value="journal" className="p-3 sm:p-4 mt-0">
                  <StockJournalTab
                    ticker={stock?.ticker}
                    user={user}
                    onLoginRequired={onLoginRequired}
                    onViewAll={() => navigateWithOrigin('/journal')}
                  />
                </TabsContent>

                {/* News Tab */}
                <TabsContent value="news" className="p-3 sm:p-4 mt-0">
                  <NewsTab ticker={stock?.ticker} prefetchedData={prefetchedNews} currentPrice={stock?.price} />
                </TabsContent>

                {/* DRIP Calculator Tab */}
                <TabsContent value="drip-calc" className="p-3 sm:p-4 mt-0">
                  <DripCalcTab stock={stock} isDark={isDark} />
                </TabsContent>
                </>
                )}
              </div>
              </Tabs>
            </div>
          </>
        )}

        {/* Error/no data state */}
        {!loading && !stock && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <TrendingDown className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">Could not load stock data</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={onClose}>Close</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
